import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import flowStepsApi from '../api/flowStepsApi';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  Chip, 
  Avatar,
  Divider,
  Tabs,
  Tab,
  CircularProgress, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  IconButton,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Pagination,
  InputAdornment,
  Menu,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
  Slider,
  OutlinedInput,
  Tooltip as MuiTooltip,
  useTheme,
  useMediaQuery,
  Autocomplete
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PublishIcon from '@mui/icons-material/Publish';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CodeIcon from '@mui/icons-material/Code';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import TokenIcon from '@mui/icons-material/Token';
import DataObjectIcon from '@mui/icons-material/DataObject';
import StepsIcon from '@mui/icons-material/Timeline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CancelIcon from '@mui/icons-material/Cancel';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RestoreIcon from '@mui/icons-material/Restore';
import AddIcon from '@mui/icons-material/Add';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import KeyIcon from '@mui/icons-material/Key';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NotificationsIcon from '@mui/icons-material/Notifications';
import GroupIcon from '@mui/icons-material/Group';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import WebhookIcon from '@mui/icons-material/Webhook';
import TableChartIcon from '@mui/icons-material/TableChart';
import LinkIcon from '@mui/icons-material/Link';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Badge from '@mui/material/Badge';
import Link from '@mui/material/Link';
import EmailIcon from '@mui/icons-material/Email';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import StorageIcon from '@mui/icons-material/Storage';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import BadgeIcon from '@mui/icons-material/Badge';
import BreadcrumbsNav from '../components/BreadcrumbsNav';
import PushNotificationTab from '../components/PushNotificationTab';
import WebhooksTab from '../components/WebhooksTab';
import EmailTab from '../components/EmailTab';
import SyncDialog from '../components/SyncDialog';

const ExcelTemplateEditor = React.lazy(() => import('../components/ExcelTemplateEditor'));

// AnalyticsTab removed - using BI tool for analytics
const BillingTab = ({ companyId, projectId, project, isActive }) => {
  const [billingSettings, setBillingSettings] = useState({
    useQuotaTokens: false,
    chargingOptions: {
      perExecution: { enabled: false, rate: 10 },
      perObject: { enabled: false, rate: 1 },
      perStep: { enabled: false, rate: 5 },
      perStorage: { enabled: false, rate: 0.10, billingCycle: 'monthly' },
      perPage: { enabled: false, rate: 1 }
    },
    tokenExpiry: 'monthly', // weekly, monthly, yearly
    defaultRefillAmount: 1000,
    autoReset: true,
    currentTokens: 750
  });
  const [storageData, setStorageData] = useState({
    current: { totalBytes: 0, totalFiles: 0 },
    monthly: [],
    lastCalculatedAt: null
  });
  const [topUpModalOpen, setTopUpModalOpen] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [storageLoading, setStorageLoading] = useState(false);

  // Load billing settings and storage data when tab becomes active
  useEffect(() => {
    if (isActive) {
      fetchBillingSettings();
      fetchStorageData();
    }
  }, [isActive, companyId, projectId]);

  const fetchBillingSettings = async () => {
    try {
      console.log('Fetching billing settings for project:', projectId);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/billing`);
      setBillingSettings(response.data);
      console.log('✅ Billing settings loaded:', response.data);
    } catch (error) {
      console.error('Failed to fetch billing settings:', error);
      // Fallback to default settings if API fails
      setBillingSettings(prev => ({
        ...prev,
        useQuotaTokens: false,
        currentTokens: 0,
      }));
    }
  };

  const fetchStorageData = async (refresh = false) => {
    try {
      setStorageLoading(true);
      console.log('Fetching storage data for project:', projectId);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/storage-analytics${refresh ? '?refresh=true' : ''}`);
      setStorageData(response.data);
      console.log('✅ Storage data loaded:', response.data);
    } catch (error) {
      console.error('Failed to fetch storage data:', error);
      setStorageData({
        current: { totalBytes: 0, totalFiles: 0 },
        monthly: [],
        lastCalculatedAt: null
      });
    } finally {
      setStorageLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const bytesToGB = (bytes) => {
    return (bytes / (1024 * 1024 * 1024)).toFixed(3);
  };

  const saveBillingSettings = async (newSettings) => {
    try {
      console.log('Saving billing settings:', newSettings);
      const response = await api.put(`/companies/${companyId}/projects/${projectId}/billing`, newSettings);
      setBillingSettings(response.data);
      console.log('✅ Billing settings saved successfully');
    } catch (error) {
      console.error('Failed to save billing settings:', error);
      // Revert to previous settings on error
      console.error('❌ Settings save failed, keeping current state');
    }
  };

  const handleQuotaToggle = (event) => {
    const newSettings = {
      ...billingSettings,
      useQuotaTokens: event.target.checked
    };
    saveBillingSettings(newSettings);
  };

  const handleChargingOptionChange = (option, field, value) => {
    const newSettings = {
      ...billingSettings,
      chargingOptions: {
        ...billingSettings.chargingOptions,
        [option]: {
          ...billingSettings.chargingOptions[option],
          [field]: value
        }
      }
    };
    saveBillingSettings(newSettings);
  };

  const handleTokenExpiryChange = (event) => {
    const newSettings = {
      ...billingSettings,
      tokenExpiry: event.target.value
    };
    saveBillingSettings(newSettings);
  };

  const handleDefaultRefillChange = (event) => {
    const newSettings = {
      ...billingSettings,
      defaultRefillAmount: parseInt(event.target.value) || 0
    };
    saveBillingSettings(newSettings);
  };

  const handleAutoResetChange = (event) => {
    const newSettings = {
      ...billingSettings,
      autoReset: event.target.checked
    };
    saveBillingSettings(newSettings);
  };

  const handleTopUpTokens = () => {
    setTopUpModalOpen(true);
  };

  const confirmTopUp = async () => {
    if (!topUpAmount || parseInt(topUpAmount) <= 0) return;
    
    try {
      console.log(`💰 Topping up ${topUpAmount} tokens...`);
      const response = await api.post(`/companies/${companyId}/projects/${projectId}/billing/topup`, {
        amount: parseInt(topUpAmount)
      });
      
      // Update billing settings with new token count
      setBillingSettings(prev => ({
        ...prev,
        currentTokens: response.data.currentTokens
      }));
      
      setTopUpModalOpen(false);
      setTopUpAmount('');
      console.log('✅ Tokens topped up successfully:', response.data);
    } catch (error) {
      console.error('Failed to top up tokens:', error);
    }
  };

  const handleResetTokens = async () => {
    try {
      console.log('💰 Resetting tokens to default amount...');
      const response = await api.post(`/companies/${companyId}/projects/${projectId}/billing/reset`);
      
      // Update billing settings with reset token count
      setBillingSettings(prev => ({
        ...prev,
        currentTokens: response.data.currentTokens
      }));
      
      console.log('✅ Tokens reset successfully:', response.data);
    } catch (error) {
      console.error('Failed to reset tokens:', error);
    }
  };

  if (!isActive) {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={40} />
        <Box ml={2}>
          <Typography variant="body2" color="textSecondary">
            Loading billing settings...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Billing & Usage Settings
      </Typography>

      {/* Storage Usage Overview - Always visible */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <StorageIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Storage Usage
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Current S3 file storage for this project
              </Typography>
            </Box>
          </Box>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => fetchStorageData(true)}
            disabled={storageLoading}
          >
            Refresh
          </Button>
        </Box>

        {storageLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box textAlign="center" p={2} bgcolor="white" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {formatBytes(storageData.current?.totalBytes || 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mt={1}>
                    Total Storage
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box textAlign="center" p={2} bgcolor="white" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {(storageData.current?.totalFiles || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mt={1}>
                    Total Files
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box textAlign="center" p={2} bgcolor="white" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {bytesToGB(storageData.current?.totalBytes || 0)} GB
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mt={1}>
                    Storage (GB)
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box textAlign="center" p={2} bgcolor="white" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {storageData.monthly && storageData.monthly.length > 0 
                      ? formatBytes(storageData.monthly[storageData.monthly.length - 1]?.totalUploaded || 0)
                      : '0 Bytes'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mt={1}>
                    This Month Uploaded
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            {storageData.lastCalculatedAt && (
              <Typography variant="caption" color="textSecondary" display="block" textAlign="right" mt={2}>
                Last updated: {new Date(storageData.lastCalculatedAt).toLocaleString()}
              </Typography>
            )}
          </>
        )}
      </Paper>

      {/* Main Quota Toggle */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, border: billingSettings.useQuotaTokens ? '2px solid #4caf50' : 'none', bgcolor: '#ffffff' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <TokenIcon color={billingSettings.useQuotaTokens ? 'success' : 'disabled'} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Quota Token System
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Control token-based charging for this project
              </Typography>
            </Box>
          </Box>
          <Switch
            checked={billingSettings.useQuotaTokens}
            onChange={handleQuotaToggle}
            size="large"
            color="success"
          />
        </Box>
        
        {billingSettings.useQuotaTokens && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Token system is active. Customers will be charged based on the settings below.
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Current Token Status */}
      {billingSettings.useQuotaTokens && (
        <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box textAlign="center">
                <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 1, width: 56, height: 56 }}>
                  <TokenIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {billingSettings.currentTokens.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Current Tokens Available
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box textAlign="center">
                <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 1, width: 56, height: 56 }}>
                  <RestoreIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {billingSettings.defaultRefillAmount.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Default Refill Amount
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box textAlign="center">
                <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 1, width: 56, height: 56 }}>
                  <ScheduleIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {billingSettings.tokenExpiry.charAt(0).toUpperCase() + billingSettings.tokenExpiry.slice(1)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Token Expiry Period
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box display="flex" gap={2} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleTopUpTokens}
              color="primary"
            >
              Top Up Tokens
            </Button>
            <Button
              variant="outlined"
              startIcon={<RestoreIcon />}
              onClick={handleResetTokens}
              color="warning"
            >
              Reset to Default
            </Button>
          </Box>
        </Paper>
      )}

      {/* Billing Settings - Only show when quota system is enabled */}
      {billingSettings.useQuotaTokens && (
        <>
          {/* Charging Options */}
          <Paper elevation={1} sx={{ p: 3, mb: 3, overflow: 'visible' }}>
            <Typography variant="h6" fontWeight="bold" mb={3}>
              <AttachMoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Charging Options
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              Configure how tokens are deducted for different activities. You can enable multiple options.
            </Typography>

            <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
              {Object.entries(billingSettings.chargingOptions)
                .filter(([key]) => key !== 'perAIToken') // Filter out AI token option
                .map(([key, option]) => {
                const labels = {
                  perExecution: 'Per Execution',
                  perObject: 'Per Object Extracted',
                  perStep: 'Per Step',
                  perStorage: 'Per Storage (GB/month)',
                  perPage: 'Per Page Extracted'
                };
                
                const descriptions = {
                  perExecution: 'Charge tokens for each workflow execution',
                  perObject: 'Charge tokens for each data object extracted',
                  perStep: 'Charge tokens for each workflow step executed',
                  perStorage: 'Charge tokens based on file storage usage per GB per month',
                  perPage: 'Charge tokens for each page processed (PDF pages, images, Word pages, Excel rows/100)'
                };

                return (
                  <Grid size={{ xs: 12, md: 6 }} key={key}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        height: '50%',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 180 // Fixed minimum height for all cards
                      }}
                    >
                      <Box sx={{ flex: 1}}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={option.enabled}
                              onChange={(e) => handleChargingOptionChange(key, 'enabled', e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="medium">
                                {labels[key]}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {descriptions[key]}
                              </Typography>
                            </Box>
                          }
                          sx={{ mb: 2, alignItems: 'flex-start' }}
                        />
                      </Box>
                      
                      {option.enabled && (
                        <Box sx={{ mt: 'auto' }}>
                          <Typography variant="body2" fontWeight="medium" mb={1}>
                            Rate: {option.rate} tokens
                          </Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={option.rate}
                            onChange={(e) => handleChargingOptionChange(key, 'rate', parseFloat(e.target.value) || 0)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">Tokens</InputAdornment>,
                            }}
                            fullWidth
                          />
                        </Box>
                      )}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>

          {/* Token Management Settings */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" mb={3}>
              <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Token Management
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Token Expiry Period
                  </FormLabel>
                  <Select
                    value={billingSettings.tokenExpiry}
                    onChange={handleTokenExpiryChange}
                    size="small"
                  >
                    <MenuItem value="weekly">Weekly Reset</MenuItem>
                    <MenuItem value="monthly">Monthly Reset</MenuItem>
                    <MenuItem value="yearly">Yearly Reset</MenuItem>
                  </Select>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                    How often tokens reset to the default amount
                  </Typography>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Default Refill Amount
                  </FormLabel>
                  <TextField
                    type="number"
                    value={billingSettings.defaultRefillAmount}
                    onChange={handleDefaultRefillChange}
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">Tokens</InputAdornment>,
                    }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                    Number of tokens to refill at each reset period
                  </Typography>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={billingSettings.autoReset}
                      onChange={handleAutoResetChange}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        Automatically reset tokens at the end of each period
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        When enabled, tokens will automatically reset to the default refill amount at the end of each {billingSettings.tokenExpiry.replace('ly', '')} period
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </>
      )}

      {/* Token Top-Up Modal */}
      <Dialog
        open={topUpModalOpen}
        onClose={() => setTopUpModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <AddIcon color="primary" />
            Top Up Tokens
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Add tokens to the current balance. Current balance: {billingSettings.currentTokens.toLocaleString()} tokens
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Number of Tokens"
            type="number"
            fullWidth
            variant="outlined"
            value={topUpAmount}
            onChange={(e) => setTopUpAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">+</InputAdornment>,
              endAdornment: <InputAdornment position="end">tokens</InputAdornment>,
            }}
            sx={{ mt: 2 }}
          />
          {topUpAmount && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                New balance will be: {(billingSettings.currentTokens + (parseInt(topUpAmount) || 0)).toLocaleString()} tokens
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTopUpModalOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmTopUp}
            variant="contained"
            disabled={!topUpAmount || parseInt(topUpAmount) <= 0}
          >
            Add Tokens
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const AISettingsTab = ({ companyId, projectId, project, isActive }) => {
  const [aiSettings, setAiSettings] = useState({
    openaiApiKey: '',
    googleAIApiKey: '',
    awsAccessKeyId: '',
    awsSecretAccessKey: '',
    awsRegion: '',
    qwenApiKey: '',
    anthropicApiKey: '',
    isOpenAIConfigured: false,
    isGoogleAIConfigured: false,
    isAWSConfigured: false,
    isQwenConfigured: false,
    isAnthropicConfigured: false,
    // AWS Textract Rate Limits
    awsRateLimits: {
      detectDocumentText: 5,
      analyzeDocument: 1,
      analyzeExpense: 1,
      autoDetect: true,
      lastThrottledAt: null,
      throttleCount: 0
    },
    // AWS SQS Configuration
    awsSqsConfig: {
      queueUrl: '',
      dlqUrl: '',
      queueEnabled: true,
      maxRetryAttempts: 3,
      isConfigured: false
    }
  });
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [showGoogleAIKey, setShowGoogleAIKey] = useState(false);
  const [showAWSSecretKey, setShowAWSSecretKey] = useState(false);
  const [showQwenKey, setShowQwenKey] = useState(false);
  const [showAnthropicKey, setShowAnthropicKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Load AI settings when tab becomes active
  useEffect(() => {
    if (isActive) {
      fetchAISettings();
    }
  }, [isActive, companyId, projectId]);

  const fetchAISettings = async () => {
    try {
      setLoading(true);
      console.log('Fetching AI settings for project:', projectId);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/ai-settings`);

      setAiSettings({
        openaiApiKey: response.data.openaiApiKey || '',
        googleAIApiKey: response.data.googleAIApiKey || '',
        awsAccessKeyId: response.data.awsAccessKeyId || '',
        awsSecretAccessKey: response.data.awsSecretAccessKey || '',
        awsRegion: response.data.awsRegion || '',
        qwenApiKey: response.data.qwenApiKey || '',
        anthropicApiKey: response.data.anthropicApiKey || '',
        isOpenAIConfigured: response.data.isOpenAIConfigured || false,
        isGoogleAIConfigured: response.data.isGoogleAIConfigured || false,
        isAWSConfigured: response.data.isAWSConfigured || false,
        isQwenConfigured: response.data.isQwenConfigured || false,
        isAnthropicConfigured: response.data.isAnthropicConfigured || false,
        // AWS Textract Rate Limits
        awsRateLimits: {
          detectDocumentText: response.data.awsRateLimits?.detectDocumentText || 5,
          analyzeDocument: response.data.awsRateLimits?.analyzeDocument || 1,
          analyzeExpense: response.data.awsRateLimits?.analyzeExpense || 1,
          autoDetect: response.data.awsRateLimits?.autoDetect !== false,
          lastThrottledAt: response.data.awsRateLimits?.lastThrottledAt || null,
          throttleCount: response.data.awsRateLimits?.throttleCount || 0
        },
        // AWS SQS Configuration
        awsSqsConfig: {
          queueUrl: response.data.awsSqsConfig?.queueUrl || '',
          dlqUrl: response.data.awsSqsConfig?.dlqUrl || '',
          queueEnabled: response.data.awsSqsConfig?.queueEnabled !== false,
          maxRetryAttempts: response.data.awsSqsConfig?.maxRetryAttempts || 3,
          isConfigured: response.data.awsSqsConfig?.isConfigured || false
        }
      });
      console.log('✅ AI settings loaded');
      
    } catch (error) {
      console.error('Failed to fetch AI settings:', error);
      // Keep default settings if API fails
      setAiSettings({
        openaiApiKey: '',
        googleAIApiKey: '',
        awsAccessKeyId: '',
        awsSecretAccessKey: '',
        awsRegion: '',
        qwenApiKey: '',
        anthropicApiKey: '',
        isOpenAIConfigured: false,
        isGoogleAIConfigured: false,
        isAWSConfigured: false,
        isQwenConfigured: false,
        isAnthropicConfigured: false
      });
    } finally {
      setLoading(false);
    }
  };

  const saveAISettings = async () => {
    try {
      setSaving(true);
      console.log('Saving AI settings...');
      
      const response = await api.put(`/companies/${companyId}/projects/${projectId}/ai-settings`, {
        openaiApiKey: aiSettings.openaiApiKey,
        googleAIApiKey: aiSettings.googleAIApiKey,
        awsAccessKeyId: aiSettings.awsAccessKeyId,
        awsSecretAccessKey: aiSettings.awsSecretAccessKey,
        awsRegion: aiSettings.awsRegion,
        qwenApiKey: aiSettings.qwenApiKey,
        anthropicApiKey: aiSettings.anthropicApiKey,
        awsRateLimits: aiSettings.awsRateLimits,
        awsSqsConfig: aiSettings.awsSqsConfig
      });
      
      // Update settings with the response data and disable fields by clearing them
      setAiSettings(prev => ({
        ...prev,
        // Clear the keys to disable the fields (they're saved on backend)
        openaiApiKey: response.data.openaiApiKey,
        googleAIApiKey: response.data.googleAIApiKey,
        awsAccessKeyId: response.data.awsAccessKeyId,
        awsSecretAccessKey: response.data.awsSecretAccessKey,
        qwenApiKey: response.data.qwenApiKey,
        anthropicApiKey: response.data.anthropicApiKey,
        // Update configuration status from the response
        isOpenAIConfigured: response.data.isOpenAIConfigured,
        isGoogleAIConfigured: response.data.isGoogleAIConfigured,
        isAWSConfigured: response.data.isAWSConfigured,
        isQwenConfigured: response.data.isQwenConfigured,
        isAnthropicConfigured: response.data.isAnthropicConfigured,
        // Update rate limits and SQS config
        awsRateLimits: response.data.awsRateLimits || prev.awsRateLimits,
        awsSqsConfig: {
          ...prev.awsSqsConfig,
          isConfigured: response.data.awsSqsConfig?.isConfigured || false
        }
      }));
      
      console.log('✅ AI settings saved successfully');
    } catch (error) {
      console.error('Failed to save AI settings:', error);
      alert('Failed to save AI settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };


  const handleOpenAIKeyChange = (event) => {
    setAiSettings(prev => ({
      ...prev,
      openaiApiKey: event.target.value
    }));
  };

  const handleGoogleAIKeyChange = (event) => {
    setAiSettings(prev => ({
      ...prev,
      googleAIApiKey: event.target.value
    }));
  };

  const handleAWSAccessKeyChange = (event) => {
    setAiSettings(prev => ({
      ...prev,
      awsAccessKeyId: event.target.value
    }));
  };

  const handleAWSSecretKeyChange = (event) => {
    setAiSettings(prev => ({
      ...prev,
      awsSecretAccessKey: event.target.value
    }));
  };

  const handleAWSRegionChange = (event) => {
    setAiSettings(prev => ({
      ...prev,
      awsRegion: event.target.value
    }));
  };

  const handleQwenKeyChange = (event) => {
    setAiSettings(prev => ({
      ...prev,
      qwenApiKey: event.target.value
    }));
  };

  const handleAnthropicKeyChange = (event) => {
    setAiSettings(prev => ({
      ...prev,
      anthropicApiKey: event.target.value
    }));
  };

  // Edit handlers removed - fields are now always editable until saved

  const toggleShowOpenAIKey = () => {
    setShowOpenAIKey(!showOpenAIKey);
  };

  const toggleShowGoogleAIKey = () => {
    setShowGoogleAIKey(!showGoogleAIKey);
  };

  const toggleShowAWSSecretKey = () => {
    setShowAWSSecretKey(!showAWSSecretKey);
  };

  const toggleShowQwenKey = () => {
    setShowQwenKey(!showQwenKey);
  };

  const toggleShowAnthropicKey = () => {
    setShowAnthropicKey(!showAnthropicKey);
  };

  const testOpenAIKey = async () => {
    if (!aiSettings.openaiApiKey) {
      alert('Please enter an OpenAI API key first');
      return;
    }

    try {
      setSaving(true);
      console.log('Testing OpenAI API key...');
      
      const response = await api.post(`/companies/${companyId}/projects/${projectId}/ai-settings/test-openai`, {
        openaiApiKey: aiSettings.openaiApiKey
      });
      
      if (response.data.success) {
        alert('✅ OpenAI API Key is valid and working!');
      } else {
        alert('❌ OpenAI API Key test failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('OpenAI API key test failed:', error);
      alert('❌ OpenAI API Key test failed: ' + (error.response?.data?.message || 'Network error'));
    } finally {
      setSaving(false);
    }
  };

  const testGoogleAIKey = async () => {
    if (!aiSettings.googleAIApiKey) {
      alert('Please enter a Google AI API key first');
      return;
    }

    try {
      setSaving(true);
      console.log('Testing Google AI API key...');
      
      const response = await api.post(`/companies/${companyId}/projects/${projectId}/ai-settings/test-google-ai`, {
        googleAIApiKey: aiSettings.googleAIApiKey
      });
      
      if (response.data.success) {
        alert('✅ Google AI API Key is valid and working!');
      } else {
        alert('❌ Google AI API Key test failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Google AI API key test failed:', error);
      alert('❌ Google AI API Key test failed: ' + (error.response?.data?.message || 'Network error'));
    } finally {
      setSaving(false);
    }
  };

  const testAWSKey = async () => {
    if (!aiSettings.awsAccessKeyId || !aiSettings.awsSecretAccessKey || !aiSettings.awsRegion) {
      alert('Please enter AWS Access Key ID, Secret Access Key, and Region first');
      return;
    }

    try {
      setSaving(true);
      console.log('Testing AWS credentials...');
      
      const response = await api.post(`/companies/${companyId}/projects/${projectId}/ai-settings/test-aws`, {
        awsAccessKeyId: aiSettings.awsAccessKeyId,
        awsSecretAccessKey: aiSettings.awsSecretAccessKey,
        awsRegion: aiSettings.awsRegion
      });
      
      if (response.data.success) {
        const message = `✅ AWS Textract credentials are valid and working!
        
Region: ${response.data.region}
Blocks detected: ${response.data.blocksDetected}
Text detected: "${response.data.textDetected}"
Test image processed: ${response.data.testImageProcessed ? 'Yes' : 'No'}

Your AWS Textract integration is ready to use!`;
        alert(message);
      } else {
        alert('❌ AWS Textract test failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('AWS credentials test failed:', error);
      alert('❌ AWS Textract test failed: ' + (error.response?.data?.message || 'Network error'));
    } finally {
      setSaving(false);
    }
  };

  const testQwenKey = async () => {
    if (!aiSettings.qwenApiKey) {
      alert('Please enter a QWEN API key first');
      return;
    }

    try {
      setSaving(true);
      console.log('Testing QWEN API key...');
      
      const response = await api.post(`/companies/${companyId}/projects/${projectId}/ai-settings/test-qwen`, {
        qwenApiKey: aiSettings.qwenApiKey
      });
      
      if (response.data.success) {
        alert('✅ QWEN API Key is valid and working!\n\nTest response: ' + response.data.response);
      } else {
        alert('❌ QWEN API Key test failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('QWEN API key test failed:', error);
      alert('❌ QWEN API Key test failed: ' + (error.response?.data?.message || 'Network error'));
    } finally {
      setSaving(false);
    }
  };

  const testAnthropicKey = async () => {
    if (!aiSettings.anthropicApiKey) {
      alert('Please enter an Anthropic API key first');
      return;
    }

    try {
      setSaving(true);
      console.log('Testing Anthropic API key...');
      
      const response = await api.post(`/companies/${companyId}/projects/${projectId}/ai-settings/test-anthropic`, {
        anthropicApiKey: aiSettings.anthropicApiKey
      });
      
      if (response.data.success) {
        alert('✅ Anthropic API Key is valid and working!\n\nModel: ' + response.data.model + '\nTest response: ' + response.data.response);
      } else {
        alert('❌ Anthropic API Key test failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Anthropic API key test failed:', error);
      alert('❌ Anthropic API Key test failed: ' + (error.response?.data?.message || 'Network error'));
    } finally {
      setSaving(false);
    }
  };

  if (!isActive) {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={40} />
        <Box ml={2}>
          <Typography variant="body2" color="textSecondary">
            Loading AI settings...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        AI Settings
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={4}>
        Configure AI service settings for this project. Using project-specific API keys allows for better cost tracking and billing management.
      </Typography>

      {/* OpenAI Configuration */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <SmartToyIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              OpenAI Configuration
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Configure your OpenAI API key for this project
            </Typography>
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            API Key Status
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip 
              icon={aiSettings.isOpenAIConfigured ? <CheckCircleIcon /> : <ErrorIcon />}
              label={aiSettings.isOpenAIConfigured ? 'Configured' : 'Not Configured'}
              color={aiSettings.isOpenAIConfigured ? 'success' : 'warning'}
              variant="outlined"
            />
            {aiSettings.isOpenAIConfigured && (
              <Typography variant="body2" color="textSecondary">
                Project will use its own OpenAI API key
              </Typography>
            )}
            {!aiSettings.isOpenAIConfigured && (
              <Typography variant="body2" color="textSecondary">
                Project will use the default system API key
              </Typography>
            )}
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            OpenAI API Key
          </Typography>
                      <TextField
              fullWidth
              type={showOpenAIKey ? 'text' : 'password'}
              value={aiSettings.openaiApiKey}
              onChange={handleOpenAIKeyChange}
              placeholder={aiSettings.isOpenAIConfigured ? "API key is configured - enter new key to update" : "sk-..."}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleShowOpenAIKey}
                      edge="end"
                      size="small"
                    >
                      {showOpenAIKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText={
                aiSettings.isOpenAIConfigured 
                  ? "API key is configured. Enter a new key to update it, or leave empty to keep current key."
                  : "Enter your OpenAI API key. This will be used for all AI operations in this project."
              }
            />
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={saving ? <CircularProgress size={20} /> : <SmartToyIcon />}
            onClick={testOpenAIKey}
            disabled={saving || !aiSettings.openaiApiKey}
          >
            {saving ? 'Testing...' : 'Test OpenAI Key'}
          </Button>
        </Box>
      </Paper>

      {/* Google AI Configuration */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <SmartToyIcon color="success" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Google AI Configuration
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Configure your Google AI API key for OCR and vision processing
            </Typography>
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            API Key Status
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip 
              icon={aiSettings.isGoogleAIConfigured ? <CheckCircleIcon /> : <ErrorIcon />}
              label={aiSettings.isGoogleAIConfigured ? 'Configured' : 'Not Configured'}
              color={aiSettings.isGoogleAIConfigured ? 'success' : 'warning'}
              variant="outlined"
            />
            {aiSettings.isGoogleAIConfigured && (
              <Typography variant="body2" color="textSecondary">
                Project will use its own Google AI API key for OCR and vision
              </Typography>
            )}
            {!aiSettings.isGoogleAIConfigured && (
              <Typography variant="body2" color="textSecondary">
                Project will use the default system Google AI key (if available)
              </Typography>
            )}
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            Google AI API Key
          </Typography>
          <TextField
            fullWidth
            type={showGoogleAIKey ? 'text' : 'password'}
            value={aiSettings.googleAIApiKey}
            onChange={handleGoogleAIKeyChange}
            placeholder={aiSettings.isGoogleAIConfigured ? "API key is configured - enter new key to update" : "AIza..."}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleShowGoogleAIKey}
                    edge="end"
                    size="small"
                  >
                    {showGoogleAIKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText={
              aiSettings.isGoogleAIConfigured 
                ? "API key is configured. Enter a new key to update it, or leave empty to keep current key."
                : "Enter your Google AI API key. This will be used for Google AI Vision OCR in this project."
            }
          />
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={saving ? <CircularProgress size={20} /> : <SmartToyIcon />}
            onClick={testGoogleAIKey}
            disabled={saving || !aiSettings.googleAIApiKey}
          >
            {saving ? 'Testing...' : 'Test Google AI Key'}
          </Button>
        </Box>
      </Paper>

      {/* AWS Configuration */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <CloudIcon color="warning" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              AWS Textract Configuration
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Configure your AWS credentials for Textract OCR processing
            </Typography>
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            API Key Status
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip 
              icon={aiSettings.isAWSConfigured ? <CheckCircleIcon /> : <ErrorIcon />}
              label={aiSettings.isAWSConfigured ? 'Configured' : 'Not Configured'}
              color={aiSettings.isAWSConfigured ? 'success' : 'warning'}
              variant="outlined"
            />
            {aiSettings.isAWSConfigured && (
              <Typography variant="body2" color="textSecondary">
                Project will use its own AWS credentials for Textract OCR
              </Typography>
            )}
            {!aiSettings.isAWSConfigured && (
              <Typography variant="body2" color="textSecondary">
                AWS Textract will not be available for OCR processing
              </Typography>
            )}
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            AWS Access Key ID
          </Typography>
          <TextField
            fullWidth
            type="text"
            value={aiSettings.awsAccessKeyId}
            onChange={handleAWSAccessKeyChange}
            placeholder={aiSettings.isAWSConfigured ? "Access key is configured - enter new key to update" : "AKIA..."}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyIcon color="action" />
                </InputAdornment>
              ),
            }}
            helperText={
              aiSettings.isAWSConfigured 
                ? "Access key is configured. Enter a new key to update it, or leave empty to keep current key."
                : "Enter your AWS Access Key ID. Must start with 'AKIA'."
            }
          />
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            AWS Secret Access Key
          </Typography>
          <TextField
            fullWidth
            type={showAWSSecretKey ? 'text' : 'password'}
            value={aiSettings.awsSecretAccessKey}
            onChange={handleAWSSecretKeyChange}
            placeholder={aiSettings.isAWSConfigured ? "Secret key is configured - enter new key to update" : "Secret access key..."}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleShowAWSSecretKey}
                    edge="end"
                    size="small"
                  >
                    {showAWSSecretKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText={
              aiSettings.isAWSConfigured 
                ? "Secret key is configured. Enter a new key to update it, or leave empty to keep current key."
                : "Enter your AWS Secret Access Key."
            }
          />
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            AWS Region
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Region</InputLabel>
            <Select
              value={aiSettings.awsRegion}
              onChange={handleAWSRegionChange}
            >
              <MenuItem value="us-east-1">US East (N. Virginia) - us-east-1</MenuItem>
              <MenuItem value="us-east-2">US East (Ohio) - us-east-2</MenuItem>
              <MenuItem value="us-west-1">US West (N. California) - us-west-1</MenuItem>
              <MenuItem value="us-west-2">US West (Oregon) - us-west-2</MenuItem>
              <MenuItem value="eu-west-1">Europe (Ireland) - eu-west-1</MenuItem>
              <MenuItem value="eu-west-2">Europe (London) - eu-west-2</MenuItem>
              <MenuItem value="eu-central-1">Europe (Frankfurt) - eu-central-1</MenuItem>
              <MenuItem value="ap-northeast-1">Asia Pacific (Tokyo) - ap-northeast-1</MenuItem>
              <MenuItem value="ap-southeast-1">Asia Pacific (Singapore) - ap-southeast-1</MenuItem>
              <MenuItem value="ap-southeast-2">Asia Pacific (Sydney) - ap-southeast-2</MenuItem>
            </Select>
            <FormHelperText>
              Choose the AWS region where you want to use Textract
            </FormHelperText>
          </FormControl>
        </Box>

        {/* AWS Textract Rate Limits */}
        <Box mt={4} mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            Rate Limits (Transactions Per Second)
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Configure rate limits for AWS Textract API calls. Default AWS limit is 5 TPS. Request increases from AWS if needed.
          </Typography>
          
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="DetectDocumentText TPS"
                value={aiSettings.awsRateLimits.detectDocumentText}
                onChange={(e) => setAiSettings(prev => ({
                  ...prev,
                  awsRateLimits: {
                    ...prev.awsRateLimits,
                    detectDocumentText: parseInt(e.target.value) || 5
                  }
                }))}
                inputProps={{ min: 1, max: 100 }}
                helperText="Default: 5 TPS"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="AnalyzeDocument TPS"
                value={aiSettings.awsRateLimits.analyzeDocument}
                onChange={(e) => setAiSettings(prev => ({
                  ...prev,
                  awsRateLimits: {
                    ...prev.awsRateLimits,
                    analyzeDocument: parseInt(e.target.value) || 1
                  }
                }))}
                inputProps={{ min: 1, max: 100 }}
                helperText="Default: 1 TPS"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="AnalyzeExpense TPS"
                value={aiSettings.awsRateLimits.analyzeExpense}
                onChange={(e) => setAiSettings(prev => ({
                  ...prev,
                  awsRateLimits: {
                    ...prev.awsRateLimits,
                    analyzeExpense: parseInt(e.target.value) || 1
                  }
                }))}
                inputProps={{ min: 1, max: 100 }}
                helperText="Default: 1 TPS"
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Switch 
                checked={aiSettings.awsRateLimits.autoDetect}
                onChange={(e) => setAiSettings(prev => ({
                  ...prev,
                  awsRateLimits: {
                    ...prev.awsRateLimits,
                    autoDetect: e.target.checked
                  }
                }))}
              />
            }
            label="Auto-detect and adjust rate limits based on throttling responses"
          />

          {aiSettings.awsRateLimits.lastThrottledAt && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Last throttled:</strong> {new Date(aiSettings.awsRateLimits.lastThrottledAt).toLocaleString()}
                <br />
                <strong>Throttle count:</strong> {aiSettings.awsRateLimits.throttleCount}
              </Typography>
              {aiSettings.awsRateLimits.autoDetect && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Rate limits have been automatically reduced. Consider requesting an increase from AWS.
                </Typography>
              )}
            </Alert>
          )}
        </Box>

        {/* AWS SQS Queue Configuration */}
        <Box mt={4} mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            SQS Queue Configuration (Optional)
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Configure AWS SQS queues for handling rate-limited Textract jobs. Jobs will be queued when rate limits are reached.
          </Typography>

          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Chip 
              icon={aiSettings.awsSqsConfig.isConfigured ? <CheckCircleIcon /> : <ErrorIcon />}
              label={aiSettings.awsSqsConfig.isConfigured ? 'Configured' : 'Not Configured'}
              color={aiSettings.awsSqsConfig.isConfigured ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>

          <FormControlLabel
            control={
              <Switch 
                checked={aiSettings.awsSqsConfig.queueEnabled}
                onChange={(e) => setAiSettings(prev => ({
                  ...prev,
                  awsSqsConfig: {
                    ...prev.awsSqsConfig,
                    queueEnabled: e.target.checked
                  }
                }))}
              />
            }
            label="Enable SQS queue for rate limiting"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Main Queue URL"
            value={aiSettings.awsSqsConfig.queueUrl}
            onChange={(e) => setAiSettings(prev => ({
              ...prev,
              awsSqsConfig: {
                ...prev.awsSqsConfig,
                queueUrl: e.target.value
              }
            }))}
            placeholder="https://sqs.us-east-1.amazonaws.com/123456789012/textract-queue"
            helperText="AWS SQS queue URL for Textract job processing"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Dead Letter Queue URL"
            value={aiSettings.awsSqsConfig.dlqUrl}
            onChange={(e) => setAiSettings(prev => ({
              ...prev,
              awsSqsConfig: {
                ...prev.awsSqsConfig,
                dlqUrl: e.target.value
              }
            }))}
            placeholder="https://sqs.us-east-1.amazonaws.com/123456789012/textract-dlq"
            helperText="Failed jobs after max retries will be moved here"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="number"
            label="Max Retry Attempts"
            value={aiSettings.awsSqsConfig.maxRetryAttempts}
            onChange={(e) => setAiSettings(prev => ({
              ...prev,
              awsSqsConfig: {
                ...prev.awsSqsConfig,
                maxRetryAttempts: parseInt(e.target.value) || 3
              }
            }))}
            inputProps={{ min: 1, max: 10 }}
            helperText="Number of times to retry failed jobs before moving to dead letter queue"
          />
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={saving ? <CircularProgress size={20} /> : <CloudIcon />}
            onClick={testAWSKey}
            disabled={saving || !aiSettings.awsAccessKeyId || !aiSettings.awsSecretAccessKey || !aiSettings.awsRegion}
          >
            {saving ? 'Testing...' : 'Test AWS Credentials'}
          </Button>
        </Box>
      </Paper>

      {/* QWEN Configuration */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <SmartToyIcon color="warning" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              QWEN AI Configuration
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Configure your QWEN AI API key for Alibaba Cloud DashScope vision and text processing
            </Typography>
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            API Key Status
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip 
              icon={aiSettings.isQwenConfigured ? <CheckCircleIcon /> : <ErrorIcon />}
              label={aiSettings.isQwenConfigured ? 'Configured' : 'Not Configured'}
              color={aiSettings.isQwenConfigured ? 'success' : 'warning'}
              variant="outlined"
            />
            {aiSettings.isQwenConfigured && (
              <Typography variant="body2" color="textSecondary">
                Project will use its own QWEN API key for vision and text processing
              </Typography>
            )}
            {!aiSettings.isQwenConfigured && (
              <Typography variant="body2" color="textSecondary">
                QWEN models will not be available for vision or text processing
              </Typography>
            )}
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            QWEN AI API Key
          </Typography>
          <TextField
            fullWidth
            type={showQwenKey ? 'text' : 'password'}
            value={aiSettings.qwenApiKey}
            onChange={handleQwenKeyChange}
            placeholder={aiSettings.isQwenConfigured ? "API key is configured - enter new key to update" : "sk-..."}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleShowQwenKey}
                    edge="end"
                    size="small"
                  >
                    {showQwenKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText={
              aiSettings.isQwenConfigured 
                ? "API key is configured. Enter a new key to update it, or leave empty to keep current key."
                : "Enter your QWEN API key from Alibaba Cloud DashScope. This will be used for QWEN Vision and text models."
            }
          />
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={saving ? <CircularProgress size={20} /> : <SmartToyIcon />}
            onClick={testQwenKey}
            disabled={saving || !aiSettings.qwenApiKey}
          >
            {saving ? 'Testing...' : 'Test QWEN Key'}
          </Button>
        </Box>
      </Paper>

      {/* Anthropic Configuration */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <SmartToyIcon sx={{ fontSize: 32, color: '#D97757' }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Anthropic Configuration
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Configure your Anthropic API key for Claude Sonnet 4, Sonnet 4.5, and Opus 4.5 models
            </Typography>
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            API Key Status
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip 
              icon={aiSettings.isAnthropicConfigured ? <CheckCircleIcon /> : <ErrorIcon />}
              label={aiSettings.isAnthropicConfigured ? 'Configured' : 'Not Configured'}
              color={aiSettings.isAnthropicConfigured ? 'success' : 'warning'}
              variant="outlined"
            />
            {aiSettings.isAnthropicConfigured && (
              <Typography variant="body2" color="textSecondary">
                Project will use its own Anthropic API key for Claude models
              </Typography>
            )}
            {!aiSettings.isAnthropicConfigured && (
              <Typography variant="body2" color="textSecondary">
                Anthropic Claude models (Sonnet 4.5, Opus 4.5) will not be available
              </Typography>
            )}
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            Anthropic API Key
          </Typography>
          <TextField
            fullWidth
            type={showAnthropicKey ? 'text' : 'password'}
            value={aiSettings.anthropicApiKey}
            onChange={handleAnthropicKeyChange}
            placeholder={aiSettings.isAnthropicConfigured ? "API key is configured - enter new key to update" : "sk-ant-..."}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleShowAnthropicKey}
                    edge="end"
                    size="small"
                  >
                    {showAnthropicKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText={
              aiSettings.isAnthropicConfigured 
                ? "API key is configured. Enter a new key to update it, or leave empty to keep current key."
                : "Enter your Anthropic API key. This will be used for Claude Sonnet 4.5 and Opus 4.5 models."
            }
          />
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={saving ? <CircularProgress size={20} /> : <SmartToyIcon />}
            onClick={testAnthropicKey}
            disabled={saving || !aiSettings.anthropicApiKey}
          >
            {saving ? 'Testing...' : 'Test Anthropic Key'}
          </Button>
        </Box>
      </Paper>

      {/* Save All Settings */}
      <Box display="flex" justifyContent="center" mb={3}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={saveAISettings}
          disabled={saving}
        >
          {saving ? 'Saving All Settings...' : 'Save All API Keys'}
        </Button>
      </Box>

      {/* Usage Information */}
      <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Usage Information
        </Typography>
        
        <Typography variant="body2" color="textSecondary" paragraph>
          <strong>Project-Specific API Keys:</strong> When you configure API keys for this project, all AI operations 
          (data extraction, OCR processing, etc.) will use these keys instead of the system defaults.
        </Typography>
        
        <Typography variant="body2" color="textSecondary" paragraph>
          <strong>Cost Tracking:</strong> Using separate API keys per project allows you to track costs and usage 
          for each project individually in your OpenAI, Google AI, and AWS dashboards.
        </Typography>
        
        <Typography variant="body2" color="textSecondary" paragraph>
          <strong>OCR Engine Support:</strong> Google AI keys enable the "Google AI Vision (API Key)" OCR engine, 
          AWS credentials enable the "AWS Textract (API Key)" OCR engine, and QWEN keys enable "QWEN Vision" 
          OCR engine in Data Processing steps, providing high-quality text extraction with simple authentication.
        </Typography>
        
        <Typography variant="body2" color="textSecondary" paragraph>
          <strong>Security:</strong> API keys are stored securely and encrypted. They are only accessible to authorized users 
          of this project.
        </Typography>

        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Cost Benefits:</strong> Project-specific API keys allow you to:
            • Track exact costs per project • Separate billing for different clients
            • Monitor usage patterns • Set up billing alerts per project
          </Typography>
        </Alert>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Getting API Keys:</strong><br/>
            • OpenAI: Visit <strong>platform.openai.com</strong> to create project-specific keys<br/>
            • Google AI: Visit <strong>makersuite.google.com/app/apikey</strong> for your Google AI key<br/>
            • AWS: Visit <strong>console.aws.amazon.com/iam/</strong> to create access keys with Textract permissions<br/>
            • QWEN: Visit <strong>dashscope.console.aliyun.com</strong> to create your Alibaba Cloud DashScope API key<br/>
            • Anthropic: Visit <strong>console.anthropic.com</strong> to create your Anthropic API key for Claude models
          </Typography>
        </Alert>
      </Paper>
    </Box>
  );
};

const ExecutionsTab = ({ companyId, projectId, project, isActive }) => {
  const navigate = useNavigate();
  
  // State persistence key for this specific project
  const getStateKey = () => `executionsTab_${companyId}_${projectId}`;
  
  // Helper to load saved state from localStorage
  const loadSavedState = () => {
    try {
      const savedState = localStorage.getItem(getStateKey());
      return savedState ? JSON.parse(savedState) : null;
    } catch (error) {
      console.error('Error loading saved executions tab state:', error);
      return null;
    }
  };
  
  // Helper to save state to localStorage
  const saveState = (state) => {
    try {
      localStorage.setItem(getStateKey(), JSON.stringify({
        ...state,
        timestamp: Date.now() // Add timestamp to track freshness
      }));
    } catch (error) {
      console.error('Error saving executions tab state:', error);
    }
  };
  
  // Initialize state with saved values or defaults
  const savedState = loadSavedState();
  const isStateFresh = savedState && (Date.now() - savedState.timestamp < 30 * 60 * 1000); // 30 minutes
  
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(isStateFresh ? (savedState.page || 0) : 0);
  const [rowsPerPage, setRowsPerPage] = useState(isStateFresh ? (savedState.rowsPerPage || 10) : 10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortOrder, setSortOrder] = useState(isStateFresh ? (savedState.sortOrder || 'newest') : 'newest');
  const [statusFilter, setStatusFilter] = useState(isStateFresh ? (savedState.statusFilter || '') : '');
  const [dateRangeFilter, setDateRangeFilter] = useState(isStateFresh ? (savedState.dateRangeFilter || '') : '');
  const [searchInput, setSearchInput] = useState(isStateFresh ? (savedState.searchInput || '') : ''); // Input field value
  const [searchFilter, setSearchFilter] = useState(isStateFresh ? (savedState.searchFilter || '') : ''); // Applied search filter
  const [lastRefresh, setLastRefresh] = useState(null);
  
  // Menu and dialog state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchExecutions = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        limit: rowsPerPage,
        page: page + 1,
        status: statusFilter,
        dateRange: dateRangeFilter
      };

      if (searchFilter && searchFilter.trim()) {
        params.search = searchFilter.trim();
      }

      const executionsRes = await api.get(`/companies/${companyId}/projects/${projectId}/executions`, { params });
      setExecutions(executionsRes.data.executions || []);
      setTotalCount(executionsRes.data.pagination?.total || 0);
      setLastRefresh(new Date());
    } catch (err) {
      setError('Failed to fetch executions');
      console.error('Executions fetch error:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isActive) {
      fetchExecutions();
    }
  }, [companyId, projectId, page, rowsPerPage, statusFilter, dateRangeFilter, searchFilter, sortOrder, isActive]);

  // Save state whenever it changes (but only if tab is active)
  useEffect(() => {
    if (isActive) {
      saveState({
        page,
        rowsPerPage,
        statusFilter,
        dateRangeFilter,
        searchInput,
        searchFilter,
        sortOrder
      });
    }
  }, [page, rowsPerPage, statusFilter, dateRangeFilter, searchInput, searchFilter, sortOrder, isActive]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    if (field === 'status') {
      setStatusFilter(value);
    } else if (field === 'dateRange') {
      setDateRangeFilter(value);
    }
    setPage(0);
  };

  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
    setPage(0);
  };

  const handleSearch = () => {
    setSearchFilter(searchInput);
    setPage(0);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setDateRangeFilter('');
    setSearchInput('');
    setSearchFilter('');
    setSortOrder('newest');
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'in_progress': return 'warning';
      case 'waiting_input': return 'info';
      case 'cancelled': return 'default';
      default: return 'info';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'in_progress': return <ScheduleIcon color="warning" />;
      case 'waiting_input': return <InfoIcon color="info" />;
      case 'cancelled': return <CancelIcon color="disabled" />;
      default: return <PlayArrowIcon color="info" />;
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const handleViewExecution = (execution) => {
    // Save current state before navigating away
    saveState({
      page,
      rowsPerPage,
      statusFilter,
      dateRangeFilter,
      searchInput,
      searchFilter,
      sortOrder
    });
    
    // Always navigate to the unified results page - it now handles all execution states
    navigate(`/company/${companyId}/project/${projectId}/execution/${execution.executionId}/results`);
  };

  const getActionButton = (execution) => {
    if (execution.status === 'completed') {
      return { text: 'View Results', icon: <VisibilityIcon />, title: 'View execution results' };
    } else if (execution.status === 'failed') {
      return { text: 'View Details', icon: <ErrorIcon />, title: 'View execution details and error information' };
    } else if (execution.status === 'in_progress' || execution.status === 'waiting_input') {
      return { text: 'View Details', icon: <ScheduleIcon />, title: 'View execution progress and details' };
    } else {
      return { text: 'View Details', icon: <VisibilityIcon />, title: 'View execution details' };
    }
  };

  const handleMenuOpen = (event, execution) => {
    setAnchorEl(event.currentTarget);
    setSelectedExecution(execution);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedExecution(null);
  };

  const handleDeleteExecution = () => {
    // Keep a reference to the execution before closing menu
    const executionToDelete = selectedExecution;
    setAnchorEl(null);
    setSelectedExecution(executionToDelete);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteExecution = async () => {
    console.log('🗑️ confirmDeleteExecution called', { selectedExecution, companyId, projectId });
    
    if (!selectedExecution) {
      console.log('❌ No selected execution, returning early');
      return;
    }
    
    console.log('🔄 Proceeding with deletion...');
    
    try {
      setDeleting(true);
      console.log('📡 Making DELETE request to:', `/companies/${companyId}/projects/${projectId}/executions/${selectedExecution.executionId}`);
      
      const response = await api.delete(`/companies/${companyId}/projects/${projectId}/executions/${selectedExecution.executionId}`);
      console.log('✅ Delete response:', response);
      
      // Refresh the executions list
      console.log('🔄 Refreshing executions list...');
      await fetchExecutions();
      
      setDeleteDialogOpen(false);
      setSelectedExecution(null);
      console.log('✅ Execution deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting execution:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      alert(error.response?.data?.message || 'Failed to delete execution');
    } finally {
      setDeleting(false);
    }
  };

  const renderCurrentStep = (execution) => {
    if (execution.status === 'completed') {
      return (
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2" fontWeight="medium" color="success.main">
              Completed
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary">
            Workflow finished
          </Typography>
        </Box>
      );
    } 
    
    if (execution.status === 'failed') {
      return (
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorIcon color="error" fontSize="small" />
            <Typography variant="body2" fontWeight="medium" color="error.main">
              Failed
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary">
            {execution.currentStepName || 'Unknown step'}
          </Typography>
        </Box>
      );
    }
    
    if (execution.status === 'in_progress') {
      return (
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <ScheduleIcon color="warning" fontSize="small" />
            <Typography variant="body2" fontWeight="medium" color="warning.main">
              In Progress
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary">
            {execution.currentStepName || `Step ${execution.progress?.completedSteps + 1 || 1}`}
          </Typography>
        </Box>
      );
    }
    
    if (execution.status === 'waiting_input') {
      return (
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon fontSize="small" color="info" />
            <Typography variant="body2" fontWeight="medium" color="info.main">
              Awaiting Input
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary">
            {execution.currentStepName || 'User input required'}
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box>
        <Typography variant="body2" color="textSecondary">
          {execution.status || 'Unknown'}
        </Typography>
      </Box>
    );
  };

  if (!isActive) {
    return null; // Don't render anything if tab is not active
  }

  if (loading && executions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with filters */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} flexWrap="wrap">
        <Typography variant="h6">Recent Executions</Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchExecutions}
            disabled={loading}
            size="small"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
          {lastRefresh && (
            <Typography variant="caption" color="textSecondary">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Accordion sx={{ mb: 3, bgcolor: '#ffffff' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon />
            <Typography variant="subtitle1" fontWeight="medium">
              Filters & Sort
            </Typography>
            {(statusFilter || dateRangeFilter || searchFilter || sortOrder !== 'newest') && (
              <Chip label="Active" color="primary" size="small" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search executions"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by execution ID, variables..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={handleSearch}
                        disabled={!searchInput.trim()}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        Search
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleFilterChange('status')}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="waiting_input">Waiting Input</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRangeFilter}
                  onChange={handleFilterChange('dateRange')}
                  label="Date Range"
                >
                  <MenuItem value="">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="year">This Year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortOrder}
                  onChange={handleSortChange}
                  label="Sort By"
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={12}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                disabled={!statusFilter && !dateRangeFilter && !searchFilter && !searchInput && sortOrder === 'newest'}
                sx={{ minWidth: 120 }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {executions.length === 0 && !loading ? (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center', bgcolor: '#ffffff' }}>
          <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Execution History
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            {statusFilter || dateRangeFilter ? 
              'No executions found matching your filters. Try adjusting the filters or clearing them.' :
              'This project hasn\'t been executed yet. Start your first workflow to see execution history here.'
            }
          </Typography>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => navigate(`/company/${companyId}/project/${projectId}/flow`)}
          >
            Start First Execution
          </Button>
        </Paper>
      ) : (
        <Card elevation={2} sx={{ bgcolor: '#ffffff' }}>
          <CardContent sx={{ p: 0, bgcolor: '#ffffff' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Execution</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Current Step</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Files</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {executions.map((execution) => (
                    <TableRow key={execution.executionId} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          {getStatusIcon(execution.status)}
                          <Box>
                            <Typography variant="subtitle2" fontWeight="medium">
                              #{execution.executionId.slice(-8)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {execution.executionId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={execution.status}
                          color={getStatusColor(execution.status)}
                          size="small"
                          variant="outlined"
                        />
                        {execution.hasErrors && (
                          <Chip
                            label="Has Errors"
                            color="error"
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {execution.progress?.percentage || 0}%
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {execution.progress?.completedSteps || 0}/{execution.progress?.totalSteps || 0} steps
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {renderCurrentStep(execution)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(execution.startedAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(execution.startedAt).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDuration(
                            // For in-progress executions, prefer actualProcessingTime (completed steps only)
                            // For completed/failed executions, use actualProcessingTime if available, otherwise fall back to duration
                            (execution.status === 'in_progress' || execution.status === 'waiting_input') 
                              ? (execution.actualProcessingTime || 0)
                              : (execution.actualProcessingTime || execution.duration)
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AssignmentIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {execution.filesCount || 0}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          {(() => {
                            const actionButton = getActionButton(execution);
                            return (
                              <IconButton
                                size="small"
                                onClick={() => handleViewExecution(execution)}
                                title={actionButton.title}
                                color={execution.status === 'in_progress' || execution.status === 'waiting_input' ? 'primary' : 'default'}
                              >
                                {actionButton.icon}
                              </IconButton>
                            );
                          })()}
                          
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, execution)}
                            title="More Actions"
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </CardContent>
        </Card>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleDeleteExecution} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete Execution
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleting) {
            setDeleteDialogOpen(false);
            setSelectedExecution(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <DeleteIcon color="error" />
            Delete Execution
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The execution and all its associated data will be permanently removed.
          </Alert>
          
          {selectedExecution && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete this execution?
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Execution Details:
                </Typography>
                <Typography variant="body2">
                  <strong>ID:</strong> #{selectedExecution.executionId.slice(-8)}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {selectedExecution.status}
                </Typography>
                <Typography variant="body2">
                  <strong>Started:</strong> {new Date(selectedExecution.startedAt).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedExecution(null);
            }} 
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteExecution}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Errors Tab - Track and manage execution errors
const ErrorsTab = ({ companyId, projectId, project, isActive }) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState([]);
  const [errorTypes, setErrorTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [dateRangeFilter, setDateRangeFilter] = useState('');
  const [errorTypeFilter, setErrorTypeFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  
  // Hide dialog state
  const [hideDialogOpen, setHideDialogOpen] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [hiding, setHiding] = useState(false);

  const fetchErrors = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        limit: rowsPerPage,
        page: page + 1,
        dateRange: dateRangeFilter,
        errorType: errorTypeFilter,
        showHidden: showHidden.toString()
      };

      if (searchFilter && searchFilter.trim()) {
        params.search = searchFilter.trim();
      }

      const response = await api.get(`/companies/${companyId}/projects/${projectId}/errors`, { params });
      setErrors(response.data.errors || []);
      setErrorTypes(response.data.errorTypes || []);
      setTotalCount(response.data.pagination?.total || 0);
      setLastRefresh(new Date());
    } catch (err) {
      setError('Failed to fetch errors');
      console.error('Errors fetch error:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isActive) {
      fetchErrors();
    }
  }, [companyId, projectId, page, rowsPerPage, dateRangeFilter, errorTypeFilter, searchFilter, showHidden, isActive]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setSearchFilter(searchInput);
    setPage(0);
  };

  const clearFilters = () => {
    setDateRangeFilter('');
    setErrorTypeFilter('');
    setSearchInput('');
    setSearchFilter('');
    setShowHidden(false);
    setPage(0);
  };

  const handleViewExecution = (executionId) => {
    navigate(`/company/${companyId}/project/${projectId}/execution/${executionId}/results`);
  };

  const handleToggleExpand = (errorKey) => {
    setExpandedRow(expandedRow === errorKey ? null : errorKey);
  };

  const handleHideError = (errorItem) => {
    setSelectedError(errorItem);
    setHideDialogOpen(true);
  };

  const confirmHideError = async () => {
    if (!selectedError) return;

    try {
      setHiding(true);
      const newHiddenState = !selectedError.hidden;
      await api.patch(
        `/companies/${companyId}/projects/${projectId}/errors/${selectedError.executionId}/${selectedError.errorIndex}/hide`,
        { hidden: newHiddenState }
      );
      
      await fetchErrors();
      setHideDialogOpen(false);
      setSelectedError(null);
    } catch (err) {
      console.error('Error hiding/unhiding error:', err);
      alert(err.response?.data?.message || 'Failed to update error visibility');
    } finally {
      setHiding(false);
    }
  };

  const formatErrorType = (type) => {
    if (!type) return 'Unknown';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!isActive) {
    return null;
  }

  if (loading && errors.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} flexWrap="wrap">
        <Typography variant="h6">Execution Errors</Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchErrors}
            disabled={loading}
            size="small"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
          {lastRefresh && (
            <Typography variant="caption" color="textSecondary">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Accordion sx={{ mb: 3, bgcolor: '#ffffff' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon />
            <Typography variant="subtitle1" fontWeight="medium">
              Filters
            </Typography>
            {(dateRangeFilter || errorTypeFilter || searchFilter || showHidden) && (
              <Chip label="Active" color="primary" size="small" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search in error message"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search errors..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={handleSearch}
                        disabled={!searchInput.trim()}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        Search
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Error Type</InputLabel>
                <Select
                  value={errorTypeFilter}
                  onChange={(e) => { setErrorTypeFilter(e.target.value); setPage(0); }}
                  label="Error Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  {errorTypes.map((type) => (
                    <MenuItem key={type} value={type}>{formatErrorType(type)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRangeFilter}
                  onChange={(e) => { setDateRangeFilter(e.target.value); setPage(0); }}
                  label="Date Range"
                >
                  <MenuItem value="">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="year">This Year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showHidden}
                    onChange={(e) => { setShowHidden(e.target.checked); setPage(0); }}
                  />
                }
                label="Show Hidden"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                disabled={!dateRangeFilter && !errorTypeFilter && !searchFilter && !searchInput && !showHidden}
                sx={{ minWidth: 120 }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {errors.length === 0 && !loading ? (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center', bgcolor: '#ffffff' }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Errors Found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {dateRangeFilter || errorTypeFilter || searchFilter ? 
              'No errors found matching your filters. Try adjusting or clearing them.' :
              showHidden ? 
                'There are no errors (including hidden ones) for this project.' :
                'Great news! There are no visible errors for this project.'
            }
          </Typography>
        </Paper>
      ) : (
        <Card elevation={2} sx={{ bgcolor: '#ffffff' }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={40}></TableCell>
                    <TableCell>Error Message</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Execution</TableCell>
                    <TableCell>Step</TableCell>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errors.map((errorItem) => {
                    const errorKey = `${errorItem.executionId}-${errorItem.errorIndex}`;
                    const isExpanded = expandedRow === errorKey;
                    
                    return (
                      <React.Fragment key={errorKey}>
                        <TableRow 
                          hover 
                          sx={{ 
                            bgcolor: errorItem.hidden ? 'grey.100' : 'inherit',
                            opacity: errorItem.hidden ? 0.7 : 1
                          }}
                        >
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleExpand(errorKey)}
                            >
                              <ExpandMoreIcon
                                sx={{
                                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s'
                                }}
                              />
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="flex-start" gap={1}>
                              <ErrorIcon color="error" fontSize="small" sx={{ mt: 0.3 }} />
                              <Box>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    maxWidth: 400,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {errorItem.errorMessage || 'No error message'}
                                </Typography>
                                {errorItem.hidden && (
                                  <Chip label="Hidden" size="small" variant="outlined" sx={{ mt: 0.5 }} />
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={formatErrorType(errorItem.errorType)}
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                #{errorItem.executionId.slice(-8)}
                              </Typography>
                              <Chip
                                label={errorItem.executionStatus}
                                size="small"
                                variant="outlined"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {errorItem.stepName || errorItem.stepId?.slice(-8) || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(errorItem.timestamp).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(errorItem.timestamp).toLocaleTimeString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={1}>
                              <IconButton
                                size="small"
                                onClick={() => handleViewExecution(errorItem.executionId)}
                                title="View Execution Details"
                              >
                                <VisibilityIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleHideError(errorItem)}
                                title={errorItem.hidden ? 'Unhide Error' : 'Hide Error'}
                              >
                                {errorItem.hidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                        
                        {/* Expandable Details Row */}
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 0, borderBottom: isExpanded ? 1 : 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                  Error Details
                                </Typography>
                                
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="textSecondary">Full Error Message:</Typography>
                                    <Paper sx={{ p: 1.5, mt: 0.5, bgcolor: 'error.50' }}>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {errorItem.errorMessage || 'No error message available'}
                                      </Typography>
                                    </Paper>
                                  </Grid>
                                  
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="textSecondary">Execution Info:</Typography>
                                    <Paper sx={{ p: 1.5, mt: 0.5 }}>
                                      <Typography variant="body2">
                                        <strong>Execution ID:</strong> {errorItem.executionId}
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>Status:</strong> {errorItem.executionStatus}
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>Started:</strong> {new Date(errorItem.executionStartedAt).toLocaleString()}
                                      </Typography>
                                      {errorItem.executionCompletedAt && (
                                        <Typography variant="body2">
                                          <strong>Completed:</strong> {new Date(errorItem.executionCompletedAt).toLocaleString()}
                                        </Typography>
                                      )}
                                    </Paper>
                                  </Grid>
                                  
                                  {errorItem.errorDetails && Object.keys(errorItem.errorDetails).length > 0 && (
                                    <Grid item xs={12}>
                                      <Typography variant="caption" color="textSecondary">Additional Details:</Typography>
                                      <Paper sx={{ p: 1.5, mt: 0.5, maxHeight: 200, overflow: 'auto' }}>
                                        <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                          {JSON.stringify(errorItem.errorDetails, null, 2)}
                                        </pre>
                                      </Paper>
                                    </Grid>
                                  )}
                                </Grid>
                                
                                <Box mt={2}>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => handleViewExecution(errorItem.executionId)}
                                  >
                                    View Full Execution
                                  </Button>
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </CardContent>
        </Card>
      )}

      {/* Hide/Unhide Confirmation Dialog */}
      <Dialog
        open={hideDialogOpen}
        onClose={() => {
          if (!hiding) {
            setHideDialogOpen(false);
            setSelectedError(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            {selectedError?.hidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
            {selectedError?.hidden ? 'Unhide Error' : 'Hide Error'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {selectedError?.hidden 
              ? 'This will make the error visible again in the errors list.' 
              : 'This will hide the error from the default view. Hidden errors can be shown by enabling "Show Hidden" in the filters.'}
          </Typography>
          
          {selectedError && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Error Details:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Message:</strong> {selectedError.errorMessage?.substring(0, 100)}
                {selectedError.errorMessage?.length > 100 ? '...' : ''}
              </Typography>
              <Typography variant="body2">
                <strong>Execution:</strong> #{selectedError.executionId.slice(-8)}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {new Date(selectedError.timestamp).toLocaleString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setHideDialogOpen(false);
              setSelectedError(null);
            }} 
            disabled={hiding}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmHideError}
            variant="contained"
            color={selectedError?.hidden ? 'primary' : 'warning'}
            disabled={hiding}
            startIcon={hiding ? <CircularProgress size={20} /> : (selectedError?.hidden ? <VisibilityIcon /> : <VisibilityOffIcon />)}
          >
            {hiding ? 'Processing...' : (selectedError?.hidden ? 'Unhide' : 'Hide')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const CollaborationTab = ({ companyId, projectId, project, isActive }) => {
  const [collaborationSettings, setCollaborationSettings] = useState({
    allowCollaborativeExecution: false,
    continuationTimeLimit: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
    requiredPermissions: ['user'],
    notifyOriginalUser: true,
    allowCrossRoleContinuation: true,
    maxContinuations: 5,
    stepNotifications: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Step notification states
  const [availableSteps, setAvailableSteps] = useState([]);
  const [availableVariables, setAvailableVariables] = useState([]);
  const [stepNotificationDialogOpen, setStepNotificationDialogOpen] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [newNotification, setNewNotification] = useState({
    stepId: '',
    stepName: '',
    triggerEvent: 'step_stopped',
    delayMinutes: 0,
    message: {
      title: '',
      body: '',
      useVariableTemplates: true
    },
    recipients: {
      type: 'all_project_users',
      specificUsers: [],
      includeAdmins: true
    },
    conditions: [],
    tags: [],
    priority: 'normal',
    isActive: true
  });
  const [deleteNotificationDialogOpen, setDeleteNotificationDialogOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);

  // Fetch collaboration settings
  const fetchCollaborationSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}`);
      
      // The response.data is the project directly
      if (response.data.collaborationSettings) {
        setCollaborationSettings(response.data.collaborationSettings);
      }
    } catch (err) {
      setError('Failed to fetch collaboration settings');
      console.error('Collaboration settings fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available flow steps
  const fetchAvailableSteps = async () => {
    try {
      const response = await flowStepsApi.getFlowData(projectId);
      setAvailableSteps(response.steps || []);
    } catch (err) {
      console.error('Failed to fetch flow steps:', err);
      setAvailableSteps([]);
    }
  };

  // Fetch available variables for templates
  const fetchAvailableVariables = async () => {
    try {
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/notification-variables?limit=20`);
      setAvailableVariables(response.data.availableVariables || []);
    } catch (err) {
      console.error('Failed to fetch available variables:', err);
      setAvailableVariables([]);
    }
  };

  // Save step notification
  const saveStepNotification = async () => {
    try {
      setSaving(true);
      setError('');
      
      if (editingNotification) {
        // Update existing notification
        await api.put(`/companies/${companyId}/projects/${projectId}/step-notifications/${editingNotification.id}`, newNotification);
        setSuccess('Step notification updated successfully!');
      } else {
        // Create new notification
        await api.post(`/companies/${companyId}/projects/${projectId}/step-notifications`, newNotification);
        setSuccess('Step notification created successfully!');
      }
      
      // Refresh collaboration settings to get updated notifications
      await fetchCollaborationSettings();
      
      // Close dialog and reset form
      setStepNotificationDialogOpen(false);
      setEditingNotification(null);
      resetNotificationForm();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save step notification');
      console.error('Step notification save error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Delete step notification
  const deleteStepNotification = async (notificationId) => {
    try {
      setSaving(true);
      setError('');
      
      await api.delete(`/companies/${companyId}/projects/${projectId}/step-notifications/${notificationId}`);
      
      // Refresh collaboration settings
      await fetchCollaborationSettings();
      
      setSuccess('Step notification deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete step notification');
      console.error('Step notification delete error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Reset notification form
  const resetNotificationForm = () => {
    setNewNotification({
      stepId: '',
      stepName: '',
      triggerEvent: 'step_stopped',
      delayMinutes: 0,
      message: {
        title: '',
        body: '',
        useVariableTemplates: true
      },
      recipients: {
        type: 'all_project_users',
        specificUsers: [],
        includeAdmins: true
      },
      conditions: [],
      tags: [],
      priority: 'normal',
      isActive: true
    });
  };

  // Helper function to resolve stepId (handles both MongoDB _id and persistent stepId)
  const resolveStepId = (stepId) => {
    // If stepId is already a persistent stepId (starts with 'step_'), return it
    if (stepId && stepId.startsWith('step_')) {
      return stepId;
    }
    
    // If stepId looks like a MongoDB _id (24 hex chars), find the corresponding persistent stepId
    if (stepId && stepId.match(/^[0-9a-fA-F]{24}$/)) {
      const step = availableSteps.find(s => s.id === stepId);
      return step?.stepId || stepId; // Return persistent stepId if found, otherwise keep original
    }
    
    return stepId;
  };

  // Save collaboration settings
  const saveCollaborationSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const updatedSettings = {
        ...collaborationSettings,
        lastUpdatedAt: new Date()
      };
      
      await api.put(`/companies/${companyId}/projects/${projectId}`, {
        collaborationSettings: updatedSettings
      });
      
      setSuccess('Collaboration settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save collaboration settings');
      console.error('Collaboration settings save error:', err);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (isActive && projectId && companyId) {
      fetchCollaborationSettings();
      fetchAvailableSteps();
      fetchAvailableVariables();
    }
  }, [isActive, projectId, companyId]);

  const handleToggleCollaboration = (event) => {
    setCollaborationSettings(prev => ({
      ...prev,
      allowCollaborativeExecution: event.target.checked
    }));
  };

  const handleTimeLimitChange = (event) => {
    const hours = parseInt(event.target.value);
    setCollaborationSettings(prev => ({
      ...prev,
      continuationTimeLimit: hours * 60 * 60 * 1000
    }));
  };

  const handleMaxContinuationsChange = (event) => {
    setCollaborationSettings(prev => ({
      ...prev,
      maxContinuations: parseInt(event.target.value)
    }));
  };

  const handleNotifyToggle = (event) => {
    setCollaborationSettings(prev => ({
      ...prev,
      notifyOriginalUser: event.target.checked
    }));
  };

  const handleCrossRoleToggle = (event) => {
    setCollaborationSettings(prev => ({
      ...prev,
      allowCrossRoleContinuation: event.target.checked
    }));
  };

  if (!isActive) {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Collaboration Settings
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Configure how users can collaborate on executions for this project
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Main Collaboration Toggle */}
        <Grid size={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={collaborationSettings.allowCollaborativeExecution}
                  onChange={handleToggleCollaboration}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="h6">Enable Collaborative Execution</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Allow other users to continue incomplete executions started by different users
                  </Typography>
                </Box>
              }
            />
          </Paper>
        </Grid>

        {/* Collaboration Settings */}
        {collaborationSettings.allowCollaborativeExecution && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={1} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Time Limits
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Continuation Time Limit</InputLabel>
                  <Select
                    value={Math.round(collaborationSettings.continuationTimeLimit / (60 * 60 * 1000))}
                    onChange={handleTimeLimitChange}
                    label="Continuation Time Limit"
                  >
                    <MenuItem value={1}>1 Hour</MenuItem>
                    <MenuItem value={6}>6 Hours</MenuItem>
                    <MenuItem value={12}>12 Hours</MenuItem>
                    <MenuItem value={24}>24 Hours</MenuItem>
                    <MenuItem value={48}>48 Hours</MenuItem>
                    <MenuItem value={72}>72 Hours</MenuItem>
                    <MenuItem value={168}>1 Week</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="textSecondary">
                  How long other users can continue an execution after it becomes incomplete
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={1} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Collaboration Limits
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Collaborators"
                  value={collaborationSettings.maxContinuations}
                  onChange={handleMaxContinuationsChange}
                  inputProps={{ min: 1, max: 20 }}
                  sx={{ mb: 2 }}
                />
                <Typography variant="body2" color="textSecondary">
                  Maximum number of different users who can work on the same execution
                </Typography>
              </Paper>
            </Grid>

            <Grid size={12}>
              <Paper elevation={1} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Notification Settings
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={collaborationSettings.notifyOriginalUser}
                      onChange={handleNotifyToggle}
                      color="primary"
                    />
                  }
                  label="Notify original user when someone continues their execution"
                  sx={{ mb: 2 }}
                />
                <br />
                <FormControlLabel
                  control={
                    <Switch
                      checked={collaborationSettings.allowCrossRoleContinuation}
                      onChange={handleCrossRoleToggle}
                      color="primary"
                    />
                  }
                  label="Allow users with different roles to continue executions"
                />
              </Paper>
            </Grid>
          </>
        )}

        {/* Step Notifications Section */}
        <Grid size={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Step Notifications
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Send push notifications when users stop on specific workflow steps
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetNotificationForm();
                  setEditingNotification(null);
                  setStepNotificationDialogOpen(true);
                }}
                disabled={availableSteps.length === 0}
              >
                Add Notification
              </Button>
            </Box>

            {availableSteps.length === 0 ? (
              <Alert severity="info">
                <Typography variant="body2">
                  No workflow steps found. Design your workflow first to set up step notifications.
                </Typography>
              </Alert>
            ) : collaborationSettings.stepNotifications?.length === 0 ? (
              <Alert severity="info">
                <Typography variant="body2">
                  No step notifications configured. Click "Add Notification" to create your first step notification.
                </Typography>
              </Alert>
            ) : (
              <List>
                {collaborationSettings.stepNotifications?.map((notification, index) => (
                  <ListItem key={notification.id} divider={index < collaborationSettings.stepNotifications.length - 1}>
                    <ListItemIcon>
                      <Chip 
                        icon={notification.isActive ? <NotificationsIcon /> : <VisibilityOffIcon />}
                        label={notification.triggerEvent.replace('_', ' ')}
                        color={notification.isActive ? 'primary' : 'default'}
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {notification.stepName}
                          </Typography>
                          <Chip 
                            label={notification.priority} 
                            size="small" 
                            color={notification.priority === 'urgent' ? 'error' : notification.priority === 'high' ? 'warning' : 'default'}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Title:</strong> {notification.message.title}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                            <strong>Message:</strong> {notification.message.body}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                            <strong>Recipients:</strong> {notification.recipients.type.replace('_', ' ')}
                            {notification.delayMinutes > 0 && ` • Delay: ${notification.delayMinutes} minutes`}
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip 
                              label={notification.stepId.startsWith('step_') ? 'Persistent ID' : 'Legacy ID'} 
                              size="small" 
                              variant="outlined"
                              color={notification.stepId.startsWith('step_') ? 'success' : 'warning'}
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          </Box>
                          {notification.tags && notification.tags.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {notification.tags.map((tag, idx) => (
                                <Chip 
                                  key={idx} 
                                  label={tag} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              ))}
                            </Box>
                          )}
                          {notification.statistics && (
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                              Triggered: {notification.statistics.totalTriggered} times • Sent: {notification.statistics.totalSent} notifications
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const resolvedStepId = resolveStepId(notification.stepId);
                          setNewNotification({
                            ...notification,
                            stepId: resolvedStepId, // Use persistent stepId
                            tags: notification.tags || [] // Ensure tags is always an array
                          });
                          setEditingNotification(notification);
                          setStepNotificationDialogOpen(true);
                        }}
                        title="Edit notification"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setNotificationToDelete(notification);
                          setDeleteNotificationDialogOpen(true);
                        }}
                        title="Delete notification"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Save Button */}
        <Grid size={12}>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={saveCollaborationSettings}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Step Notification Dialog */}
      <Dialog 
        open={stepNotificationDialogOpen} 
        onClose={() => setStepNotificationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <NotificationsIcon color="primary" />
            {editingNotification ? 'Edit Step Notification' : 'Add Step Notification'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Step Selection */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Workflow Step</InputLabel>
                <Select
                  value={newNotification.stepId}
                  onChange={(e) => {
                    const selectedStep = availableSteps.find(step => step.stepId === e.target.value);
                    setNewNotification(prev => ({
                      ...prev,
                      stepId: e.target.value,
                      stepName: selectedStep?.name || ''
                    }));
                  }}
                  label="Workflow Step"
                  required
                >
                  {availableSteps.map((step) => (
                    <MenuItem key={step.stepId || step.id} value={step.stepId || step.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip label={step.type} size="small" />
                        {step.name}
                        {step.stepId && (
                          <Chip 
                            label="Persistent ID" 
                            size="small" 
                            variant="outlined" 
                            color="success"
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Trigger Event */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Trigger Event</InputLabel>
                <Select
                  value={newNotification.triggerEvent}
                  onChange={(e) => setNewNotification(prev => ({ ...prev, triggerEvent: e.target.value }))}
                  label="Trigger Event"
                >
                  <MenuItem value="step_stopped">User Stops on Step</MenuItem>
                  <MenuItem value="step_waiting_input">Step Waiting for Input</MenuItem>
                  <MenuItem value="step_failed">Step Failed</MenuItem>
                  <MenuItem value="step_timeout">Step Timeout</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Message Title */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Notification Title"
                value={newNotification.message.title}
                onChange={(e) => setNewNotification(prev => ({
                  ...prev,
                  message: { ...prev.message, title: e.target.value }
                }))}
                required
                helperText="Short title for the push notification. Use {{variableName}} for dynamic content (max 100 characters)"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>

            {/* Message Body */}
            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notification Message"
                value={newNotification.message.body}
                onChange={(e) => setNewNotification(prev => ({
                  ...prev,
                  message: { ...prev.message, body: e.target.value }
                }))}
                required
                helperText="Use {{variableName}} to include dynamic data from the execution (max 300 characters)"
                inputProps={{ maxLength: 300 }}
              />
            </Grid>

            {/* Variables Helper */}
            {availableVariables.length > 0 && (
              <Grid size={12}>
                <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Available Variables for Templates:
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Click a variable to add it to the message body. Ctrl+Click to add to the title.
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {availableVariables.slice(0, 10).map((variable) => (
                      <Chip
                        key={variable.variableName}
                        label={`{{${variable.variableName}}}`}
                        size="small"
                        onClick={(e) => {
                          const template = `{{${variable.variableName}}}`;
                          if (e.ctrlKey || e.metaKey) {
                            // Add to title when Ctrl/Cmd is held
                            setNewNotification(prev => ({
                              ...prev,
                              message: {
                                ...prev.message,
                                title: prev.message.title + template
                              }
                            }));
                          } else {
                            // Add to body by default
                            setNewNotification(prev => ({
                              ...prev,
                              message: {
                                ...prev.message,
                                body: prev.message.body + template
                              }
                            }));
                          }
                        }}
                        clickable
                        variant="outlined"
                        title={`Click to add to body, Ctrl+Click to add to title`}
                      />
                    ))}
                    {availableVariables.length > 10 && (
                      <Typography variant="caption" color="textSecondary">
                        and {availableVariables.length - 10} more...
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Recipients */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Recipients</InputLabel>
                <Select
                  value={newNotification.recipients.type}
                  onChange={(e) => setNewNotification(prev => ({
                    ...prev,
                    recipients: { ...prev.recipients, type: e.target.value }
                  }))}
                  label="Recipients"
                >
                  <MenuItem value="all_project_users">All Project Users</MenuItem>
                  <MenuItem value="original_user">Original User Only</MenuItem>
                  <MenuItem value="admin_only">Admins Only</MenuItem>
                  <MenuItem value="specific_users">Specific Users</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Priority */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newNotification.priority}
                  onChange={(e) => setNewNotification(prev => ({ ...prev, priority: e.target.value }))}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Tags */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Tags (comma-separated)"
                value={newNotification.tags.join(', ')}
                onChange={(e) => {
                  const tagsString = e.target.value;
                  const tagsArray = tagsString ? tagsString.split(',').map(tag => tag.trim()).filter(Boolean) : [];
                  setNewNotification(prev => ({ ...prev, tags: tagsArray }));
                }}
                placeholder="e.g. urgent, workflow, admin"
                helperText="Enter tags separated by commas to categorize notifications"
              />
            </Grid>

            {/* Delay */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Delay (Minutes)"
                value={newNotification.delayMinutes}
                onChange={(e) => setNewNotification(prev => ({ ...prev, delayMinutes: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 0, max: 1440 }}
                helperText="Delay before sending notification (0 = immediate)"
              />
            </Grid>

            {/* Active Toggle */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newNotification.isActive}
                    onChange={(e) => setNewNotification(prev => ({ ...prev, isActive: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Active"
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStepNotificationDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={saveStepNotification}
            variant="contained"
            disabled={saving || !newNotification.stepId || !newNotification.message.title || !newNotification.message.body}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : editingNotification ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteNotificationDialogOpen}
        onClose={() => setDeleteNotificationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <DeleteIcon color="error" />
            Delete Step Notification
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The step notification will be permanently removed.
          </Alert>
          
          {notificationToDelete && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete this step notification?
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Notification Details:
                </Typography>
                <Typography variant="body2">
                  <strong>Step:</strong> {notificationToDelete.stepName}
                </Typography>
                <Typography variant="body2">
                  <strong>Trigger:</strong> {notificationToDelete.triggerEvent.replace('_', ' ')}
                </Typography>
                <Typography variant="body2">
                  <strong>Title:</strong> {notificationToDelete.message.title}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteNotificationDialogOpen(false)} 
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (notificationToDelete) {
                deleteStepNotification(notificationToDelete.id);
                setDeleteNotificationDialogOpen(false);
                setNotificationToDelete(null);
              }
            }}
            variant="contained"
            color="error"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const FilesTab = ({ companyId, projectId, project, isActive, navigate }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  // Fetch files from the backend
  const fetchFiles = async () => {
    if (!isActive) return;

    setLoading(true);
    setError('');
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        fileType: fileTypeFilter,
        dateRange: dateRangeFilter
      };

      const response = await api.get(`/companies/${companyId}/projects/${projectId}/files`, { params });
      setFiles(response.data.files || []);
      setTotalCount(response.data.pagination?.total || 0);
      setLastRefresh(new Date());
      console.log('✅ Files loaded:', response.data.files?.length);
    } catch (err) {
      setError('Failed to fetch files');
      console.error('Files fetch error:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isActive) {
      fetchFiles();
    }
  }, [companyId, projectId, page, rowsPerPage, searchTerm, fileTypeFilter, dateRangeFilter, isActive]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleFileTypeFilterChange = (event) => {
    setFileTypeFilter(event.target.value);
    setPage(0);
  };

  const handleDateRangeFilterChange = (event) => {
    setDateRangeFilter(event.target.value);
    setPage(0);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFileTypeFilter('');
    setDateRangeFilter('');
    setPage(0);
  };

  const handleDownloadFile = async (file) => {
    try {
      console.log('📥 Downloading file:', file.originalName);
      
      const response = await api.get(
        `/companies/${companyId}/projects/${projectId}/files/${file.id}/download`,
        { responseType: 'blob' }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log('✅ File download completed:', file.originalName);
    } catch (error) {
      console.error('❌ File download failed:', error);
      setError(`Failed to download ${file.originalName}: ${error.response?.data?.message || error.message}`);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileTypeIcon = (fileType) => {
    switch (fileType?.toLowerCase()) {
      case 'pdf':
        return <AssignmentIcon color="error" />;
      case 'image':
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <VisibilityIcon color="info" />;
      case 'excel':
      case 'xlsx':
      case 'xls':
        return <DataObjectIcon color="success" />;
      case 'word':
      case 'doc':
      case 'docx':
        return <AssignmentIcon color="primary" />;
      default:
        return <AssignmentIcon color="action" />;
    }
  };

  const getStorageLocationChip = (file) => {
    if (file.uploadedToS3) {
      return (
        <Chip
          label="S3 Storage"
          color="success"
          size="small"
          icon={<AccountBalanceWalletIcon />}
          title="File is securely stored in S3 with encryption"
        />
      );
    } else {
      return (
        <Chip
          label="Local Storage"
          color="warning"
          size="small"
          icon={<DataObjectIcon />}
          title="File is stored locally (will be migrated to S3)"
        />
      );
    }
  };

  if (!isActive) {
    return null;
  }

  if (loading && files.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={40} />
        <Box ml={2}>
          <Typography variant="body2" color="textSecondary">
            Loading files...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with search and filters */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} flexWrap="wrap">
        <Typography variant="h6">Project Files</Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchFiles}
            disabled={loading}
            size="small"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
          {lastRefresh && (
            <Typography variant="caption" color="textSecondary">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon />
            <Typography variant="subtitle1" fontWeight="medium">
              Search & Filters
            </Typography>
            {(searchTerm || fileTypeFilter || dateRangeFilter) && (
              <Chip label="Active" color="primary" size="small" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search files"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by filename..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>File Type</InputLabel>
                <Select
                  value={fileTypeFilter}
                  onChange={handleFileTypeFilterChange}
                  label="File Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="pdf">PDF</MenuItem>
                  <MenuItem value="image">Images</MenuItem>
                  <MenuItem value="excel">Excel</MenuItem>
                  <MenuItem value="word">Word</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRangeFilter}
                  onChange={handleDateRangeFilterChange}
                  label="Date Range"
                >
                  <MenuItem value="">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="year">This Year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                disabled={!searchTerm && !fileTypeFilter && !dateRangeFilter}
                fullWidth
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {files.length === 0 && !loading ? (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center' }}>
          <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Files Found
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            {searchTerm || fileTypeFilter || dateRangeFilter ? 
              'No files found matching your search criteria. Try adjusting the filters or clearing them.' :
              'No files have been uploaded to this project yet. Files will appear here after users upload them through workflow executions.'
            }
          </Typography>
          {(searchTerm || fileTypeFilter || dateRangeFilter) && (
            <Button
              variant="outlined"
              onClick={clearFilters}
            >
              Clear Filters
            </Button>
          )}
        </Paper>
      ) : (
        <Card elevation={2}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Storage</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell>Execution</TableCell>
                    <TableCell>Uploaded By</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((file, index) => (
                    <TableRow key={`${file.id}-${index}`} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          {getFileTypeIcon(file.fileType)}
                          <Box>
                            <Typography variant="subtitle2" fontWeight="medium">
                              {file.originalName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {file.filename}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={file.fileType || 'Unknown'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatFileSize(file.fileSize)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getStorageLocationChip(file)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(file.uploadedAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(file.uploadedAt).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <MuiTooltip title={`Execution: ${file.executionId}`}>
                          <Chip
                            label={`#${file.executionId.slice(-8)}`}
                            size="small"
                            clickable
                            onClick={() => navigate(`/company/${companyId}/project/${projectId}/execution/${file.executionId}/results`)}
                          />
                        </MuiTooltip>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {file.executionStatus}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {file.uploadedBy ? (
                          <Box>
                            <Typography variant="body2">
                              {file.uploadedBy.name || file.uploadedBy.email}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {file.uploadedBy.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Unknown
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleDownloadFile(file)}
                          title="Download file"
                        >
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

const SearchConfigurationTab = ({ companyId, projectId, project, isActive }) => {
  const [searchConfig, setSearchConfig] = useState({
    enabled: true,
    searchableFields: [],
    defaultSearchFields: [],
    searchBehavior: {
      matchAll: false,
      fuzzySearch: false,
      maxResults: 100,
      highlightResults: true
    }
  });
  const [availableVariables, setAvailableVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  
  // Fetch current configuration when tab becomes active
  useEffect(() => {
    if (isActive && companyId && projectId) {
      fetchSearchConfig();
      fetchAvailableVariables();
    }
  }, [isActive, companyId, projectId]);

  const fetchSearchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/search-config`);
      setSearchConfig(response.data.searchConfiguration);
      console.log('✅ Search configuration loaded:', response.data.searchConfiguration);
    } catch (error) {
      console.error('❌ Error fetching search configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableVariables = async () => {
    try {
      setAnalysisLoading(true);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/available-variables?limit=20`);
      setAvailableVariables(response.data.availableVariables);
      console.log('🔍 Available variables loaded for search:', response.data.availableVariables.length, 'variables');
    } catch (error) {
      console.error('❌ Error fetching available variables:', error);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const saveSearchConfig = async () => {
    try {
      setSaving(true);
      await api.put(`/companies/${companyId}/projects/${projectId}/search-config`, {
        searchConfiguration: searchConfig
      });
      console.log('✅ Search configuration saved successfully');
    } catch (error) {
      console.error('❌ Error saving search configuration:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldToggle = (variable) => {
    const fieldName = `variables.${variable.variableName}`;
    const isSelected = searchConfig.searchableFields.some(f => f.fieldName === fieldName);
    
    if (isSelected) {
      // Remove field
      setSearchConfig(prev => ({
        ...prev,
        searchableFields: prev.searchableFields.filter(f => f.fieldName !== fieldName)
      }));
    } else {
      // Add field
      const newField = {
        fieldName,
        displayName: variable.displayName,
        searchType: 'contains',
        priority: searchConfig.searchableFields.length,
        isActive: true,
        dataType: variable.dataType || 'string',
        searchOptions: {
          caseSensitive: false,
          allowPartial: true,
          exactMatchOnly: false
        }
      };
      
      setSearchConfig(prev => ({
        ...prev,
        searchableFields: [...prev.searchableFields, newField]
      }));
    }
  };

  const handleBuiltInFieldToggle = (fieldName, displayName, dataType = 'string') => {
    const isSelected = searchConfig.searchableFields.some(f => f.fieldName === fieldName);
    
    if (isSelected) {
      // Remove field
      setSearchConfig(prev => ({
        ...prev,
        searchableFields: prev.searchableFields.filter(f => f.fieldName !== fieldName)
      }));
    } else {
      // Add field
      const newField = {
        fieldName,
        displayName,
        searchType: fieldName === 'executionId' ? 'contains' : (dataType === 'date' ? 'date_range' : 'contains'),
        priority: searchConfig.searchableFields.length,
        isActive: true,
        dataType,
        searchOptions: {
          caseSensitive: false,
          allowPartial: true,
          exactMatchOnly: false
        }
      };
      
      setSearchConfig(prev => ({
        ...prev,
        searchableFields: [...prev.searchableFields, newField]
      }));
    }
  };

  const handleFieldUpdate = (fieldName, updates) => {
    setSearchConfig(prev => ({
      ...prev,
      searchableFields: prev.searchableFields.map(field => 
        field.fieldName === fieldName 
          ? { ...field, ...updates }
          : field
      )
    }));
  };

  const handleDefaultFieldToggle = (fieldName) => {
    const isDefault = searchConfig.defaultSearchFields.includes(fieldName);
    
    if (isDefault) {
      setSearchConfig(prev => ({
        ...prev,
        defaultSearchFields: prev.defaultSearchFields.filter(f => f !== fieldName)
      }));
    } else {
      setSearchConfig(prev => ({
        ...prev,
        defaultSearchFields: [...prev.defaultSearchFields, fieldName]
      }));
    }
  };

  const builtInFields = [
    { fieldName: 'executionId', displayName: 'Execution ID', dataType: 'string' },
    { fieldName: 'status', displayName: 'Status', dataType: 'string' },
    { fieldName: 'startedAt', displayName: 'Started At', dataType: 'date' },
    { fieldName: 'completedAt', displayName: 'Completed At', dataType: 'date' },
  ];

  if (!isActive) {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={40} />
        <Box ml={2}>
          <Typography variant="body2" color="textSecondary">
            Loading search configuration...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Search Configuration
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Configure which fields can be searched in execution history and how search queries are processed.
      </Typography>

      <Grid container spacing={3}>
        {/* Enable/Disable Search */}
        <Grid size={{ xs: 12 }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={searchConfig.enabled}
                  onChange={(e) => setSearchConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Enable Search Functionality
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Allow users to search through execution history based on configured fields
                  </Typography>
                </Box>
              }
            />
          </Paper>
        </Grid>

        {searchConfig.enabled && (
          <>
            {/* Built-in Fields */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={1} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Built-in Fields
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Standard execution fields available for search.
                </Typography>

                <List dense>
                  {builtInFields.map((field) => {
                    const isSelected = searchConfig.searchableFields.some(f => f.fieldName === field.fieldName);
                    const isDefault = searchConfig.defaultSearchFields.includes(field.fieldName);
                    
                    return (
                      <ListItem key={field.fieldName} disablePadding>
                        <ListItemButton onClick={() => handleBuiltInFieldToggle(field.fieldName, field.displayName, field.dataType)}>
                          <ListItemIcon>
                            <Checkbox 
                              edge="start" 
                              checked={isSelected}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'}>
                                  {field.displayName}
                                </Typography>
                                <Chip 
                                  label={field.dataType} 
                                  size="small" 
                                  variant="outlined"
                                />
                                {isDefault && (
                                  <Chip 
                                    label="Default" 
                                    size="small" 
                                    color="primary"
                                  />
                                )}
                              </Box>
                            }
                            secondary={`Field: ${field.fieldName}`}
                          />
                          {isSelected && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDefaultFieldToggle(field.fieldName);
                              }}
                              title={isDefault ? "Remove from default search" : "Add to default search"}
                            >
                              {isDefault ? <VisibilityIcon color="primary" /> : <VisibilityOffIcon />}
                            </IconButton>
                          )}
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            </Grid>

            {/* Variable Fields */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={1} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    <DataObjectIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Variable Fields
                  </Typography>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={fetchAvailableVariables}
                    disabled={analysisLoading}
                    size="small"
                  >
                    {analysisLoading ? 'Analyzing...' : 'Refresh'}
                  </Button>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Variables detected from recent executions. Click to add/remove from search configuration.
                </Typography>

                {analysisLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : availableVariables.length === 0 ? (
                  <Alert severity="info">
                    No variables found in recent executions. Variables will appear here after you run some executions.
                  </Alert>
                ) : (
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <List dense>
                      {availableVariables.map((variable) => {
                        const fieldName = `variables.${variable.variableName}`;
                        const isSelected = searchConfig.searchableFields.some(f => f.fieldName === fieldName);
                        const isDefault = searchConfig.defaultSearchFields.includes(fieldName);
                        
                        return (
                          <ListItem key={variable.variableName} disablePadding>
                            <ListItemButton onClick={() => handleFieldToggle(variable)}>
                              <ListItemIcon>
                                <Checkbox 
                                  edge="start" 
                                  checked={isSelected}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              <ListItemText 
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'}>
                                      {variable.displayName}
                                    </Typography>
                                    <Chip 
                                      label={variable.dataType} 
                                      size="small" 
                                      variant="outlined"
                                    />
                                    {isDefault && (
                                      <Chip 
                                        label="Default" 
                                        size="small" 
                                        color="primary"
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <React.Fragment>
                                    <Typography variant="caption" color="textSecondary" component="span" display="block">
                                      Variable: {variable.variableName} • Found in {variable.frequency} executions
                                    </Typography>
                                    {variable.sampleValue && (
                                      <Typography variant="caption" color="textSecondary" component="span" display="block">
                                        Sample: {variable.sampleValue}
                                      </Typography>
                                    )}
                                  </React.Fragment>
                                }
                              />
                              {isSelected && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDefaultFieldToggle(fieldName);
                                  }}
                                  title={isDefault ? "Remove from default search" : "Add to default search"}
                                >
                                  {isDefault ? <VisibilityIcon color="primary" /> : <VisibilityOffIcon />}
                                </IconButton>
                              )}
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Search Field Configuration */}
            {searchConfig.searchableFields.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Paper elevation={1} sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Search Field Configuration
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Configure search behavior for each selected field.
                  </Typography>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          <TableCell>Search Type</TableCell>
                          <TableCell>Case Sensitive</TableCell>
                          <TableCell>Default Search</TableCell>
                          <TableCell>Active</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {searchConfig.searchableFields.map((field) => (
                          <TableRow key={field.fieldName}>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {field.displayName}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" fontFamily="monospace">
                                  {field.fieldName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                  value={field.searchType}
                                  onChange={(e) => handleFieldUpdate(field.fieldName, { searchType: e.target.value })}
                                >
                                  <MenuItem value="exact">Exact Match</MenuItem>
                                  <MenuItem value="contains">Contains</MenuItem>
                                  <MenuItem value="regex">Regex</MenuItem>
                                  {field.dataType === 'date' && <MenuItem value="date_range">Date Range</MenuItem>}
                                  {field.dataType === 'number' && <MenuItem value="number_range">Number Range</MenuItem>}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={field.searchOptions?.caseSensitive || false}
                                onChange={(e) => handleFieldUpdate(field.fieldName, { 
                                  searchOptions: { ...field.searchOptions, caseSensitive: e.target.checked }
                                })}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={searchConfig.defaultSearchFields.includes(field.fieldName)}
                                onChange={() => handleDefaultFieldToggle(field.fieldName)}
                                size="small"
                                color="primary"
                              />
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={field.isActive}
                                onChange={(e) => handleFieldUpdate(field.fieldName, { isActive: e.target.checked })}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}

            {/* Search Behavior */}
            <Grid size={{ xs: 12 }}>
              <Paper elevation={1} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Search Behavior
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Global settings that affect how search queries are processed.
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={searchConfig.searchBehavior.matchAll}
                          onChange={(e) => setSearchConfig(prev => ({
                            ...prev,
                            searchBehavior: { ...prev.searchBehavior, matchAll: e.target.checked }
                          }))}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            Match All Terms (AND Logic)
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            When enabled, all search terms must match. When disabled, any term can match (OR logic).
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={searchConfig.searchBehavior.highlightResults}
                          onChange={(e) => setSearchConfig(prev => ({
                            ...prev,
                            searchBehavior: { ...prev.searchBehavior, highlightResults: e.target.checked }
                          }))}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            Highlight Search Results
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Highlight matching terms in search results for better visibility.
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Maximum Results"
                      value={searchConfig.searchBehavior.maxResults}
                      onChange={(e) => setSearchConfig(prev => ({
                        ...prev,
                        searchBehavior: { ...prev.searchBehavior, maxResults: parseInt(e.target.value) || 100 }
                      }))}
                      inputProps={{ min: 10, max: 1000 }}
                      helperText="Maximum number of search results to return"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Save Button */}
            <Grid size={{ xs: 12 }}>
              <Box display="flex" justifyContent="center" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    fetchSearchConfig();
                    fetchAvailableVariables();
                  }}
                  disabled={loading || saving}
                >
                  Reset Changes
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={saveSearchConfig}
                  disabled={loading || saving}
                >
                  {saving ? 'Saving...' : 'Save Configuration'}
                </Button>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

const HistoryDisplayTab = ({ companyId, projectId, project, isActive }) => {
  const [historyConfig, setHistoryConfig] = useState({
    selectedVariables: [],
    defaultColumns: [
      { columnName: 'executionId', displayName: 'Execution ID', columnWidth: 180, showInList: true, sortOrder: 0 },
      { columnName: 'status', displayName: 'Status', columnWidth: 120, showInList: true, sortOrder: 1 },
      { columnName: 'startedAt', displayName: 'Started', columnWidth: 160, showInList: true, sortOrder: 2 },
      { columnName: 'batchId', displayName: 'Batch ID', columnWidth: 220, showInList: false, sortOrder: 3 },
      { columnName: 'batchName', displayName: 'Batch Name', columnWidth: 200, showInList: false, sortOrder: 4 },
      { columnName: 'batchSequence', displayName: 'Batch #', columnWidth: 80, showInList: false, sortOrder: 5 }
    ],
    maxColumns: 8,
    autoDetectVariables: true,
    variableFilters: [],
    filterLogic: 'or'
  });
  const [availableVariables, setAvailableVariables] = useState([]);
  const [availableSteps, setAvailableSteps] = useState([]);
  const [companyCustomFields, setCompanyCustomFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState(null);
  
  // Fetch current configuration when tab becomes active
  useEffect(() => {
    if (isActive && companyId && projectId) {
      fetchHistoryConfig();
      fetchAvailableVariables();
      fetchAvailableSteps();
      fetchCompanyCustomFields();
    }
  }, [isActive, companyId, projectId]);

  const fetchHistoryConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/history-display`);
      setHistoryConfig(response.data.historyDisplayConfig);
      console.log('✅ History display configuration loaded:', response.data.historyDisplayConfig);
    } catch (error) {
      console.error('❌ Error fetching history display configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableVariables = async () => {
    try {
      setAnalysisLoading(true);
      // Increased limit to 50 to capture more variables from recent executions
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/available-variables?limit=50`);
      setAvailableVariables(response.data.availableVariables);
      console.log('🔍 Available variables loaded:', response.data.availableVariables.length, 'variables');
    } catch (error) {
      console.error('❌ Error fetching available variables:', error);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const fetchAvailableSteps = async () => {
    try {
      // Get flow steps from project configuration
      if (project?.flow?.steps) {
        const steps = project.flow.steps.map(step => ({
          stepId: step._id || step.id,
          persistentStepId: step.stepId,
          stepName: step.name,
          stepType: step.type
        }));
        setAvailableSteps(steps);
        console.log('🔍 Available steps loaded:', steps.length, 'steps');
      }
    } catch (error) {
      console.error('❌ Error fetching available steps:', error);
    }
  };

  const fetchCompanyCustomFields = async () => {
    try {
      const response = await api.get(`/companies/${companyId}/custom-fields`);
      // API returns customClientUserFields
      const fields = response.data.customClientUserFields || response.data.customFields || [];
      setCompanyCustomFields(fields);
      console.log('🔍 Company custom fields loaded:', fields.length, 'fields');
    } catch (error) {
      console.error('❌ Error fetching company custom fields:', error);
      // Set empty array on error - this is not critical
      setCompanyCustomFields([]);
    }
  };

  // Variable Filter handlers
  const handleAddVariableFilter = () => {
    setEditingFilter({
      id: `filter_${Date.now()}`,
      variableName: '',
      matchType: 'equals',
      clientUserField: '',
      isActive: true
    });
    setFilterDialogOpen(true);
  };

  const handleEditVariableFilter = (filter) => {
    setEditingFilter({ ...filter });
    setFilterDialogOpen(true);
  };

  const handleSaveVariableFilter = () => {
    if (!editingFilter.variableName || !editingFilter.clientUserField) {
      return; // Don't save incomplete filters
    }

    setHistoryConfig(prev => {
      const existingIndex = prev.variableFilters?.findIndex(f => f.id === editingFilter.id) ?? -1;
      const updatedFilters = [...(prev.variableFilters || [])];
      
      if (existingIndex >= 0) {
        updatedFilters[existingIndex] = editingFilter;
      } else {
        updatedFilters.push(editingFilter);
      }
      
      return {
        ...prev,
        variableFilters: updatedFilters
      };
    });
    
    setFilterDialogOpen(false);
    setEditingFilter(null);
  };

  const handleDeleteVariableFilter = (filterId) => {
    setHistoryConfig(prev => ({
      ...prev,
      variableFilters: (prev.variableFilters || []).filter(f => f.id !== filterId)
    }));
  };

  const handleToggleVariableFilter = (filterId) => {
    setHistoryConfig(prev => ({
      ...prev,
      variableFilters: (prev.variableFilters || []).map(f =>
        f.id === filterId ? { ...f, isActive: !f.isActive } : f
      )
    }));
  };

  const handleFilterLogicChange = (event) => {
    setHistoryConfig(prev => ({
      ...prev,
      filterLogic: event.target.value
    }));
  };

  const saveHistoryConfig = async () => {
    try {
      setSaving(true);
      await api.put(`/companies/${companyId}/projects/${projectId}/history-display`, {
        historyDisplayConfig: historyConfig
      });
      console.log('✅ History display configuration saved successfully');
    } catch (error) {
      console.error('❌ Error saving history display configuration:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleVariableToggle = (variable) => {
    const isSelected = historyConfig.selectedVariables.some(v => v.variableName === variable.variableName);
    
    if (isSelected) {
      // Remove variable
      setHistoryConfig(prev => ({
        ...prev,
        selectedVariables: prev.selectedVariables.filter(v => v.variableName !== variable.variableName)
      }));
    } else {
      // Add variable
      const newVariable = {
        variableName: variable.variableName,
        displayName: variable.displayName,
        dataType: variable.dataType,
        columnWidth: 150,
        showInList: true,
        sortOrder: historyConfig.selectedVariables.length
      };
      
      setHistoryConfig(prev => ({
        ...prev,
        selectedVariables: [...prev.selectedVariables, newVariable]
      }));
    }
  };

  const handleColumnToggle = (columnName) => {
    setHistoryConfig(prev => ({
      ...prev,
      defaultColumns: prev.defaultColumns.map(col => 
        col.columnName === columnName 
          ? { ...col, showInList: !col.showInList }
          : col
      )
    }));
  };

  const handleDisplayNameChange = (variableName, newDisplayName) => {
    setHistoryConfig(prev => ({
      ...prev,
      selectedVariables: prev.selectedVariables.map(v => 
        v.variableName === variableName 
          ? { ...v, displayName: newDisplayName }
          : v
      )
    }));
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        <ViewColumnIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        History Display Configuration
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Configure which variables appear as columns in the execution history list.
      </Typography>

      <Grid container spacing={3}>
        {/* Default Columns Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Default Columns
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Standard columns that appear in all execution lists.
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={30} />
              </Box>
            ) : (
              <List dense>
                {historyConfig.defaultColumns.map((column) => (
                  <ListItem key={column.columnName} disablePadding>
                    <ListItemButton onClick={() => handleColumnToggle(column.columnName)}>
                      <ListItemIcon>
                        {column.showInList ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </ListItemIcon>
                      <ListItemText 
                        primary={column.displayName}
                        secondary={`Column: ${column.columnName}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Available Variables Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                <DataObjectIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Available Variables
              </Typography>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchAvailableVariables}
                disabled={analysisLoading}
                size="small"
              >
                {analysisLoading ? 'Analyzing...' : 'Refresh'}
              </Button>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Variables detected from recent executions. Click to add/remove from history display.
            </Typography>

            {analysisLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={30} />
              </Box>
            ) : availableVariables.length === 0 ? (
              <Alert severity="info">
                No variables found in recent executions. Variables will appear here after you run some executions.
              </Alert>
            ) : (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                <List dense>
                  {availableVariables.map((variable) => {
                    const isSelected = historyConfig.selectedVariables.some(v => v.variableName === variable.variableName);
                    return (
                      <ListItem key={variable.variableName} disablePadding>
                        <ListItemButton onClick={() => handleVariableToggle(variable)}>
                          <ListItemIcon>
                            <Checkbox 
                              edge="start" 
                              checked={isSelected}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'}>
                                  {variable.displayName}
                                </Typography>
                                <Chip 
                                  label={variable.dataType} 
                                  size="small" 
                                  variant="outlined"
                                />
                                {variable.isPotentialVariable && (
                                  <Chip 
                                    label="Step Timing" 
                                    size="small" 
                                    color="info" 
                                    variant="outlined"
                                    sx={{ fontSize: '0.65rem', height: '18px' }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <React.Fragment>
                                <Typography variant="caption" color="textSecondary" component="span" display="block">
                                  Variable: {variable.variableName} • {variable.isPotentialVariable ? 'Will appear after execution' : `Found in ${variable.frequency} executions`}
                                </Typography>
                                {variable.sampleValue && (
                                  <Typography variant="caption" color="textSecondary" component="span" display="block">
                                    Sample: {variable.sampleValue}
                                  </Typography>
                                )}
                              </React.Fragment>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Selected Variables Configuration */}
        {historyConfig.selectedVariables.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <EditIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Selected Variables Configuration
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Customize display names and settings for selected variables.
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Variable Name</TableCell>
                      <TableCell>Display Name</TableCell>
                      <TableCell>Data Type</TableCell>
                      <TableCell>Column Width</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyConfig.selectedVariables.map((variable, index) => (
                      <TableRow key={variable.variableName}>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {variable.variableName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={variable.displayName}
                            onChange={(e) => handleDisplayNameChange(variable.variableName, e.target.value)}
                            variant="outlined"
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label={variable.dataType} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {variable.columnWidth}px
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small" 
                            onClick={() => handleVariableToggle(variable)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        {/* Variable Filters Section for Email/Webhook Executions */}
        <Grid size={{ xs: 12 }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  <FilterListIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Variable Filters for Email/Webhook Executions
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Filter email and webhook executions based on matching execution variables with client user custom fields.
                  When configured, users will only see email/webhook executions where the specified variable matches their custom field value.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddVariableFilter}
                disabled={companyCustomFields.length === 0}
              >
                Add Filter
              </Button>
            </Box>

            {companyCustomFields.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No custom fields configured for client users in this company. Configure custom fields in Company Settings first.
              </Alert>
            )}

            {availableVariables.length === 0 && companyCustomFields.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No execution variables detected from recent executions. You can still add filters by typing the variable name manually.
              </Alert>
            )}

            {historyConfig.variableFilters && historyConfig.variableFilters.length > 0 && (
              <>
                {/* Filter Logic Toggle */}
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Filter Logic:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={historyConfig.filterLogic || 'or'}
                      onChange={handleFilterLogicChange}
                    >
                      <MenuItem value="or">OR (match any)</MenuItem>
                      <MenuItem value="and">AND (match all)</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="textSecondary">
                    {historyConfig.filterLogic === 'and' 
                      ? 'Execution must match ALL filters to be visible'
                      : 'Execution must match ANY filter to be visible'
                    }
                  </Typography>
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Active</TableCell>
                        <TableCell>Execution Variable</TableCell>
                        <TableCell>Match Type</TableCell>
                        <TableCell>Client User Field</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historyConfig.variableFilters.map((filter) => (
                        <TableRow key={filter.id} sx={{ opacity: filter.isActive ? 1 : 0.5 }}>
                          <TableCell>
                            <Switch
                              checked={filter.isActive}
                              onChange={() => handleToggleVariableFilter(filter.id)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {filter.variableName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={filter.matchType === 'equals' ? 'Equals' : filter.matchType === 'contains' ? 'Contains' : 'In Array'} 
                              size="small" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {companyCustomFields.find(f => f.fieldName === filter.clientUserField)?.displayName || filter.clientUserField}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditVariableFilter(filter)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteVariableFilter(filter.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {(!historyConfig.variableFilters || historyConfig.variableFilters.length === 0) && companyCustomFields.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                No variable filters configured. All email/webhook executions will be visible to users with project access.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Variable Filter Dialog */}
        <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingFilter?.id?.startsWith('filter_') && !historyConfig.variableFilters?.find(f => f.id === editingFilter.id)
              ? 'Add Variable Filter'
              : 'Edit Variable Filter'
            }
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Autocomplete
                freeSolo
                options={availableVariables.map(v => v.variableName)}
                value={editingFilter?.variableName || ''}
                onChange={(event, newValue) => {
                  setEditingFilter(prev => ({ ...prev, variableName: newValue || '' }));
                }}
                onInputChange={(event, newInputValue) => {
                  setEditingFilter(prev => ({ ...prev, variableName: newInputValue }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Execution Variable"
                    helperText="Select from list or type a custom variable name"
                    fullWidth
                  />
                )}
                renderOption={(props, option) => {
                  const variable = availableVariables.find(v => v.variableName === option);
                  return (
                    <li {...props} key={option}>
                      <Box>
                        <Typography variant="body2">{variable?.displayName || option}</Typography>
                        <Typography variant="caption" color="textSecondary">{option}</Typography>
                      </Box>
                    </li>
                  );
                }}
              />

              <FormControl fullWidth>
                <InputLabel>Match Type</InputLabel>
                <Select
                  value={editingFilter?.matchType || 'equals'}
                  onChange={(e) => setEditingFilter(prev => ({ ...prev, matchType: e.target.value }))}
                  label="Match Type"
                >
                  <MenuItem value="equals">Equals (exact match)</MenuItem>
                  <MenuItem value="contains">Contains (partial match)</MenuItem>
                  <MenuItem value="in_array">In Array (value in list)</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Client User Custom Field</InputLabel>
                <Select
                  value={editingFilter?.clientUserField || ''}
                  onChange={(e) => setEditingFilter(prev => ({ ...prev, clientUserField: e.target.value }))}
                  label="Client User Custom Field"
                >
                  {companyCustomFields.map((field) => (
                    <MenuItem key={field.fieldName} value={field.fieldName}>
                      {field.displayName} ({field.fieldName})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  When this filter is active, email/webhook executions will only be visible to users 
                  whose <strong>{companyCustomFields.find(f => f.fieldName === editingFilter?.clientUserField)?.displayName || 'selected field'}</strong> value 
                  {editingFilter?.matchType === 'equals' ? ' equals ' : editingFilter?.matchType === 'contains' ? ' contains ' : ' is in '}
                  the execution&apos;s <strong>{editingFilter?.variableName || 'selected variable'}</strong> value.
                </Typography>
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveVariableFilter} 
              variant="contained"
              disabled={!editingFilter?.variableName || !editingFilter?.clientUserField}
            >
              Save Filter
            </Button>
          </DialogActions>
        </Dialog>

        {/* Save Button */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                fetchHistoryConfig();
                fetchAvailableVariables();
                fetchAvailableSteps();
                fetchCompanyCustomFields();
              }}
              disabled={loading || saving}
            >
              Reset Changes
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={saveHistoryConfig}
              disabled={loading || saving}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const ExternalDataIntegrationTab = ({ companyId, projectId, project, isActive }) => {
  // Google Sheets configuration state
  const [googleSheetsConfig, setGoogleSheetsConfig] = useState({
    clientId: '',
    clientSecret: '',
    isConfigured: false,
    isAuthenticated: false,
    lastAuthenticatedAt: null
  });
  const [googleSheetsSaving, setGoogleSheetsSaving] = useState(false);
  const [googleSheetsError, setGoogleSheetsError] = useState('');
  
  // MySQL configuration state
  const [mysqlConfig, setMysqlConfig] = useState({
    host: '',
    port: 3306,
    database: '',
    username: '',
    password: '',
    connectionLimit: 10,
    connectTimeout: 10000,
    ssl: { enabled: false, rejectUnauthorized: true },
    isConfigured: false,
    isConnected: false,
    lastTestedAt: null,
    lastTestResult: ''
  });
  const [mysqlDialogOpen, setMysqlDialogOpen] = useState(false);
  const [mysqlTesting, setMysqlTesting] = useState(false);
  const [mysqlSaving, setMysqlSaving] = useState(false);
  const [mysqlError, setMysqlError] = useState('');
  const [showMysqlPassword, setShowMysqlPassword] = useState(false);
  
  // Xero configuration state
  const [xeroConfig, setXeroConfig] = useState({
    isConfigured: false,
    isAuthenticated: false,
    tenants: [],
    selectedTenantId: null,
    selectedTenantName: null,
    lastAuthenticatedAt: null
  });
  const [xeroTenants, setXeroTenants] = useState([]);
  const [xeroTenantDialogOpen, setXeroTenantDialogOpen] = useState(false);
  const [xeroAuthenticating, setXeroAuthenticating] = useState(false);
  const [xeroError, setXeroError] = useState('');
  const [xeroAuthMode, setXeroAuthMode] = useState('user');
  const [xeroFallbackBehavior, setXeroFallbackBehavior] = useState('use_project_auth');
  
  const [loading, setLoading] = useState(false);
  
  // Load configs when tab becomes active
  useEffect(() => {
    if (isActive) {
      fetchGoogleSheetsConfig();
      fetchMysqlConfig();
      fetchXeroConfig();
    }
  }, [isActive, companyId, projectId]);

  const fetchGoogleSheetsConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/google-auth/status/${projectId}`);
      if (response.data.success) {
        setGoogleSheetsConfig(response.data.config);
      }
    } catch (error) {
      console.error('Error fetching Google Sheets config:', error);
      setGoogleSheetsError('Failed to load Google Sheets configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticateGoogleSheets = async () => {
    try {
      setGoogleSheetsError('');
      
      // First, initialize Google Sheets integration if needed
      if (!googleSheetsConfig.isConfigured) {
        console.log('Initializing Google Sheets integration...');
        await api.post(`/google-auth/configure/${projectId}`, {});
      }
      
      // Then start OAuth flow
      const response = await api.get(`/google-auth/oauth/${projectId}`);
      if (response.data.success && response.data.authUrl) {
        window.open(response.data.authUrl, '_blank', 'width=600,height=600');
        // Poll for authentication status
        const pollAuth = setInterval(async () => {
          try {
            const statusResponse = await api.get(`/google-auth/status/${projectId}`);
            console.log('Polling auth status:', statusResponse.data);
            if (statusResponse.data.success && statusResponse.data.config.isAuthenticated) {
              clearInterval(pollAuth);
              // Update the state directly with the response
              setGoogleSheetsConfig(statusResponse.data.config);
              console.log('✅ Google Sheets authentication successful!');
            }
          } catch (error) {
            console.error('Error checking auth status:', error);
          }
        }, 2000);
        
        // Stop polling after 5 minutes
        setTimeout(() => clearInterval(pollAuth), 300000);
      }
    } catch (error) {
      console.error('Error initiating Google Sheets authentication:', error);
      setGoogleSheetsError(error.response?.data?.message || 'Failed to initiate authentication');
    }
  };

  const handleRevokeGoogleSheetsAccess = async () => {
    try {
      const response = await api.post(`/google-auth/revoke/${projectId}`);
      if (response.data.success) {
        await fetchGoogleSheetsConfig();
        console.log('Google Sheets access revoked successfully');
      }
    } catch (error) {
      console.error('Error revoking Google Sheets access:', error);
      setGoogleSheetsError(error.response?.data?.message || 'Failed to revoke access');
    }
  };

  // MySQL configuration functions
  const fetchMysqlConfig = async () => {
    try {
      const response = await api.get(`/mysql-config/status/${projectId}`);
      if (response.data.success) {
        setMysqlConfig(prev => ({
          ...prev,
          ...response.data.config
        }));
      }
    } catch (error) {
      console.error('Error fetching MySQL config:', error);
    }
  };

  const handleTestMysqlConnection = async () => {
    try {
      setMysqlTesting(true);
      setMysqlError('');
      
      const response = await api.post(`/mysql-config/test/${projectId}`, {
        host: mysqlConfig.host,
        port: mysqlConfig.port,
        database: mysqlConfig.database,
        username: mysqlConfig.username,
        password: mysqlConfig.password,
        ssl: mysqlConfig.ssl
      });

      if (response.data.success) {
        setMysqlError('');
        alert(`Connection successful!\n\nDatabase: ${response.data.database}\nMySQL Version: ${response.data.version}`);
      } else {
        setMysqlError(response.data.message || 'Connection test failed');
      }
    } catch (error) {
      console.error('Error testing MySQL connection:', error);
      setMysqlError(error.response?.data?.message || 'Failed to test connection');
    } finally {
      setMysqlTesting(false);
    }
  };

  const handleSaveMysqlConfig = async () => {
    try {
      setMysqlSaving(true);
      setMysqlError('');
      
      const response = await api.post(`/mysql-config/configure/${projectId}`, {
        host: mysqlConfig.host,
        port: mysqlConfig.port,
        database: mysqlConfig.database,
        username: mysqlConfig.username,
        password: mysqlConfig.password,
        connectionLimit: mysqlConfig.connectionLimit,
        connectTimeout: mysqlConfig.connectTimeout,
        ssl: mysqlConfig.ssl
      });

      if (response.data.success) {
        await fetchMysqlConfig();
        setMysqlDialogOpen(false);
        alert(response.data.message);
      } else {
        setMysqlError(response.data.message || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving MySQL config:', error);
      setMysqlError(error.response?.data?.message || 'Failed to save configuration');
    } finally {
      setMysqlSaving(false);
    }
  };

  const handleDeleteMysqlConfig = async () => {
    if (!window.confirm('Are you sure you want to delete the MySQL configuration? This will remove all connection details.')) {
      return;
    }

    try {
      const response = await api.delete(`/mysql-config/${projectId}`);
      if (response.data.success) {
        await fetchMysqlConfig();
        setMysqlDialogOpen(false);
        alert('MySQL configuration deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting MySQL config:', error);
      setMysqlError(error.response?.data?.message || 'Failed to delete configuration');
    }
  };

  // Xero configuration functions
  const fetchXeroConfig = async () => {
    try {
      const response = await api.get(`/xero-auth/status/${projectId}`);
      if (response.data.success) {
        setXeroConfig(response.data.config);
        setXeroAuthMode(response.data.authMode || 'user');
        setXeroFallbackBehavior(response.data.fallbackBehavior || 'use_project_auth');
        
        // Fetch tenants if authenticated
        if (response.data.config.isAuthenticated) {
          fetchXeroTenants();
        }
      }
    } catch (error) {
      console.error('Error fetching Xero config:', error);
      setXeroError('Failed to load Xero configuration');
    }
  };

  const fetchXeroTenants = async () => {
    try {
      const response = await api.get(`/xero-auth/tenants/${projectId}`);
      if (response.data.success) {
        setXeroTenants(response.data.tenants);
      }
    } catch (error) {
      console.error('Error fetching Xero tenants:', error);
    }
  };

  const handleAuthenticateXero = async () => {
    try {
      setXeroAuthenticating(true);
      setXeroError('');
      
      const response = await api.get(`/xero-auth/oauth/${projectId}`);
      
      if (response.data.success) {
        // Open OAuth URL in popup window
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
          response.data.authUrl,
          'Xero Authentication',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        if (!popup) {
          setXeroError('Popup was blocked. Please allow popups for this site and try again.');
          setXeroAuthenticating(false);
          return;
        }

        // Poll for popup closure
        const pollTimer = setInterval(() => {
          try {
            if (popup.closed) {
              clearInterval(pollTimer);
              setXeroAuthenticating(false);
              console.log('Xero popup closed, refreshing config...');
              // Refresh config after authentication
              setTimeout(() => {
                fetchXeroConfig();
              }, 1000);
            }
          } catch (e) {
            // Popup might be on different origin, ignore errors
            console.log('Popup check error (expected):', e.message);
          }
        }, 500);

        // Safety timeout - stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(pollTimer);
          if (!popup.closed) {
            setXeroAuthenticating(false);
            console.log('Popup polling timeout reached');
          }
        }, 300000);
      } else {
        setXeroError(response.data.message || 'Failed to initiate authentication');
        setXeroAuthenticating(false);
      }
    } catch (error) {
      console.error('Error authenticating Xero:', error);
      setXeroError(error.response?.data?.message || 'Failed to initiate authentication');
      setXeroAuthenticating(false);
    }
  };

  const handleSelectXeroTenant = async (tenantId) => {
    try {
      const response = await api.post(`/xero-auth/select-tenant/${projectId}`, {
        tenantId
      });
      
      if (response.data.success) {
        await fetchXeroConfig();
        setXeroTenantDialogOpen(false);
        alert(`Organization selected: ${response.data.selectedTenant.tenantName}`);
      } else {
        setXeroError(response.data.message || 'Failed to select organization');
      }
    } catch (error) {
      console.error('Error selecting Xero tenant:', error);
      setXeroError(error.response?.data?.message || 'Failed to select organization');
    }
  };

  const handleRevokeXeroAccess = async () => {
    if (!window.confirm('Are you sure you want to disconnect Xero? This will remove all authentication tokens.')) {
      return;
    }

    try {
      const response = await api.post(`/xero-auth/revoke/${projectId}`);
      if (response.data.success) {
        await fetchXeroConfig();
        setXeroTenants([]);
        alert('Xero disconnected successfully');
      }
    } catch (error) {
      console.error('Error revoking Xero access:', error);
      setXeroError(error.response?.data?.message || 'Failed to disconnect Xero');
    }
  };

  const handleUpdateXeroAuthMode = async () => {
    try {
      const response = await api.put(`/companies/${companyId}/projects/${projectId}/xero-auth-mode`, {
        authMode: xeroAuthMode,
        fallbackBehavior: xeroFallbackBehavior
      });
      if (response.data.success) {
        alert('Xero authentication mode updated successfully');
      }
    } catch (error) {
      console.error('Error updating Xero auth mode:', error);
      setXeroError(error.response?.data?.message || 'Failed to update authentication mode');
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        <CloudSyncIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        External Data Integration
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={4}>
        Connect your project to external data sources like Google Sheets, Microsoft Excel, and more. Configure authentication and manage your data connections.
      </Typography>

      {/* Google Sheets Integration */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <TableChartIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Google Sheets Integration
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Connect to Google Sheets to read, write, and search data in your spreadsheets
            </Typography>
          </Box>
        </Box>

        {googleSheetsError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {googleSheetsError}
          </Alert>
        )}

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            Configuration Status
          </Typography>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Chip 
              icon={googleSheetsConfig.isConfigured ? <CheckCircleIcon /> : <ErrorIcon />}
              label={googleSheetsConfig.isConfigured ? 'Configured' : 'Not Configured'}
              color={googleSheetsConfig.isConfigured ? 'success' : 'warning'}
              variant="outlined"
            />
            <Chip 
              icon={googleSheetsConfig.isAuthenticated ? <CheckCircleIcon /> : <ErrorIcon />}
              label={googleSheetsConfig.isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
              color={googleSheetsConfig.isAuthenticated ? 'success' : 'warning'}
              variant="outlined"
            />
          </Box>
          {googleSheetsConfig.lastAuthenticatedAt && (
            <Typography variant="body2" color="textSecondary">
              Last authenticated: {new Date(googleSheetsConfig.lastAuthenticatedAt).toLocaleString()}
            </Typography>
          )}
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            🚀 Simple Google Authentication
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            No complex setup required! Simply click "Login with Google" below to connect your Google Sheets.
            <br />
            <br />
            <strong>What you'll get access to:</strong>
            <br />
            • Read data from your Google Sheets
            <br />
            • Search and filter spreadsheet data
            <br />
            • Write data back to your sheets
            <br />
            • All with secure, limited access to only your spreadsheets
          </Typography>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Privacy & Security:</strong> We only request access to your Google Sheets (not Gmail, Drive, or other services). 
              You can revoke access at any time from your Google Account settings or from this page.
            </Typography>
          </Alert>
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap">
          {!googleSheetsConfig.isAuthenticated ? (
            <Button
              variant="contained"
              size="large"
              startIcon={<LinkIcon />}
              onClick={handleAuthenticateGoogleSheets}
              color="primary"
              sx={{ 
                bgcolor: '#4285f4', 
                '&:hover': { bgcolor: '#3367d6' },
                textTransform: 'none',
                fontSize: '16px',
                py: 1.5,
                px: 3
              }}
            >
              Login with Google
            </Button>
          ) : (
            <Button
              variant="outlined"
              startIcon={<ErrorIcon />}
              onClick={handleRevokeGoogleSheetsAccess}
              color="warning"
            >
              Disconnect Google Account
            </Button>
          )}
        </Box>
      </Paper>

      {/* MySQL Database Integration */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <StorageIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              MySQL Database Integration
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Connect to MySQL databases to query and retrieve data directly in your flows
            </Typography>
          </Box>
        </Box>

        {mysqlError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {mysqlError}
          </Alert>
        )}

        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            Configuration Status
          </Typography>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Chip 
              icon={mysqlConfig.isConfigured ? <CheckCircleIcon /> : <ErrorIcon />}
              label={mysqlConfig.isConfigured ? 'Configured' : 'Not Configured'}
              color={mysqlConfig.isConfigured ? 'success' : 'warning'}
              variant="outlined"
            />
            <Chip 
              icon={mysqlConfig.isConnected ? <CheckCircleIcon /> : <ErrorIcon />}
              label={mysqlConfig.isConnected ? 'Connection Verified' : 'Not Connected'}
              color={mysqlConfig.isConnected ? 'success' : 'warning'}
              variant="outlined"
            />
          </Box>
          {mysqlConfig.lastTestedAt && (
            <Typography variant="body2" color="textSecondary">
              Last tested: {new Date(mysqlConfig.lastTestedAt).toLocaleString()}
              {mysqlConfig.lastTestResult && ` - ${mysqlConfig.lastTestResult}`}
            </Typography>
          )}
          {mysqlConfig.isConfigured && mysqlConfig.database && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Database: {mysqlConfig.database}
            </Typography>
          )}
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={async () => {
              // If already configured, fetch full config for editing
              if (mysqlConfig.isConfigured) {
                try {
                  const response = await api.get(`/mysql-config/edit/${projectId}`);
                  if (response.data.success) {
                    setMysqlConfig(response.data.config);
                  }
                } catch (error) {
                  console.error('Error fetching MySQL config for editing:', error);
                }
              }
              setMysqlDialogOpen(true);
            }}
            color="primary"
          >
            {mysqlConfig.isConfigured ? 'Manage Connection' : 'Configure MySQL'}
          </Button>
          {mysqlConfig.isConfigured && (
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteMysqlConfig}
              color="error"
            >
              Remove Configuration
            </Button>
          )}
        </Box>
      </Paper>

      {/* MySQL Configuration Dialog */}
      <Dialog 
        open={mysqlDialogOpen} 
        onClose={() => setMysqlDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <StorageIcon color="primary" />
            <Typography variant="h6">MySQL Database Configuration</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {mysqlError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {mysqlError}
            </Alert>
          )}

          <Typography variant="body2" color="textSecondary" mb={3}>
            Enter your MySQL database connection details. These credentials will be stored securely and used when executing External Data Source steps.
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Host"
              value={mysqlConfig.host}
              onChange={(e) => setMysqlConfig({ ...mysqlConfig, host: e.target.value })}
              fullWidth
              required
              placeholder="localhost or 192.168.1.100"
              helperText="MySQL server hostname or IP address"
            />

            <TextField
              label="Port"
              type="number"
              value={mysqlConfig.port}
              onChange={(e) => setMysqlConfig({ ...mysqlConfig, port: parseInt(e.target.value) || 3306 })}
              fullWidth
              placeholder="3306"
              helperText="MySQL server port (default: 3306)"
            />

            <TextField
              label="Database Name"
              value={mysqlConfig.database}
              onChange={(e) => setMysqlConfig({ ...mysqlConfig, database: e.target.value })}
              fullWidth
              required
              placeholder="my_database"
              helperText="Name of the database to connect to"
            />

            <TextField
              label="Username"
              value={mysqlConfig.username}
              onChange={(e) => setMysqlConfig({ ...mysqlConfig, username: e.target.value })}
              fullWidth
              required
              placeholder="db_user"
              helperText="MySQL username"
            />

            <TextField
              label="Password"
              type={showMysqlPassword ? 'text' : 'password'}
              value={mysqlConfig.password}
              onChange={(e) => setMysqlConfig({ ...mysqlConfig, password: e.target.value })}
              fullWidth
              placeholder="••••••••"
              helperText="MySQL password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowMysqlPassword(!showMysqlPassword)}
                      edge="end"
                    >
                      {showMysqlPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              Advanced Options
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={mysqlConfig.ssl?.enabled || false}
                  onChange={(e) => setMysqlConfig({
                    ...mysqlConfig,
                    ssl: { ...mysqlConfig.ssl, enabled: e.target.checked }
                  })}
                />
              }
              label="Enable SSL Connection"
            />

            <TextField
              label="Connection Pool Limit"
              type="number"
              value={mysqlConfig.connectionLimit}
              onChange={(e) => setMysqlConfig({ ...mysqlConfig, connectionLimit: parseInt(e.target.value) || 10 })}
              fullWidth
              placeholder="10"
              helperText="Maximum number of connections in the pool (default: 10)"
            />

            <TextField
              label="Connection Timeout (ms)"
              type="number"
              value={mysqlConfig.connectTimeout}
              onChange={(e) => setMysqlConfig({ ...mysqlConfig, connectTimeout: parseInt(e.target.value) || 10000 })}
              fullWidth
              placeholder="10000"
              helperText="Connection timeout in milliseconds (default: 10000)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMysqlDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleTestMysqlConnection}
            disabled={!mysqlConfig.host || !mysqlConfig.database || !mysqlConfig.username || mysqlTesting}
            color="info"
            startIcon={mysqlTesting ? <CircularProgress size={20} /> : null}
          >
            {mysqlTesting ? 'Testing...' : 'Test Connection'}
          </Button>
          <Button
            onClick={handleSaveMysqlConfig}
            disabled={!mysqlConfig.host || !mysqlConfig.database || !mysqlConfig.username || mysqlSaving}
            variant="contained"
            startIcon={mysqlSaving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {mysqlSaving ? 'Saving...' : 'Save & Connect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Xero Integration */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <AccountBalanceIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Xero Integration
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Connect to Xero to access invoices, contacts, accounts, payments, and bank transactions in your flows
            </Typography>
          </Box>
        </Box>

        {xeroError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {xeroError}
          </Alert>
        )}

        {/* Authentication Mode Configuration */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            Authentication Mode
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Auth Mode</InputLabel>
            <Select
              value={xeroAuthMode}
              onChange={(e) => setXeroAuthMode(e.target.value)}
              label="Auth Mode"
            >
              <MenuItem value="user">User Authentication (Default)</MenuItem>
              <MenuItem value="project">Project Service Account</MenuItem>
            </Select>
            <FormHelperText>
              {xeroAuthMode === 'user' 
                ? 'Each user connects their own Xero account. Recommended for most use cases.'
                : 'Use a shared project-level Xero connection. Suitable for API/webhook executions.'}
            </FormHelperText>
          </FormControl>
          
          {xeroAuthMode === 'user' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Fallback for API/Webhook</InputLabel>
              <Select
                value={xeroFallbackBehavior}
                onChange={(e) => setXeroFallbackBehavior(e.target.value)}
                label="Fallback for API/Webhook"
              >
                <MenuItem value="use_project_auth">Use Project Auth (if configured)</MenuItem>
                <MenuItem value="fail_with_error">Fail with Error</MenuItem>
              </Select>
              <FormHelperText>
                What to do when API/webhook executions encounter Xero steps
              </FormHelperText>
            </FormControl>
          )}
          
          <Button 
            variant="contained" 
            onClick={handleUpdateXeroAuthMode}
            startIcon={<SaveIcon />}
          >
            Save Authentication Settings
          </Button>
        </Paper>

        {/* Project-Level Authentication Section */}
        {(xeroAuthMode === 'project' || xeroFallbackBehavior === 'use_project_auth') && (
          <Box mb={3}>
            <Typography variant="subtitle2" fontWeight="bold" mb={2}>
              Project-Level Authentication (Service Account)
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              {xeroAuthMode === 'project' 
                ? 'This project uses a shared Xero connection. All users will use the same Xero account.'
                : 'Project-level authentication is configured as a fallback for API/webhook executions.'}
            </Alert>
          </Box>
        )}

        <Box mb={3}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle2" fontWeight="bold">
              {(xeroAuthMode === 'project' || xeroFallbackBehavior === 'use_project_auth') 
                ? 'Project Authentication Status' 
                : 'Authentication Status'}
            </Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchXeroConfig}
              variant="text"
            >
              Refresh Status
            </Button>
          </Box>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Chip 
              icon={xeroConfig.isConfigured ? <CheckCircleIcon /> : <ErrorIcon />}
              label={xeroConfig.isConfigured ? 'Configured' : 'Not Configured'}
              color={xeroConfig.isConfigured ? 'success' : 'warning'}
              variant="outlined"
            />
            <Chip 
              icon={xeroConfig.isAuthenticated ? <CheckCircleIcon /> : <ErrorIcon />}
              label={xeroConfig.isAuthenticated ? 'Connected' : 'Not Connected'}
              color={xeroConfig.isAuthenticated ? 'success' : 'warning'}
              variant="outlined"
            />
          </Box>
          {xeroConfig.lastAuthenticatedAt && (
            <Typography variant="body2" color="textSecondary">
              Last authenticated: {new Date(xeroConfig.lastAuthenticatedAt).toLocaleString()}
            </Typography>
          )}
          {xeroConfig.isAuthenticated && xeroConfig.selectedTenantName && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Active Organization: <strong>{xeroConfig.selectedTenantName}</strong>
              {xeroConfig.tenantCount > 1 && ` (${xeroConfig.tenantCount} organizations available)`}
            </Typography>
          )}
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap">
          {!xeroConfig.isAuthenticated ? (
            <Button
              variant="contained"
              startIcon={xeroAuthenticating ? <CircularProgress size={20} /> : <LinkIcon />}
              onClick={handleAuthenticateXero}
              disabled={xeroAuthenticating || !xeroConfig.isConfigured}
              color="primary"
            >
              {xeroAuthenticating ? 'Authenticating...' : 'Connect to Xero'}
            </Button>
          ) : (
            <>
              {xeroTenants.length > 1 && (
                <Button
                  variant="outlined"
                  startIcon={<BusinessIcon />}
                  onClick={() => setXeroTenantDialogOpen(true)}
                  color="primary"
                >
                  Change Organization
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<LinkOffIcon />}
                onClick={handleRevokeXeroAccess}
                color="error"
              >
                Disconnect Xero
              </Button>
            </>
          )}
        </Box>

        {!xeroConfig.isConfigured && (
          <Alert severity="info" sx={{ mt: 3 }}>
            Xero OAuth2 credentials are not configured on the server. Please contact your administrator to set up Xero integration.
          </Alert>
        )}
      </Paper>

      {/* Xero Tenant Selection Dialog */}
      <Dialog 
        open={xeroTenantDialogOpen} 
        onClose={() => setXeroTenantDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon color="primary" />
            <Typography variant="h6">Select Xero Organization</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Select which Xero organization to use for this project. You can change this at any time.
          </Typography>

          {xeroError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {xeroError}
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap={2}>
            {xeroTenants.map((tenant) => (
              <Paper
                key={tenant.tenantId}
                elevation={tenant.tenantId === xeroConfig.selectedTenantId ? 3 : 1}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: tenant.tenantId === xeroConfig.selectedTenantId ? '2px solid' : '1px solid',
                  borderColor: tenant.tenantId === xeroConfig.selectedTenantId ? 'primary.main' : 'divider',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
                onClick={() => handleSelectXeroTenant(tenant.tenantId)}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {tenant.tenantName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {tenant.tenantType}
                    </Typography>
                  </Box>
                  {tenant.tenantId === xeroConfig.selectedTenantId && (
                    <CheckCircleIcon color="primary" />
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setXeroTenantDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Future Integrations Placeholder */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <CloudIcon color="disabled" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold" color="textSecondary">
              More Integrations Coming Soon
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Microsoft Excel, PostgreSQL, MongoDB, Salesforce, and more data sources will be available in future updates
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip label="PostgreSQL" variant="outlined" disabled />
          <Chip label="MongoDB" variant="outlined" disabled />
          <Chip label="Microsoft SQL Server" variant="outlined" disabled />
          <Chip label="Salesforce" variant="outlined" disabled />
          <Chip label="Airtable" variant="outlined" disabled />
        </Box>
      </Paper>
    </Box>
  );
};

// Matching Libraries Tab Component
const MatchingLibrariesTab = ({ companyId, projectId, isActive }) => {
  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedLibrary, setSelectedLibrary] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [libraryToDelete, setLibraryToDelete] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'products',
    rules: {
      caseSensitive: false,
      ignorePunctuation: true,
      ignoreWhitespace: true,
      fuzzyThreshold: 0.8,
      enablePartialMatching: true
    },
    entries: []
  });
  
  // Entry management states
  const [newEntry, setNewEntry] = useState({ canonicalName: '', aliases: [] });
  const [newAlias, setNewAlias] = useState({ value: '', source: '', confidence: 0.9 });
  const [editingEntryId, setEditingEntryId] = useState(null);
  
  // Pagination and search states
  const [entriesPage, setEntriesPage] = useState(0);
  const [entriesPerPage] = useState(20);
  const [entriesSearchQuery, setEntriesSearchQuery] = useState('');
  const [entriesPagination, setEntriesPagination] = useState(null);
  const [loadingEntries, setLoadingEntries] = useState(false);
  
  // Import states
  const [importFormat, setImportFormat] = useState('csv');
  const [importData, setImportData] = useState('');
  const [importName, setImportName] = useState('');
  const [importDescription, setImportDescription] = useState('');
  const [importCategory, setImportCategory] = useState('products');
  const [importMode, setImportMode] = useState('merge');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [importError, setImportError] = useState('');
  
  // Test states
  const [testInput, setTestInput] = useState('');
  const [testResults, setTestResults] = useState([]);
  const [testing, setTesting] = useState(false);
  
  // Export menu states
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [exportMenuLibrary, setExportMenuLibrary] = useState(null);
  
  // More actions menu states
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [moreMenuLibrary, setMoreMenuLibrary] = useState(null);
  
  // Learning states
  const [learningDialogOpen, setLearningDialogOpen] = useState(false);
  const [learningConfigDialogOpen, setLearningConfigDialogOpen] = useState(false);
  const [pendingLearning, setPendingLearning] = useState([]);
  const [learningStats, setLearningStats] = useState(null);
  const [selectedLearnings, setSelectedLearnings] = useState([]);
  const [learningConfig, setLearningConfig] = useState({
    enableLearning: false,
    learningFields: [],
    autoApprovalThreshold: 0.9,
    requireReview: true,
    maxAliasesPerEntry: 50
  });
  const [learningFieldsRawInput, setLearningFieldsRawInput] = useState(''); // Separate state for raw text input
  const [statsLoading, setStatsLoading] = useState(false);
  const [activeStatsTab, setActiveStatsTab] = useState(0);
  const [loadingLearning, setLoadingLearning] = useState(false);
  
  // Sync states
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncHistoryDialogOpen, setSyncHistoryDialogOpen] = useState(false);
  const [syncConfig, setSyncConfig] = useState({
    enabled: false,
    source: 'mysql',
    mysqlConfig: {
      projectId: projectId,
      tableName: '',
      canonicalNameField: '',
      aliasFields: [],
      syncStrategy: 'exclude_existing',
      strategyConfig: {
        timestampField: '',
        timestampType: 'created',
        primaryKeyField: '',
        customWhereClause: ''
      },
      additionalFilters: {
        whereClause: '',
        limitRows: null,
        orderBy: ''
      }
    },
    apiConfig: {
      baseUrl: '',
      method: 'GET',
      auth: {
        type: 'none',
        apiKeyHeader: '',
        apiKeyValue: '',
        bearerToken: '',
        basicUsername: '',
        basicPassword: ''
      },
      headers: [],
      requestBody: '',
      responseConfig: {
        dataPath: '',
        canonicalNameField: '',
        aliasFields: []
      },
      incrementalStrategy: {
        type: 'none',
        dateConfig: {
          paramName: '',
          dateFormat: 'YYYY-MM-DD',
          dateSource: 'last_sync',
          offsetDays: 1
        },
        paginationConfig: {
          pageSize: 100,
          offsetParam: 'offset',
          limitParam: 'limit',
          pageParam: 'page',
          cursorParam: 'cursor',
          nextCursorPath: '',
          endCondition: {
            type: 'empty_data',
            totalCountPath: '',
            hasMorePath: ''
          }
        }
      },
      rateLimit: {
        requestsPerMinute: 60,
        retryOnRateLimit: true,
        maxRetries: 3
      },
      timeout: 30000
    },
    schedule: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      times: [{ hour: 2, minute: 0 }],
      enabled: true
    },
    mode: 'insert_only',
    deduplication: {
      enabled: false,
      identifierField: '',
      onDuplicate: 'skip',
      freshnessField: ''
    }
  });
  const [mysqlSchema, setMysqlSchema] = useState({ tables: [], schema: {} });
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [testingSyncConfig, setTestingSyncConfig] = useState(false);
  const [syncTestResult, setSyncTestResult] = useState(null);
  const [triggeringSync, setTriggeringSync] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncStats, setSyncStats] = useState(null);
  const [loadingSyncHistory, setLoadingSyncHistory] = useState(false);

  useEffect(() => {
    if (isActive) {
      fetchLibraries();
      fetchLearningStats();
    }
  }, [isActive, companyId, projectId]);

  const fetchLearningStats = async () => {
    try {
      setStatsLoading(true);
      const response = await api.get(`/matching-libraries/stats/${projectId}`);
      setLearningStats(response.data);
    } catch (err) {
      console.error('Failed to fetch learning stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchLibraries = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/matching-libraries?includePublic=true`);
      setLibraries(response.data.libraries || []);
    } catch (err) {
      setError('Failed to fetch matching libraries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntriesForEdit = async (libraryId, page, search) => {
    try {
      setLoadingEntries(true);
      const response = await api.get(
        `/companies/${companyId}/projects/${projectId}/matching-libraries/${libraryId}/entries-paginated`,
        {
          params: {
            page: page + 1,
            limit: entriesPerPage,
            search: search || '',
            sortBy: 'canonicalName',
            sortOrder: 'asc'
          }
        }
      );
      
      setFormData(prev => ({ ...prev, entries: response.data.entries || [] }));
      setEntriesPagination(response.data.pagination);
    } catch (err) {
      console.error('Failed to fetch entries:', err);
      setError('Failed to load entries');
    } finally {
      setLoadingEntries(false);
    }
  };

  const handleCreateLibrary = async () => {
    try {
      await api.post(`/companies/${companyId}/projects/${projectId}/matching-libraries`, formData);
      setCreateDialogOpen(false);
      resetForm();
      fetchLibraries();
    } catch (err) {
      setError('Failed to create library');
      console.error(err);
    }
  };

  const handleUpdateLibrary = async () => {
    try {
      const updateData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        rules: formData.rules
      };
      
      await api.put(
        `/companies/${companyId}/projects/${projectId}/matching-libraries/${selectedLibrary.libraryId}`,
        updateData
      );
      setEditDialogOpen(false);
      resetForm();
      fetchLibraries();
    } catch (err) {
      if (err.response?.data?.code === 'USE_ENTRY_ENDPOINTS') {
        setError('Please use individual entry operations to modify library entries.');
      } else {
        setError('Failed to update library');
      }
      console.error(err);
    }
  };

  const handleDeleteLibrary = async () => {
    try {
      await api.delete(
        `/companies/${companyId}/projects/${projectId}/matching-libraries/${libraryToDelete.libraryId}`
      );
      setDeleteConfirmOpen(false);
      setLibraryToDelete(null);
      fetchLibraries();
    } catch (err) {
      setError('Failed to delete library');
      console.error(err);
    }
  };

  const handleAddEntry = async () => {
    if (!newEntry.canonicalName) return;
    
    // When creating a new library (selectedLibrary is null), add entries locally to formData
    if (!selectedLibrary) {
      if (editingEntryId) {
        // Update existing entry in formData
        setFormData(prev => ({
          ...prev,
          entries: prev.entries.map(entry => 
            (entry._id || entry.id || entry.tempId) === editingEntryId
              ? { ...entry, canonicalName: newEntry.canonicalName, aliases: newEntry.aliases }
              : entry
          )
        }));
        setEditingEntryId(null);
      } else {
        // Add new entry to formData with a temporary ID
        const tempEntry = {
          tempId: `temp-${Date.now()}`,
          canonicalName: newEntry.canonicalName,
          aliases: newEntry.aliases,
          metadata: {}
        };
        setFormData(prev => ({
          ...prev,
          entries: [...prev.entries, tempEntry]
        }));
      }
      setNewEntry({ canonicalName: '', aliases: [] });
      return;
    }
    
    // When editing an existing library, use the API
    try {
      if (editingEntryId) {
        await api.put(
          `/companies/${companyId}/projects/${projectId}/matching-libraries/${selectedLibrary.libraryId}/entries-update/${editingEntryId}`,
          { canonicalName: newEntry.canonicalName, aliases: newEntry.aliases, metadata: {} }
        );
        setEditingEntryId(null);
      } else {
        await api.post(
          `/companies/${companyId}/projects/${projectId}/matching-libraries/${selectedLibrary.libraryId}/entries-new`,
          { canonicalName: newEntry.canonicalName, aliases: newEntry.aliases, metadata: {} }
        );
      }
      
      await fetchEntriesForEdit(selectedLibrary.libraryId, entriesPage, entriesSearchQuery);
      setNewEntry({ canonicalName: '', aliases: [] });
    } catch (err) {
      console.error('Failed to save entry:', err);
      setError(`Failed to ${editingEntryId ? 'update' : 'add'} entry`);
    }
  };

  const handleEditEntry = (entry) => {
    setNewEntry({
      canonicalName: entry.canonicalName,
      aliases: [...entry.aliases]
    });
    setEditingEntryId(entry._id || entry.id || entry.tempId);
  };

  const handleCancelEditEntry = () => {
    setNewEntry({ canonicalName: '', aliases: [] });
    setEditingEntryId(null);
  };

  const handleAddAlias = () => {
    if (!newAlias.value) return;
    setNewEntry(prev => ({
      ...prev,
      aliases: [...prev.aliases, { ...newAlias }]
    }));
    setNewAlias({ value: '', source: '', confidence: 0.9 });
  };

  const handleRemoveEntry = async (entryId) => {
    // When creating a new library (selectedLibrary is null), remove from formData locally
    if (!selectedLibrary) {
      setFormData(prev => ({
        ...prev,
        entries: prev.entries.filter(entry => 
          (entry._id || entry.id || entry.tempId) !== entryId
        )
      }));
      return;
    }
    
    // When editing an existing library, use the API
    try {
      await api.delete(
        `/companies/${companyId}/projects/${projectId}/matching-libraries/${selectedLibrary.libraryId}/entries-delete/${entryId}`
      );
      await fetchEntriesForEdit(selectedLibrary.libraryId, entriesPage, entriesSearchQuery);
    } catch (err) {
      console.error('Failed to delete entry:', err);
      setError('Failed to delete entry');
    }
  };

  const handleRemoveAlias = (aliasIndex) => {
    setNewEntry(prev => ({
      ...prev,
      aliases: prev.aliases.filter((_, idx) => idx !== aliasIndex)
    }));
  };

  // Helper function to parse CSV line properly (handles quoted values, escaped quotes, commas in values)
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote (two quotes in a row)
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add last field
    result.push(current.trim());
    
    return result;
  };

  const handleImport = async (existingLibrary = null) => {
    try {
      setLoading(true);
      setImportError('');
      let entries = [];
      
      if (importFormat === 'csv') {
        const lines = importData.split('\n').filter(line => line.trim());
        if (lines.length > 20000) {
          setImportError('CSV file too large. Maximum 20,000 entries allowed.');
          setLoading(false);
          return;
        }
        
        const firstLine = lines[0];
        const hasHeader = firstLine.toLowerCase().includes('canonical') || 
                          firstLine.toLowerCase().includes('alias') ||
                          firstLine.toLowerCase().includes('source');
        const dataLines = hasHeader ? lines.slice(1) : lines;
        
        // Detect CSV format using proper CSV parsing
        let format = 'simple';
        if (hasHeader) {
          const headers = parseCSVLine(firstLine).map(h => h.toLowerCase());
          if (headers.some(h => h.includes('source')) || headers.some(h => h.includes('confidence'))) {
            format = 'extended';
          }
        }
        
        entries = dataLines.map(line => {
          const parts = parseCSVLine(line); // Use proper CSV parser
          const canonicalName = parts[0];
          if (!canonicalName) return null;
          
          let aliases = [];
          if (format === 'extended') {
            // Extended format: Alias 1, Source 1, Confidence 1, Alias 2, Source 2, Confidence 2, ...
            // FIXED: Continue processing even if some alias positions are empty (allows skipping positions)
            for (let i = 1; i < parts.length; i += 3) {
              const aliasValue = parts[i];
              const source = parts[i + 1];
              const confidenceStr = parts[i + 2];
              
              // Only add alias if value is not empty (allows gaps in alias positions)
              if (aliasValue) {
                const confidence = parseFloat(confidenceStr);
                aliases.push({
                  value: aliasValue,
                  source: source || 'import',
                  confidence: (!isNaN(confidence) && confidence >= 0 && confidence <= 1) ? confidence : 0.9
                });
              }
              // No break - continue processing remaining positions
            }
          } else {
            aliases = parts.slice(1).filter(a => a).map(alias => ({
              value: alias,
              source: 'import',
              confidence: 0.9
            }));
          }
          return { canonicalName, aliases };
        }).filter(entry => entry !== null);
      } else {
        entries = JSON.parse(importData);
      }
      
      // Check if we're importing into an existing library
      if (existingLibrary) {
        const response = await api.post(
          `/companies/${companyId}/projects/${projectId}/matching-libraries/${existingLibrary.libraryId}/import-entries`,
          {
            entries,
            format: importFormat,
            mode: importMode
          },
          { timeout: 300000 }
        );
        
        const stats = response.data.stats;
        setImportError('');
        alert(`Import successful!\n\n${importMode === 'merge' ? 'Merge' : 'Replace'} mode:\n- Created: ${stats.created}\n- Updated: ${stats.updated}\n- Deleted: ${stats.deleted}\n- Total: ${stats.total}`);
      } else {
        await api.post(
          `/companies/${companyId}/projects/${projectId}/matching-libraries/import`, 
          {
            name: importName,
            description: importDescription,
            category: importCategory,
            entries,
            format: importFormat
          },
          { timeout: 120000 }
        );
        setImportError('');
      }
      
      setImportDialogOpen(false);
      resetImportForm();
      fetchLibraries();
    } catch (err) {
      setImportError(`Failed to import library: ${err.response?.data?.message || err.message}`);
      console.error('Import error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTestLibrary = async () => {
    try {
      setTesting(true);
      const testValues = testInput.split('\n').filter(v => v.trim());
      
      const response = await api.post(
        `/companies/${companyId}/projects/${projectId}/matching-libraries/${selectedLibrary.libraryId}/test`,
        { testValues }
      );
      
      setTestResults(response.data.results || []);
    } catch (err) {
      setError('Failed to test library');
      console.error(err);
    } finally {
      setTesting(false);
    }
  };

  const handleExportLibrary = async (library, format = 'csv') => {
    try {
      const queryParam = format === 'template' ? '?format=template' : '?format=csv';
      const url = `/companies/${companyId}/projects/${projectId}/matching-libraries/${library.libraryId}/export${queryParam}`;
      
      console.log(`📥 Exporting library: ${library.name} (format: ${format})`);
      
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      
      const filename = format === 'template' 
        ? 'matching_library_template.csv'
        : `${library.name.replace(/\s+/g, '_')}_export.csv`;
      a.download = filename;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      
      console.log(`✅ Export successful: ${filename}`);
    } catch (err) {
      console.error('Export error:', err);
      setError(`Failed to export library: ${err.response?.data?.message || err.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'products',
      rules: {
        caseSensitive: false,
        ignorePunctuation: true,
        ignoreWhitespace: true,
        fuzzyThreshold: 0.8,
        enablePartialMatching: true
      },
      entries: []
    });
    setNewEntry({ canonicalName: '', aliases: [] });
    setEditingEntryId(null);
    setSelectedLibrary(null);
    setEntriesPage(0);
    setEntriesSearchQuery('');
  };

  const resetImportForm = () => {
    setImportData('');
    setImportName('');
    setImportDescription('');
    setImportCategory('products');
    setImportMode('merge');
    setUploadedFileName('');
    setImportError('');
  };

  const handleDownloadTemplate = (includeExistingEntries = false) => {
    let csvContent = '';
    
    if (includeExistingEntries && formData.entries.length > 0) {
      const maxAliases = Math.max(...formData.entries.map(e => e.aliases?.length || 0), 3);
      const headers = ['Canonical Name'];
      for (let i = 1; i <= maxAliases; i++) headers.push(`Alias ${i}`);
      for (let i = 1; i <= maxAliases; i++) headers.push(`Source ${i}`);
      for (let i = 1; i <= maxAliases; i++) headers.push(`Confidence ${i}`);
      csvContent = headers.join(',') + '\n';
      
      formData.entries.forEach(entry => {
        const row = [entry.canonicalName];
        for (let i = 0; i < maxAliases; i++) row.push(entry.aliases?.[i]?.value || '');
        for (let i = 0; i < maxAliases; i++) row.push(entry.aliases?.[i]?.source || '');
        for (let i = 0; i < maxAliases; i++) row.push(entry.aliases?.[i]?.confidence || '');
        csvContent += row.join(',') + '\n';
      });
    } else {
      csvContent = `Canonical Name,Alias 1,Alias 2,Alias 3,Source 1,Source 2,Source 3,Confidence 1,Confidence 2,Confidence 3
Fork Lift,Fork Lft,Forklift,FL-001,Supplier A,Warehouse,System,0.9,0.85,0.8
Pallet Jack,Pallet Jck,PJ-001,P-Jack,Supplier B,System,Vendor C,0.95,0.9,0.85
Hydraulic Pump,Hyd Pump,H-PUMP,HP-001,Supplier A,System,Vendor B,0.9,0.88,0.82`;
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const filename = formData.name ? `${formData.name.replace(/\s+/g, '_')}_library.csv` : 'matching_library_template.csv';
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportError('');
    const maxSizeBytes = 50 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setImportError(`File too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 50MB.`);
      event.target.value = '';
      return;
    }

    setUploadedFileName(file.name);
    const reader = new FileReader();

    reader.onload = (e) => {
      const content = e.target.result;
      const lines = content.split('\n').filter(line => line.trim()).length;
      
      if (lines > 20000) {
        setImportError(`File has too many lines (${lines}). Maximum 20,000 entries allowed.`);
        setUploadedFileName('');
        event.target.value = '';
        return;
      }
      
      setImportData(content);
      setImportError('');
      
      if (file.name.endsWith('.json')) {
        setImportFormat('json');
      } else if (file.name.endsWith('.csv')) {
        setImportFormat('csv');
      }

      if (!importName && file.name) {
        const nameWithoutExt = file.name.replace(/\.(csv|json)$/i, '').replace(/_/g, ' ');
        setImportName(nameWithoutExt);
      }
    };

    reader.onerror = () => {
      setImportError('Failed to read file. Please try again.');
      setUploadedFileName('');
    };

    reader.readAsText(file);
  };

  const openEditDialog = async (library) => {
    setSelectedLibrary(library);
    setFormData({
      name: library.name,
      description: library.description || '',
      category: library.category,
      rules: library.rules || {
        caseSensitive: false,
        ignorePunctuation: true,
        ignoreWhitespace: true,
        fuzzyThreshold: 0.8,
        enablePartialMatching: true
      },
      entries: []
    });
    setEntriesPage(0);
    setEntriesSearchQuery('');
    setEditDialogOpen(true);
    await fetchEntriesForEdit(library.libraryId, 0, '');
  };

  const openTestDialog = (library) => {
    setSelectedLibrary(library);
    setTestInput('');
    setTestResults([]);
    setTestDialogOpen(true);
  };

  const fetchPendingLearning = async (library) => {
    try {
      setLoadingLearning(true);
      const response = await api.get(`/matching-libraries/${library.libraryId}/pending-learning`);
      setPendingLearning(response.data.pendingLearning || []);
      setLearningConfig(response.data.libraryConfig || learningConfig);
    } catch (err) {
      console.error('Failed to fetch pending learning:', err);
    } finally {
      setLoadingLearning(false);
    }
  };

  const openLearningDialog = async (library) => {
    setSelectedLibrary(library);
    setSelectedLearnings([]);
    await fetchPendingLearning(library);
    setLearningDialogOpen(true);
  };

  const openLearningConfigDialog = async (library) => {
    setSelectedLibrary(library);
    const fields = library.learningConfig?.learningFields || [];
    setLearningConfig({
      enableLearning: library.learningConfig?.enableLearning || false,
      learningFields: fields,
      autoApprovalThreshold: library.learningConfig?.autoApprovalThreshold || 0.9,
      requireReview: library.learningConfig?.requireReview !== false,
      maxAliasesPerEntry: library.learningConfig?.maxAliasesPerEntry || 50
    });
    
    // Initialize raw input field with existing fields
    setLearningFieldsRawInput(fields.join(', '));
    
    setLearningConfigDialogOpen(true);
  };

  const handleSaveLearningConfig = async () => {
    try {
      // Parse the raw input one final time before saving
      const fields = learningFieldsRawInput.split(',').map(f => f.trim()).filter(f => f);
      const configToSave = {
        ...learningConfig,
        learningFields: fields
      };
      
      await api.put(`/matching-libraries/${selectedLibrary.libraryId}/learning-config`, configToSave);
      setLearningConfigDialogOpen(false);
      setLearningFieldsRawInput(''); // Reset raw input
      await fetchLibraries();
    } catch (err) {
      console.error('Failed to save learning config:', err);
      setError('Failed to save learning configuration');
    }
  };

  const handleApproveLearning = async (learningId) => {
    try {
      await api.post(`/matching-libraries/${selectedLibrary.libraryId}/pending-learning/${learningId}/approve`);
      await fetchPendingLearning(selectedLibrary);
      await fetchLibraries();
    } catch (err) {
      console.error('Failed to approve learning:', err);
    }
  };

  const handleRejectLearning = async (learningId) => {
    try {
      await api.post(`/matching-libraries/${selectedLibrary.libraryId}/pending-learning/${learningId}/reject`, {});
      await fetchPendingLearning(selectedLibrary);
    } catch (err) {
      console.error('Failed to reject learning:', err);
    }
  };
  
  // Sync handler functions
  const openSyncDialog = async (library) => {
    setSelectedLibrary(library);
    
    // Load existing sync config or use defaults
    if (library.syncConfig) {
      setSyncConfig({
        ...syncConfig,
        ...library.syncConfig,
        mysqlConfig: {
          ...syncConfig.mysqlConfig,
          ...library.syncConfig.mysqlConfig,
          strategyConfig: {
            ...syncConfig.mysqlConfig.strategyConfig,
            ...library.syncConfig.mysqlConfig?.strategyConfig
          },
          additionalFilters: {
            ...syncConfig.mysqlConfig.additionalFilters,
            ...library.syncConfig.mysqlConfig?.additionalFilters
          }
        },
        apiConfig: {
          ...syncConfig.apiConfig,
          ...library.syncConfig.apiConfig,
          auth: {
            ...syncConfig.apiConfig.auth,
            ...library.syncConfig.apiConfig?.auth
          },
          responseConfig: {
            ...syncConfig.apiConfig.responseConfig,
            ...library.syncConfig.apiConfig?.responseConfig
          },
          incrementalStrategy: {
            ...syncConfig.apiConfig.incrementalStrategy,
            ...library.syncConfig.apiConfig?.incrementalStrategy,
            dateConfig: {
              ...syncConfig.apiConfig.incrementalStrategy.dateConfig,
              ...library.syncConfig.apiConfig?.incrementalStrategy?.dateConfig
            },
            paginationConfig: {
              ...syncConfig.apiConfig.incrementalStrategy.paginationConfig,
              ...library.syncConfig.apiConfig?.incrementalStrategy?.paginationConfig,
              endCondition: {
                ...syncConfig.apiConfig.incrementalStrategy.paginationConfig.endCondition,
                ...library.syncConfig.apiConfig?.incrementalStrategy?.paginationConfig?.endCondition
              }
            }
          },
          rateLimit: {
            ...syncConfig.apiConfig.rateLimit,
            ...library.syncConfig.apiConfig?.rateLimit
          }
        },
        schedule: {
          ...syncConfig.schedule,
          ...library.syncConfig.schedule
        },
        deduplication: {
          ...syncConfig.deduplication,
          ...library.syncConfig.deduplication
        }
      });
    } else {
      // Reset to defaults if no existing config
      setSyncConfig({
        enabled: false,
        source: 'mysql',
        mysqlConfig: {
          projectId: projectId,
          tableName: '',
          canonicalNameField: '',
          aliasFields: [],
          syncStrategy: 'exclude_existing',
          strategyConfig: {
            timestampField: '',
            timestampType: 'created',
            primaryKeyField: '',
            customWhereClause: ''
          },
          additionalFilters: {
            whereClause: '',
            limitRows: null,
            orderBy: ''
          }
        },
        schedule: {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          times: [{ hour: 2, minute: 0 }],
          enabled: true
        },
        mode: 'insert_only',
        deduplication: {
          enabled: false,
          identifierField: '',
          onDuplicate: 'skip',
          freshnessField: ''
        }
      });
    }
    
    // Fetch MySQL schema using the library ID directly
    await fetchMySQLSchema(library.libraryId);
    
    setSyncDialogOpen(true);
  };
  
  const fetchMySQLSchema = async (libraryId) => {
    try {
      setLoadingSchema(true);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/matching-libraries/${libraryId || 'temp'}/sync/mysql-schema`);
      if (response.data.success) {
        setMysqlSchema({
          tables: response.data.tables || [],
          schema: response.data.schema || {}
        });
      }
    } catch (err) {
      console.error('Failed to fetch MySQL schema:', err);
      setError('Failed to fetch MySQL schema. Ensure MySQL is configured for this project.');
      setMysqlSchema({ tables: [], schema: {} });
    } finally {
      setLoadingSchema(false);
    }
  };
  
  const handleSaveSyncConfig = async () => {
    try {
      await api.put(`/companies/${companyId}/projects/${projectId}/matching-libraries/${selectedLibrary.libraryId}/sync-config`, {
        syncConfig
      });
      setSyncDialogOpen(false);
      await fetchLibraries();
      setError('');
    } catch (err) {
      console.error('Failed to save sync config:', err);
      setError('Failed to save sync configuration');
    }
  };
  
  const handleTestSyncConfig = async () => {
    if (!selectedLibrary) {
      console.error('No library selected');
      return;
    }
    
    try {
      setTestingSyncConfig(true);
      setSyncTestResult(null);
      
      // First save the current config to the library so the test can use it
      await api.put(`/companies/${companyId}/projects/${projectId}/matching-libraries/${selectedLibrary.libraryId}/sync-config`, {
        syncConfig
      });
      
      const response = await api.post(`/companies/${companyId}/projects/${projectId}/matching-libraries/${selectedLibrary.libraryId}/sync/test`);
      setSyncTestResult(response.data);
    } catch (err) {
      console.error('Failed to test sync config:', err);
      setSyncTestResult({
        success: false,
        error: err.response?.data?.error || err.message
      });
    } finally {
      setTestingSyncConfig(false);
    }
  };
  
  const handleTriggerSync = async (library) => {
    try {
      setTriggeringSync(true);
      const response = await api.post(`/companies/${companyId}/projects/${projectId}/matching-libraries/${library.libraryId}/sync/trigger`);
      if (response.data.success) {
        setError('');
        alert(`Sync completed successfully!\nProcessed: ${response.data.itemsProcessed}\nAdded: ${response.data.itemsAdded}\nUpdated: ${response.data.itemsUpdated || 0}\nSkipped: ${response.data.itemsSkipped}`);
        await fetchLibraries();
      }
    } catch (err) {
      console.error('Failed to trigger sync:', err);
      setError(err.response?.data?.message || 'Failed to trigger sync');
    } finally {
      setTriggeringSync(false);
    }
  };
  
  const openSyncHistoryDialog = async (library) => {
    setSelectedLibrary(library);
    await fetchSyncHistory(library.libraryId);
    setSyncHistoryDialogOpen(true);
  };
  
  const fetchSyncHistory = async (libraryId) => {
    try {
      setLoadingSyncHistory(true);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/matching-libraries/${libraryId}/sync/history`);
      if (response.data.success) {
        setSyncHistory(response.data.history || []);
        setSyncStats(response.data.stats || null);
      }
    } catch (err) {
      console.error('Failed to fetch sync history:', err);
    } finally {
      setLoadingSyncHistory(false);
    }
  };
  
  const handleMoreMenuOpen = (event, library) => {
    setMoreMenuAnchor(event.currentTarget);
    setMoreMenuLibrary(library);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchor(null);
    setMoreMenuLibrary(null);
  };

  if (loading && libraries.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            <LibraryBooksIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Matching Libraries
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Create and manage matching libraries for intelligent array combination
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Import Library
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Library
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Learning Stats Dashboard */}
      {learningStats && learningStats.stats?.totalPending > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 4, bgcolor: '#ffffff' }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Learning Analytics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                <CardContent>
                  <Typography variant="h4" fontWeight="bold">
                    {learningStats.stats?.totalPending || 0}
                  </Typography>
                  <Typography variant="body2">Pending Learnings</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                <CardContent>
                  <Typography variant="h4" fontWeight="bold">
                    {learningStats.stats?.totalApproved || 0}
                  </Typography>
                  <Typography variant="body2">Approved Learnings</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
                <CardContent>
                  <Typography variant="h4" fontWeight="bold">
                    {learningStats.stats?.totalAliases || 0}
                  </Typography>
                  <Typography variant="body2">Total Aliases</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <CardContent>
                  <Typography variant="h4" fontWeight="bold">
                    {learningStats.stats?.approvalRate || 0}%
                  </Typography>
                  <Typography variant="body2">Approval Rate</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Libraries Grid */}
      {libraries.length === 0 ? (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center' }}>
          <LibraryBooksIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Matching Libraries
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Create your first matching library to improve array combination accuracy
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create First Library
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {libraries.map((library) => (
            <Grid item xs={12} md={6} lg={4} key={library.libraryId}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                      {library.name}
                    </Typography>
                    <Chip label={library.category} size="small" color="primary" variant="outlined" />
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" mb={2}>
                    {library.description || 'No description'}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box display="flex" gap={2} mb={2}>
                    <Box>
                      <Typography variant="h4" color="primary" fontWeight="bold">
                        {library.entryCount || library.entries?.length || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">Entries</Typography>
                    </Box>
                    {library.learningConfig?.enableLearning && (
                      <Box>
                        <Badge badgeContent={library.pendingLearning?.filter(l => l.status === 'pending').length || 0} color="warning">
                          <Typography variant="h4" color="warning.main" fontWeight="bold">
                            <SchoolIcon />
                          </Typography>
                        </Badge>
                        <Typography variant="caption" color="textSecondary">Learning</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
                
                <CardActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {/* Primary Actions - Always visible */}
                  <Button size="small" startIcon={<EditIcon />} onClick={() => openEditDialog(library)}>
                    Edit
                  </Button>
                  <Button size="small" startIcon={<PlayArrowIcon />} onClick={() => openTestDialog(library)}>
                    Test
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<SyncIcon />} 
                    onClick={() => openSyncDialog(library)}
                    color={library.syncConfig?.enabled ? "primary" : "default"}
                  >
                    Sync
                  </Button>
                  
                  {/* Conditional Quick Actions */}
                  {library.syncConfig?.enabled && (
                    <Button 
                      size="small" 
                      startIcon={<SyncIcon />} 
                      onClick={() => handleTriggerSync(library)}
                      disabled={triggeringSync}
                      variant="outlined"
                      color="success"
                    >
                      Run Now
                    </Button>
                  )}
                  
                  {library.learningConfig?.enableLearning && (
                    <Badge badgeContent={library.pendingLearning?.filter(l => l.status === 'pending').length || 0} color="warning">
                      <Button size="small" startIcon={<SchoolIcon />} onClick={() => openLearningDialog(library)} color="warning">
                        Review
                      </Button>
                    </Badge>
                  )}
                  
                  {/* More Actions Menu */}
                  <MuiTooltip title="More Actions">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMoreMenuOpen(e, library)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </MuiTooltip>
                  
                  {/* Delete in corner */}
                  <Box sx={{ marginLeft: 'auto' }}>
                    <MuiTooltip title="Delete Library">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setLibraryToDelete(library);
                          setDeleteConfirmOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </MuiTooltip>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => {
          setExportMenuAnchor(null);
          setExportMenuLibrary(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (exportMenuLibrary) {
              handleExportLibrary(exportMenuLibrary, 'csv');
            }
            setExportMenuAnchor(null);
            setExportMenuLibrary(null);
          }}
        >
          <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
          Export All Entries (CSV)
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (exportMenuLibrary) {
              handleExportLibrary(exportMenuLibrary, 'template');
            }
            setExportMenuAnchor(null);
            setExportMenuLibrary(null);
          }}
        >
          <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
          Download Blank Template
        </MenuItem>
      </Menu>

      {/* More Actions Menu */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={handleMoreMenuClose}
      >
        <MenuItem onClick={() => {
          if (moreMenuLibrary) {
            openLearningConfigDialog(moreMenuLibrary);
          }
          handleMoreMenuClose();
        }}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Learning Settings</ListItemText>
        </MenuItem>
        
        {moreMenuLibrary?.syncConfig?.lastSync && (
          <MenuItem onClick={() => {
            if (moreMenuLibrary) {
              openSyncHistoryDialog(moreMenuLibrary);
            }
            handleMoreMenuClose();
          }}>
            <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Sync History</ListItemText>
          </MenuItem>
        )}
        
        <MenuItem onClick={() => {
          if (moreMenuLibrary) {
            setSelectedLibrary(moreMenuLibrary);
            setImportDialogOpen(true);
          }
          handleMoreMenuClose();
        }}>
          <ListItemIcon><UploadFileIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Import Entries</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={(e) => {
          if (moreMenuLibrary) {
            setExportMenuAnchor(e.currentTarget);
            setExportMenuLibrary(moreMenuLibrary);
          }
          handleMoreMenuClose();
        }}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Export Entries</ListItemText>
        </MenuItem>
      </Menu>

      {/* All dialogs would go here - Import, Create/Edit, Test, Delete, Learning Config, Learning Review */}
      {/* For brevity, I'm showing just the structure. The full dialogs would be similar to MatchingLibrariesPage.js */}
      
      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen || editDialogOpen} onClose={() => { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }} maxWidth="md" fullWidth>
        <DialogTitle>{editDialogOpen ? 'Edit Matching Library' : 'Create Matching Library'}</DialogTitle>
        <DialogContent>
          {editDialogOpen && (
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> Entries are now managed individually. Changes to entries (add/edit/delete) are saved immediately to the database. 
                Click "Update" to save library settings (name, description, rules) only.
              </Typography>
            </Alert>
          )}
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            <TextField
              label="Library Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                label="Category"
              >
                <MenuItem value="products">Products</MenuItem>
                <MenuItem value="customers">Customers</MenuItem>
                <MenuItem value="locations">Locations</MenuItem>
                <MenuItem value="companies">Companies</MenuItem>
                <MenuItem value="materials">Materials</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>

            {/* Matching Rules */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">Matching Rules</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box display="flex" flexDirection="column" gap={2}>
                  <FormControlLabel
                    control={<Switch checked={formData.rules.caseSensitive} onChange={(e) => setFormData({ ...formData, rules: { ...formData.rules, caseSensitive: e.target.checked } })} />}
                    label="Case Sensitive"
                  />
                  <FormControlLabel
                    control={<Switch checked={formData.rules.ignorePunctuation} onChange={(e) => setFormData({ ...formData, rules: { ...formData.rules, ignorePunctuation: e.target.checked } })} />}
                    label="Ignore Punctuation"
                  />
                  <FormControlLabel
                    control={<Switch checked={formData.rules.ignoreWhitespace} onChange={(e) => setFormData({ ...formData, rules: { ...formData.rules, ignoreWhitespace: e.target.checked } })} />}
                    label="Ignore Whitespace"
                  />
                  <TextField
                    label="Fuzzy Threshold"
                    type="number"
                    value={formData.rules.fuzzyThreshold}
                    onChange={(e) => setFormData({ ...formData, rules: { ...formData.rules, fuzzyThreshold: parseFloat(e.target.value) } })}
                    inputProps={{ min: 0, max: 1, step: 0.1 }}
                    fullWidth
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Entries Section */}
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Entries ({editDialogOpen ? (entriesPagination?.total || formData.entries.length) : formData.entries.length})
                </Typography>
              </Box>

              {editDialogOpen && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Tip:</strong> Use the Export button on the library card to download all entries as CSV, or use the Import button to bulk update entries.
                  </Typography>
                </Alert>
              )}

              {/* Add New Entry Form */}
              <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">{editingEntryId ? 'Edit Entry' : 'Add New Entry'}</Typography>
                  {editingEntryId && (
                    <Button size="small" onClick={handleCancelEditEntry} startIcon={<CancelIcon />}>Cancel Edit</Button>
                  )}
                </Box>
                <TextField
                  label="Canonical Name"
                  value={newEntry.canonicalName}
                  onChange={(e) => setNewEntry({ ...newEntry, canonicalName: e.target.value })}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />
                <Box display="flex" gap={1} mb={2}>
                  <TextField
                    label="Alias Value"
                    value={newAlias.value}
                    onChange={(e) => setNewAlias({ ...newAlias, value: e.target.value })}
                    size="small"
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    label="Source"
                    value={newAlias.source}
                    onChange={(e) => setNewAlias({ ...newAlias, source: e.target.value })}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Confidence"
                    type="number"
                    value={newAlias.confidence}
                    onChange={(e) => setNewAlias({ ...newAlias, confidence: parseFloat(e.target.value) })}
                    inputProps={{ min: 0, max: 1, step: 0.1 }}
                    size="small"
                    sx={{ width: 100 }}
                  />
                  <Button variant="outlined" onClick={handleAddAlias} disabled={!newAlias.value}>Add Alias</Button>
                </Box>

                {newEntry.aliases.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="caption" color="textSecondary" gutterBottom display="block" mb={1}>
                      Aliases ({newEntry.aliases.length}):
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Alias Value</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell width={100}>Confidence</TableCell>
                            <TableCell width={50}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {newEntry.aliases.map((alias, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <TextField
                                  value={alias.value}
                                  onChange={(e) => {
                                    const updatedAliases = [...newEntry.aliases];
                                    updatedAliases[idx] = { ...alias, value: e.target.value };
                                    setNewEntry({ ...newEntry, aliases: updatedAliases });
                                  }}
                                  size="small"
                                  fullWidth
                                  variant="standard"
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  value={alias.source || ''}
                                  onChange={(e) => {
                                    const updatedAliases = [...newEntry.aliases];
                                    updatedAliases[idx] = { ...alias, source: e.target.value };
                                    setNewEntry({ ...newEntry, aliases: updatedAliases });
                                  }}
                                  size="small"
                                  fullWidth
                                  variant="standard"
                                  placeholder="Optional"
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  value={alias.confidence}
                                  onChange={(e) => {
                                    const updatedAliases = [...newEntry.aliases];
                                    updatedAliases[idx] = { ...alias, confidence: parseFloat(e.target.value) };
                                    setNewEntry({ ...newEntry, aliases: updatedAliases });
                                  }}
                                  size="small"
                                  fullWidth
                                  variant="standard"
                                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton size="small" color="error" onClick={() => handleRemoveAlias(idx)}><DeleteIcon fontSize="small" /></IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                <Button
                  variant="contained"
                  startIcon={editingEntryId ? <SaveIcon /> : <AddIcon />}
                  onClick={handleAddEntry}
                  disabled={!newEntry.canonicalName}
                  fullWidth
                  color="primary"
                >
                  {editingEntryId ? 'Update Entry' : 'Add Entry'}
                </Button>
              </Paper>

              {/* Existing Entries List with Pagination */}
              {(loadingEntries || formData.entries.length > 0 || entriesPagination) && (() => {
                const paginatedEntries = formData.entries;
                const totalPages = entriesPagination?.totalPages || 1;
                const startIdx = (entriesPagination?.page - 1) * entriesPerPage || 0;
                const endIdx = Math.min(startIdx + entriesPerPage, entriesPagination?.total || formData.entries.length);
                
                return (
                  <Box>
                    <TextField
                      placeholder="Search entries by canonical name or alias..."
                      value={entriesSearchQuery}
                      onChange={(e) => {
                        const newQuery = e.target.value;
                        setEntriesSearchQuery(newQuery);
                        setEntriesPage(0);
                        if (selectedLibrary) {
                          if (window.entriesSearchTimeout) clearTimeout(window.entriesSearchTimeout);
                          window.entriesSearchTimeout = setTimeout(() => {
                            fetchEntriesForEdit(selectedLibrary.libraryId, 0, newQuery);
                          }, 500);
                        }
                      }}
                      fullWidth
                      size="small"
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        endAdornment: entriesSearchQuery && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => { setEntriesSearchQuery(''); setEntriesPage(0); if (selectedLibrary) fetchEntriesForEdit(selectedLibrary.libraryId, 0, ''); }}>
                              <CancelIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    {loadingEntries ? (
                      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="textSecondary">Loading entries...</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        Showing {startIdx + 1}-{endIdx} of {entriesPagination?.total || 0} entries
                        {entriesSearchQuery && ` (search: "${entriesSearchQuery}")`}
                      </Typography>
                    )}

                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Canonical Name</TableCell>
                            <TableCell>Aliases</TableCell>
                            <TableCell width={100}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loadingEntries ? (
                            <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell></TableRow>
                          ) : paginatedEntries.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={3} align="center">
                                <Typography variant="body2" color="textSecondary" py={2}>
                                  {entriesSearchQuery ? 'No entries match your search' : 'No entries yet. Add your first entry above.'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedEntries.map((entry) => {
                              const entryKey = entry._id || entry.id || entry.tempId;
                              return (
                                <TableRow key={entryKey} sx={{ bgcolor: editingEntryId === entryKey ? 'warning.light' : 'inherit' }}>
                                  <TableCell>{entry.canonicalName}</TableCell>
                                  <TableCell>
                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                      {entry.aliases?.slice(0, 3).map((alias, idx) => (
                                        <Chip key={idx} label={alias.value} size="small" variant="outlined" />
                                      ))}
                                      {entry.aliases?.length > 3 && (
                                        <Chip label={`+${entry.aliases.length - 3} more`} size="small" variant="outlined" color="primary" />
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box display="flex" gap={0.5}>
                                      <IconButton size="small" color="primary" onClick={() => handleEditEntry(entry)}><EditIcon fontSize="small" /></IconButton>
                                      <IconButton size="small" color="error" onClick={() => handleRemoveEntry(entryKey)}><DeleteIcon fontSize="small" /></IconButton>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                        <Button
                          size="small"
                          onClick={() => {
                            const newPage = Math.max(0, entriesPage - 1);
                            setEntriesPage(newPage);
                            if (selectedLibrary) fetchEntriesForEdit(selectedLibrary.libraryId, newPage, entriesSearchQuery);
                          }}
                          disabled={entriesPage === 0 || loadingEntries}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2">Page {entriesPage + 1} of {totalPages}</Typography>
                        <Button
                          size="small"
                          onClick={() => {
                            const newPage = Math.min(totalPages - 1, entriesPage + 1);
                            setEntriesPage(newPage);
                            if (selectedLibrary) fetchEntriesForEdit(selectedLibrary.libraryId, newPage, entriesSearchQuery);
                          }}
                          disabled={entriesPage >= totalPages - 1 || loadingEntries}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }}>Cancel</Button>
          <Button onClick={editDialogOpen ? handleUpdateLibrary : handleCreateLibrary} variant="contained" startIcon={<SaveIcon />} disabled={!formData.name}>
            {editDialogOpen ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => { if (!loading) { setImportDialogOpen(false); resetImportForm(); setSelectedLibrary(null); } }} maxWidth="md" fullWidth>
        <DialogTitle>{selectedLibrary ? `Import into ${selectedLibrary.name}` : 'Import Matching Library'}</DialogTitle>
        <DialogContent>
          {loading && (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
              <CircularProgress size={60} />
              <Typography variant="h6">Importing library...</Typography>
              <Typography variant="body2" color="textSecondary">This may take a few moments for large files</Typography>
            </Box>
          )}
          <Box display="flex" flexDirection="column" gap={3} mt={2} sx={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
            {importError && <Alert severity="error" onClose={() => setImportError('')}>{importError}</Alert>}
            
            <Alert severity="info">
              <Typography variant="body2" fontWeight="bold" gutterBottom>📁 File Size Recommendations</Typography>
              <Typography variant="body2">
                • <strong>Small files (&lt;1,000 entries):</strong> Fastest, processes in single request<br />
                • <strong>Medium files (1,000-10,000 entries):</strong> Auto-splits into batches<br />
                • <strong>Large files (10,000-20,000 entries):</strong> Auto-splits into multiple batches (may take 2-3 minutes)<br />
                • <strong>Maximum:</strong> 50MB per file, 20,000 entries
              </Typography>
            </Alert>
            
            {!selectedLibrary && (
              <>
                <TextField label="Library Name" value={importName} onChange={(e) => setImportName(e.target.value)} fullWidth required />
                <TextField label="Description" value={importDescription} onChange={(e) => setImportDescription(e.target.value)} multiline rows={2} fullWidth />
                
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select value={importCategory} onChange={(e) => setImportCategory(e.target.value)} label="Category">
                    <MenuItem value="products">Products</MenuItem>
                    <MenuItem value="customers">Customers</MenuItem>
                    <MenuItem value="locations">Locations</MenuItem>
                    <MenuItem value="companies">Companies</MenuItem>
                    <MenuItem value="materials">Materials</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
            
            <FormControl fullWidth>
              <InputLabel>Import Mode</InputLabel>
              <Select value={importMode} onChange={(e) => setImportMode(e.target.value)} label="Import Mode">
                <MenuItem value="merge">Merge (update existing + add new)</MenuItem>
                <MenuItem value="replace">Replace (delete all + import)</MenuItem>
              </Select>
            </FormControl>
            
            <Alert severity="info">
              <Typography variant="body2" fontWeight="bold" gutterBottom>Import Mode Explanation</Typography>
              <Typography variant="body2">
                <strong>Merge:</strong> Updates entries with matching canonical names, adds new ones, keeps existing entries not in upload.<br />
                <strong>Replace:</strong> Deletes all existing entries and replaces with uploaded data.
              </Typography>
            </Alert>
            
            <Tabs value={importFormat} onChange={(e, v) => setImportFormat(v)}>
              <Tab label="CSV" value="csv" />
              <Tab label="JSON" value="json" />
            </Tabs>
            
            {importFormat === 'csv' ? (
              <Box>
                <Box display="flex" gap={2} mb={2}>
                  <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate} fullWidth>
                    Download CSV Template
                  </Button>
                  <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} fullWidth>
                    Upload CSV File
                    <input type="file" hidden accept=".csv" onChange={handleFileUpload} />
                  </Button>
                </Box>
                
                {uploadedFileName && (
                  <>
                    <Alert severity="success" sx={{ mb: 2 }}>File uploaded: {uploadedFileName}</Alert>
                    {importData && importData.split('\n').filter(line => line.trim()).length > 10000 && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>⚠️ Large File Detected</Typography>
                        <Typography variant="body2">
                          Your file has {importData.split('\n').filter(line => line.trim()).length.toLocaleString()} entries. 
                          This will be processed in {Math.ceil(importData.split('\n').filter(line => line.trim()).length / 1000)} batches and may take 2-3 minutes.
                        </Typography>
                      </Alert>
                    )}
                  </>
                )}
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>CSV Format Instructions:</Typography>
                  <Typography variant="body2" component="div">
                    • First row: Headers (Canonical Name, Alias 1, Alias 2, etc.)<br />
                    • Each row: One canonical name followed by its aliases<br />
                    • Optional: Add Source columns (Source 1, Source 2, etc.)<br />
                    • Optional: Add Confidence columns (Confidence 1, Confidence 2, etc.)<br />
                    • Download template above for an example
                  </Typography>
                </Alert>
                <TextField
                  label="CSV Data"
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  multiline
                  rows={10}
                  fullWidth
                  placeholder="Fork Lift,Fork Lft,Forklift,FL-001&#10;Pallet Jack,Pallet Jck,PJ-001"
                  helperText="Paste CSV data here or upload a file above"
                />
              </Box>
            ) : (
              <Box>
                <Box display="flex" gap={2} mb={2}>
                  <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} fullWidth>
                    Upload JSON File
                    <input type="file" hidden accept=".json" onChange={handleFileUpload} />
                  </Button>
                </Box>
                
                {uploadedFileName && <Alert severity="success" sx={{ mb: 2 }}>File uploaded: {uploadedFileName}</Alert>}
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>JSON Format Instructions:</Typography>
                  <Typography variant="body2" component="div">
                    • Array of objects with canonicalName and aliases<br />
                    • Each alias can have: value, source, confidence<br />
                    • Example shown in placeholder below
                  </Typography>
                </Alert>
                <TextField
                  label="JSON Data"
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  multiline
                  rows={10}
                  fullWidth
                  placeholder={`[
  {
    "canonicalName": "Fork Lift",
    "aliases": [
      {"value": "Fork Lft", "source": "company-b", "confidence": 0.9}
    ]
  }
]`}
                  helperText="Paste JSON data here or upload a file above"
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportDialogOpen(false); resetImportForm(); setSelectedLibrary(null); }} disabled={loading}>Cancel</Button>
          <Button onClick={() => handleImport(selectedLibrary)} variant="contained" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <UploadFileIcon />} disabled={(selectedLibrary ? !importData : (!importName || !importData)) || loading}>
            {loading ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Test Library: {selectedLibrary?.name}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            <TextField
              label="Test Values (one per line)"
              value={testInput}
              onChange={(e) => setTestInput(e.target.value)}
              multiline
              rows={6}
              fullWidth
              placeholder="Enter test values..."
            />
            <Button variant="contained" startIcon={testing ? <CircularProgress size={20} /> : <PlayArrowIcon />} onClick={handleTestLibrary} disabled={!testInput || testing}>
              {testing ? 'Testing...' : 'Test Matching'}
            </Button>
            {testResults.length > 0 && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Input</TableCell>
                      <TableCell>Match Found</TableCell>
                      <TableCell>Canonical Name</TableCell>
                      <TableCell>Confidence</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testResults.map((result, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{result.input}</TableCell>
                        <TableCell>{result.match ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}</TableCell>
                        <TableCell>{result.match?.canonicalName || '-'}</TableCell>
                        <TableCell>{result.match ? `${(result.match.confidence * 100).toFixed(0)}%` : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Library</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the library "{libraryToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteLibrary} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Learning Configuration Dialog */}
      <Dialog open={learningConfigDialogOpen} onClose={() => {
          setLearningConfigDialogOpen(false);
          setLearningFieldsRawInput(''); // Reset raw input when closing
        }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <SettingsIcon color="primary" />
            Learning Configuration - {selectedLibrary?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} pt={2}>
            <Alert severity="info">
              <Typography variant="body2">
                Enable learning to allow this library to automatically improve based on user corrections during data re-matching.
              </Typography>
            </Alert>

            <FormControlLabel
              control={<Switch checked={learningConfig.enableLearning} onChange={(e) => setLearningConfig(prev => ({ ...prev, enableLearning: e.target.checked }))} color="primary" />}
              label={
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">Enable Library Learning</Typography>
                  <Typography variant="body2" color="textSecondary">Allow this library to learn from user corrections</Typography>
                </Box>
              }
            />

            {learningConfig.enableLearning && (
              <>
                <TextField
                  label="Learning Fields (comma-separated)"
                  value={learningFieldsRawInput}
                  onChange={(e) => {
                    // Store raw input value so user can see commas and spaces as they type
                    setLearningFieldsRawInput(e.target.value);
                  }}
                  onBlur={() => {
                    // Parse fields when user leaves the input
                    const fields = learningFieldsRawInput.split(',').map(f => f.trim()).filter(f => f);
                    setLearningConfig(prev => ({ ...prev, learningFields: fields }));
                  }}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="productName, productCode, customerName"
                  helperText={
                    <span>
                      <strong>Field names from your table that should be learned.</strong><br />
                      • For Library Re-Match: Enter the "Target Field to Correct" name<br />
                      • Multiple fields: Separate with commas (e.g., productName, productCode)<br />
                      • Leave empty to auto-detect from flow configuration (recommended)
                    </span>
                  }
                  inputProps={{
                    autoComplete: 'off',
                    autoCorrect: 'off',
                    autoCapitalize: 'off',
                    spellCheck: 'false'
                  }}
                />

                <FormControlLabel
                  control={<Switch checked={learningConfig.requireReview} onChange={(e) => setLearningConfig(prev => ({ ...prev, requireReview: e.target.checked }))} color="primary" />}
                  label={
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">Require Manual Review</Typography>
                      <Typography variant="body2" color="textSecondary">Require admin approval before adding learned aliases</Typography>
                    </Box>
                  }
                />

                <TextField
                  label="Auto-Approval Threshold"
                  type="number"
                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                  value={learningConfig.autoApprovalThreshold}
                  onChange={(e) => setLearningConfig(prev => ({ ...prev, autoApprovalThreshold: parseFloat(e.target.value) }))}
                  fullWidth
                  helperText="Minimum confidence (0-1) required for automatic approval when review is disabled"
                />

                <TextField
                  label="Max Aliases Per Entry"
                  type="number"
                  inputProps={{ min: 1, max: 200 }}
                  value={learningConfig.maxAliasesPerEntry}
                  onChange={(e) => setLearningConfig(prev => ({ ...prev, maxAliasesPerEntry: parseInt(e.target.value) }))}
                  fullWidth
                  helperText="Maximum number of aliases allowed per canonical entry"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setLearningConfigDialogOpen(false);
            setLearningFieldsRawInput(''); // Reset raw input on cancel
          }}>Cancel</Button>
          <Button onClick={handleSaveLearningConfig} variant="contained" startIcon={<SaveIcon />}>Save Configuration</Button>
        </DialogActions>
      </Dialog>

      {/* Learning Review Dialog - Simplified */}
      <Dialog open={learningDialogOpen} onClose={() => setLearningDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <SchoolIcon color="warning" />
              Pending Learning - {selectedLibrary?.name}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingLearning ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : pendingLearning.length === 0 ? (
            <Box textAlign="center" py={4}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>No Pending Learning</Typography>
              <Typography variant="body2" color="textSecondary">All corrections have been reviewed.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Original Value</TableCell>
                    <TableCell>Canonical Name</TableCell>
                    <TableCell>Field</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingLearning.map((learning) => (
                    <TableRow key={learning._id}>
                      <TableCell><Typography variant="body2" fontFamily="monospace">{learning.originalValue}</Typography></TableCell>
                      <TableCell><Typography variant="body2" fontWeight="bold">{learning.canonicalName}</Typography></TableCell>
                      <TableCell><Chip label={learning.field} size="small" variant="outlined" /></TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <IconButton size="small" color="success" onClick={() => handleApproveLearning(learning._id)}><CheckCircleIcon /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleRejectLearning(learning._id)}><CancelIcon /></IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLearningDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Sync Configuration Dialog */}
      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Configure Automated Sync</Typography>
            {selectedLibrary && (
              <Chip 
                label={syncConfig.enabled ? "Enabled" : "Disabled"}
                color={syncConfig.enabled ? "success" : "default"}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            
            {/* Enable Sync Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={syncConfig.enabled}
                  onChange={(e) => setSyncConfig({...syncConfig, enabled: e.target.checked})}
                />
              }
              label="Enable Automated Sync"
            />
            
            {syncConfig.enabled && (
              <>
                {/* Data Source Selection */}
                <FormControl fullWidth>
                  <InputLabel>Data Source</InputLabel>
                  <Select
                    value={syncConfig.source || 'mysql'}
                    onChange={(e) => setSyncConfig({...syncConfig, source: e.target.value})}
                    label="Data Source"
                  >
                    <MenuItem value="mysql">
                      <Box>
                        <Typography variant="body2" fontWeight="bold">MySQL Database</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Sync from a MySQL table using project database connection
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="api">
                      <Box>
                        <Typography variant="body2" fontWeight="bold">External API</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Sync from an external REST API with pagination and date filtering
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* MySQL Configuration */}
                {syncConfig.source === 'mysql' && (
                  <>
                    {/* Sync Strategy Selection */}
                    <FormControl fullWidth>
                      <InputLabel>Sync Strategy</InputLabel>
                      <Select
                        value={syncConfig.mysqlConfig?.syncStrategy || 'exclude_existing'}
                        onChange={(e) => setSyncConfig({
                          ...syncConfig,
                          mysqlConfig: {...syncConfig.mysqlConfig, syncStrategy: e.target.value}
                        })}
                        label="Sync Strategy"
                      >
                        <MenuItem value="exclude_existing">
                          <Box>
                            <Typography variant="body2" fontWeight="bold">Exclude Existing</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Fetch items NOT IN existing canonicalNames (good for &lt;10K entries)
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="timestamp_incremental">
                          <Box>
                            <Typography variant="body2" fontWeight="bold">Timestamp Incremental (Recommended)</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Fetch items created/modified after last sync (requires timestamp field)
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="primary_key_tracking">
                          <Box>
                            <Typography variant="body2" fontWeight="bold">Primary Key Tracking</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Track synced primary keys, fetch new PKs only
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="fetch_all_dedupe">
                          <Box>
                            <Typography variant="body2" fontWeight="bold">Fetch All & Deduplicate</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Fetch all records, deduplicate on insertion (simple, less efficient)
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="custom_where">
                          <Box>
                            <Typography variant="body2" fontWeight="bold">Custom WHERE Clause</Typography>
                            <Typography variant="caption" color="textSecondary">
                              User-defined WHERE clause (maximum flexibility)
                            </Typography>
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                
                {/* Table Selection */}
                <FormControl fullWidth>
                  <InputLabel>MySQL Table</InputLabel>
                  <Select
                    value={syncConfig.mysqlConfig?.tableName || ''}
                    onChange={(e) => setSyncConfig({
                      ...syncConfig,
                      mysqlConfig: {...syncConfig.mysqlConfig, tableName: e.target.value}
                    })}
                    label="MySQL Table"
                    disabled={loadingSchema}
                  >
                    {loadingSchema ? (
                      <MenuItem disabled>Loading tables...</MenuItem>
                    ) : mysqlSchema.tables.length === 0 ? (
                      <MenuItem disabled>No tables found. Configure MySQL in External Data Integration.</MenuItem>
                    ) : (
                      mysqlSchema.tables.map(table => (
                        <MenuItem key={table} value={table}>{table}</MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                
                {/* Canonical Name Field */}
                {syncConfig.mysqlConfig?.tableName && (
                  <FormControl fullWidth>
                    <InputLabel>Canonical Name Field</InputLabel>
                    <Select
                      value={syncConfig.mysqlConfig?.canonicalNameField || ''}
                      onChange={(e) => setSyncConfig({
                        ...syncConfig,
                        mysqlConfig: {...syncConfig.mysqlConfig, canonicalNameField: e.target.value}
                      })}
                      label="Canonical Name Field"
                    >
                      {mysqlSchema.schema[syncConfig.mysqlConfig.tableName]?.map(col => (
                        <MenuItem key={col.name} value={col.name}>
                          {col.name} ({col.type})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                
                {/* Strategy-Specific Config */}
                {syncConfig.mysqlConfig?.syncStrategy === 'timestamp_incremental' && syncConfig.mysqlConfig?.tableName && (
                  <Box display="flex" flexDirection="column" gap={2}>
                    <FormControl fullWidth>
                      <InputLabel>Timestamp Field</InputLabel>
                      <Select
                        value={syncConfig.mysqlConfig?.strategyConfig?.timestampField || ''}
                        onChange={(e) => setSyncConfig({
                          ...syncConfig,
                          mysqlConfig: {
                            ...syncConfig.mysqlConfig,
                            strategyConfig: {...syncConfig.mysqlConfig.strategyConfig, timestampField: e.target.value}
                          }
                        })}
                        label="Timestamp Field"
                      >
                        {mysqlSchema.schema[syncConfig.mysqlConfig.tableName]
                          ?.filter(col => col.type.toLowerCase().includes('date') || col.type.toLowerCase().includes('time'))
                          .map(col => (
                            <MenuItem key={col.name} value={col.name}>
                              {col.name} ({col.type})
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    
                    {/* Sync From Date - allows resetting or setting custom start date */}
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                        Sync From Date (only fetch records after this date)
                      </Typography>
                      {syncConfig.mysqlConfig?.strategyConfig?.lastSyncTimestamp ? (
                        <Box display="flex" gap={2} alignItems="center">
                          <TextField
                            type="datetime-local"
                            value={syncConfig.mysqlConfig?.strategyConfig?.lastSyncTimestamp 
                              ? new Date(syncConfig.mysqlConfig.strategyConfig.lastSyncTimestamp).toISOString().slice(0, 16)
                              : ''
                            }
                            onChange={(e) => setSyncConfig({
                              ...syncConfig,
                              mysqlConfig: {
                                ...syncConfig.mysqlConfig,
                                strategyConfig: {
                                  ...syncConfig.mysqlConfig.strategyConfig, 
                                  lastSyncTimestamp: e.target.value ? new Date(e.target.value).toISOString() : null
                                }
                              }
                            })}
                            size="small"
                            sx={{ flex: 1 }}
                            InputLabelProps={{ shrink: true }}
                          />
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            onClick={() => setSyncConfig({
                              ...syncConfig,
                              mysqlConfig: {
                                ...syncConfig.mysqlConfig,
                                strategyConfig: {...syncConfig.mysqlConfig.strategyConfig, lastSyncTimestamp: null}
                              }
                            })}
                          >
                            Reset (Fetch All)
                          </Button>
                        </Box>
                      ) : (
                        <Alert severity="info" sx={{ py: 0.5 }}>
                          <Typography variant="caption">
                            No sync date set - next sync will fetch <strong>all records</strong> from the table
                          </Typography>
                        </Alert>
                      )}
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                        Tip: Reset this to fetch all records again, or set a specific date to only sync records after that date
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                {syncConfig.mysqlConfig?.syncStrategy === 'primary_key_tracking' && syncConfig.mysqlConfig?.tableName && (
                  <FormControl fullWidth>
                    <InputLabel>Primary Key Field</InputLabel>
                    <Select
                      value={syncConfig.mysqlConfig?.strategyConfig?.primaryKeyField || ''}
                      onChange={(e) => setSyncConfig({
                        ...syncConfig,
                        mysqlConfig: {
                          ...syncConfig.mysqlConfig,
                          strategyConfig: {...syncConfig.mysqlConfig.strategyConfig, primaryKeyField: e.target.value}
                        }
                      })}
                      label="Primary Key Field"
                    >
                      {mysqlSchema.schema[syncConfig.mysqlConfig.tableName]?.map(col => (
                        <MenuItem key={col.name} value={col.name}>
                          {col.name} ({col.type}) {col.key === 'PRI' && '(PRIMARY KEY)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                
                {syncConfig.mysqlConfig?.syncStrategy === 'custom_where' && (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Custom WHERE Clause"
                    value={syncConfig.mysqlConfig?.strategyConfig?.customWhereClause || ''}
                    onChange={(e) => setSyncConfig({
                      ...syncConfig,
                      mysqlConfig: {
                        ...syncConfig.mysqlConfig,
                        strategyConfig: {...syncConfig.mysqlConfig.strategyConfig, customWhereClause: e.target.value}
                      }
                    })}
                    placeholder="e.g., status = 'active' AND created_at > '2024-01-01'"
                    helperText="SQL WHERE clause without the 'WHERE' keyword"
                  />
                )}
                
                {/* Alias Fields Configuration */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Alias Fields (Optional)</Typography>
                  {syncConfig.mysqlConfig?.aliasFields?.map((aliasField, index) => (
                    <Box key={index} display="flex" gap={2} mb={2}>
                      <FormControl sx={{ flex: 1 }}>
                        <InputLabel>Field</InputLabel>
                        <Select
                          value={aliasField.fieldName}
                          onChange={(e) => {
                            const newAliasFields = [...(syncConfig.mysqlConfig.aliasFields || [])];
                            newAliasFields[index].fieldName = e.target.value;
                            setSyncConfig({
                              ...syncConfig,
                              mysqlConfig: {...syncConfig.mysqlConfig, aliasFields: newAliasFields}
                            });
                          }}
                          label="Field"
                        >
                          {mysqlSchema.schema[syncConfig.mysqlConfig.tableName]?.map(col => (
                            <MenuItem key={col.name} value={col.name}>{col.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="Source"
                        value={aliasField.source}
                        onChange={(e) => {
                          const newAliasFields = [...(syncConfig.mysqlConfig.aliasFields || [])];
                          newAliasFields[index].source = e.target.value;
                          setSyncConfig({
                            ...syncConfig,
                            mysqlConfig: {...syncConfig.mysqlConfig, aliasFields: newAliasFields}
                          });
                        }}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Confidence"
                        type="number"
                        inputProps={{ min: 0, max: 1, step: 0.1 }}
                        value={aliasField.confidence}
                        onChange={(e) => {
                          const newAliasFields = [...(syncConfig.mysqlConfig.aliasFields || [])];
                          newAliasFields[index].confidence = parseFloat(e.target.value);
                          setSyncConfig({
                            ...syncConfig,
                            mysqlConfig: {...syncConfig.mysqlConfig, aliasFields: newAliasFields}
                          });
                        }}
                        sx={{ width: 120 }}
                      />
                      <IconButton
                        onClick={() => {
                          const newAliasFields = syncConfig.mysqlConfig.aliasFields.filter((_, i) => i !== index);
                          setSyncConfig({
                            ...syncConfig,
                            mysqlConfig: {...syncConfig.mysqlConfig, aliasFields: newAliasFields}
                          });
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newAliasFields = [...(syncConfig.mysqlConfig.aliasFields || []), { fieldName: '', source: '', confidence: 0.9 }];
                      setSyncConfig({
                        ...syncConfig,
                        mysqlConfig: {...syncConfig.mysqlConfig, aliasFields: newAliasFields}
                      });
                    }}
                    disabled={!syncConfig.mysqlConfig?.tableName}
                  >
                    Add Alias Field
                  </Button>
                </Box>
                  </>
                )}

                {/* API Configuration */}
                {syncConfig.source === 'api' && (
                  <>
                    {/* API URL */}
                    <TextField
                      fullWidth
                      label="API URL"
                      value={syncConfig.apiConfig?.baseUrl || ''}
                      onChange={(e) => setSyncConfig({
                        ...syncConfig,
                        apiConfig: {...syncConfig.apiConfig, baseUrl: e.target.value}
                      })}
                      placeholder="https://api.example.com/items or https://api.example.com/items/{{DATE:YYYY-MM-DD}}"
                      helperText="Use {{DATE}}, {{DATE:format}}, {{OFFSET}}, {{LIMIT}}, {{PAGE}}, {{CURSOR}} for dynamic values"
                    />

                    {/* HTTP Method */}
                    <FormControl fullWidth>
                      <InputLabel>HTTP Method</InputLabel>
                      <Select
                        value={syncConfig.apiConfig?.method || 'GET'}
                        onChange={(e) => setSyncConfig({
                          ...syncConfig,
                          apiConfig: {...syncConfig.apiConfig, method: e.target.value}
                        })}
                        label="HTTP Method"
                      >
                        <MenuItem value="GET">GET</MenuItem>
                        <MenuItem value="POST">POST</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Authentication */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Authentication</Typography>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Auth Type</InputLabel>
                        <Select
                          value={syncConfig.apiConfig?.auth?.type || 'none'}
                          onChange={(e) => setSyncConfig({
                            ...syncConfig,
                            apiConfig: {
                              ...syncConfig.apiConfig,
                              auth: {...syncConfig.apiConfig.auth, type: e.target.value}
                            }
                          })}
                          label="Auth Type"
                        >
                          <MenuItem value="none">None</MenuItem>
                          <MenuItem value="api_key">API Key (Header)</MenuItem>
                          <MenuItem value="bearer">Bearer Token</MenuItem>
                          <MenuItem value="basic">Basic Auth</MenuItem>
                        </Select>
                      </FormControl>

                      {syncConfig.apiConfig?.auth?.type === 'api_key' && (
                        <Box display="flex" gap={2}>
                          <TextField
                            label="Header Name"
                            value={syncConfig.apiConfig?.auth?.apiKeyHeader || ''}
                            onChange={(e) => setSyncConfig({
                              ...syncConfig,
                              apiConfig: {
                                ...syncConfig.apiConfig,
                                auth: {...syncConfig.apiConfig.auth, apiKeyHeader: e.target.value}
                              }
                            })}
                            placeholder="X-API-Key"
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            label="API Key"
                            type="password"
                            value={syncConfig.apiConfig?.auth?.apiKeyValue || ''}
                            onChange={(e) => setSyncConfig({
                              ...syncConfig,
                              apiConfig: {
                                ...syncConfig.apiConfig,
                                auth: {...syncConfig.apiConfig.auth, apiKeyValue: e.target.value}
                              }
                            })}
                            sx={{ flex: 2 }}
                          />
                        </Box>
                      )}

                      {syncConfig.apiConfig?.auth?.type === 'bearer' && (
                        <TextField
                          fullWidth
                          label="Bearer Token"
                          type="password"
                          value={syncConfig.apiConfig?.auth?.bearerToken || ''}
                          onChange={(e) => setSyncConfig({
                            ...syncConfig,
                            apiConfig: {
                              ...syncConfig.apiConfig,
                              auth: {...syncConfig.apiConfig.auth, bearerToken: e.target.value}
                            }
                          })}
                        />
                      )}

                      {syncConfig.apiConfig?.auth?.type === 'basic' && (
                        <Box display="flex" gap={2}>
                          <TextField
                            label="Username"
                            value={syncConfig.apiConfig?.auth?.basicUsername || ''}
                            onChange={(e) => setSyncConfig({
                              ...syncConfig,
                              apiConfig: {
                                ...syncConfig.apiConfig,
                                auth: {...syncConfig.apiConfig.auth, basicUsername: e.target.value}
                              }
                            })}
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            label="Password"
                            type="password"
                            value={syncConfig.apiConfig?.auth?.basicPassword || ''}
                            onChange={(e) => setSyncConfig({
                              ...syncConfig,
                              apiConfig: {
                                ...syncConfig.apiConfig,
                                auth: {...syncConfig.apiConfig.auth, basicPassword: e.target.value}
                              }
                            })}
                            sx={{ flex: 1 }}
                          />
                        </Box>
                      )}
                    </Box>

                    {/* Custom Headers */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Custom Headers (Optional)</Typography>
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                        Add custom HTTP headers to include with each request
                      </Typography>
                      {syncConfig.apiConfig?.headers?.map((header, index) => (
                        <Box key={index} display="flex" gap={2} mb={2} alignItems="center">
                          <TextField
                            label="Header Name"
                            value={header.key || ''}
                            onChange={(e) => {
                              const newHeaders = [...(syncConfig.apiConfig.headers || [])];
                              newHeaders[index] = { ...header, key: e.target.value };
                              setSyncConfig({
                                ...syncConfig,
                                apiConfig: {...syncConfig.apiConfig, headers: newHeaders}
                              });
                            }}
                            placeholder="X-Custom-Header"
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            label="Header Value"
                            value={header.value || ''}
                            onChange={(e) => {
                              const newHeaders = [...(syncConfig.apiConfig.headers || [])];
                              newHeaders[index] = { ...header, value: e.target.value };
                              setSyncConfig({
                                ...syncConfig,
                                apiConfig: {...syncConfig.apiConfig, headers: newHeaders}
                              });
                            }}
                            placeholder="header-value"
                            sx={{ flex: 2 }}
                          />
                          <IconButton
                            onClick={() => {
                              const newHeaders = syncConfig.apiConfig.headers.filter((_, i) => i !== index);
                              setSyncConfig({
                                ...syncConfig,
                                apiConfig: {...syncConfig.apiConfig, headers: newHeaders}
                              });
                            }}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const newHeaders = [...(syncConfig.apiConfig?.headers || []), { key: '', value: '' }];
                          setSyncConfig({
                            ...syncConfig,
                            apiConfig: {...syncConfig.apiConfig, headers: newHeaders}
                          });
                        }}
                        size="small"
                      >
                        Add Header
                      </Button>
                    </Box>

                    {/* Response Configuration */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Response Configuration</Typography>
                      <TextField
                        fullWidth
                        label="Data Path (JSONPath)"
                        value={syncConfig.apiConfig?.responseConfig?.dataPath || ''}
                        onChange={(e) => setSyncConfig({
                          ...syncConfig,
                          apiConfig: {
                            ...syncConfig.apiConfig,
                            responseConfig: {...syncConfig.apiConfig.responseConfig, dataPath: e.target.value}
                          }
                        })}
                        placeholder="data.items or results (leave empty for root array)"
                        helperText="Path to the array of items in the API response"
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Canonical Name Field"
                        value={syncConfig.apiConfig?.responseConfig?.canonicalNameField || ''}
                        onChange={(e) => setSyncConfig({
                          ...syncConfig,
                          apiConfig: {
                            ...syncConfig.apiConfig,
                            responseConfig: {...syncConfig.apiConfig.responseConfig, canonicalNameField: e.target.value}
                          }
                        })}
                        placeholder="name or item.name"
                        helperText="Field name in each item that contains the canonical name"
                      />
                    </Box>

                    {/* Alias Fields for API */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Alias Fields (Optional)</Typography>
                      {syncConfig.apiConfig?.responseConfig?.aliasFields?.map((aliasField, index) => (
                        <Box key={index} display="flex" gap={2} mb={2}>
                          <TextField
                            label="Field Path"
                            value={aliasField.fieldName}
                            onChange={(e) => {
                              const newAliasFields = [...(syncConfig.apiConfig.responseConfig.aliasFields || [])];
                              newAliasFields[index].fieldName = e.target.value;
                              setSyncConfig({
                                ...syncConfig,
                                apiConfig: {
                                  ...syncConfig.apiConfig,
                                  responseConfig: {...syncConfig.apiConfig.responseConfig, aliasFields: newAliasFields}
                                }
                              });
                            }}
                            placeholder="sku or item.code"
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            label="Source"
                            value={aliasField.source}
                            onChange={(e) => {
                              const newAliasFields = [...(syncConfig.apiConfig.responseConfig.aliasFields || [])];
                              newAliasFields[index].source = e.target.value;
                              setSyncConfig({
                                ...syncConfig,
                                apiConfig: {
                                  ...syncConfig.apiConfig,
                                  responseConfig: {...syncConfig.apiConfig.responseConfig, aliasFields: newAliasFields}
                                }
                              });
                            }}
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            label="Confidence"
                            type="number"
                            inputProps={{ min: 0, max: 1, step: 0.1 }}
                            value={aliasField.confidence}
                            onChange={(e) => {
                              const newAliasFields = [...(syncConfig.apiConfig.responseConfig.aliasFields || [])];
                              newAliasFields[index].confidence = parseFloat(e.target.value);
                              setSyncConfig({
                                ...syncConfig,
                                apiConfig: {
                                  ...syncConfig.apiConfig,
                                  responseConfig: {...syncConfig.apiConfig.responseConfig, aliasFields: newAliasFields}
                                }
                              });
                            }}
                            sx={{ width: 120 }}
                          />
                          <IconButton
                            onClick={() => {
                              const newAliasFields = syncConfig.apiConfig.responseConfig.aliasFields.filter((_, i) => i !== index);
                              setSyncConfig({
                                ...syncConfig,
                                apiConfig: {
                                  ...syncConfig.apiConfig,
                                  responseConfig: {...syncConfig.apiConfig.responseConfig, aliasFields: newAliasFields}
                                }
                              });
                            }}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const newAliasFields = [...(syncConfig.apiConfig?.responseConfig?.aliasFields || []), { fieldName: '', source: '', confidence: 0.9 }];
                          setSyncConfig({
                            ...syncConfig,
                            apiConfig: {
                              ...syncConfig.apiConfig,
                              responseConfig: {...syncConfig.apiConfig.responseConfig, aliasFields: newAliasFields}
                            }
                          });
                        }}
                      >
                        Add Alias Field
                      </Button>
                    </Box>

                    {/* Incremental Strategy */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Incremental Sync Strategy</Typography>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Strategy</InputLabel>
                        <Select
                          value={syncConfig.apiConfig?.incrementalStrategy?.type || 'none'}
                          onChange={(e) => setSyncConfig({
                            ...syncConfig,
                            apiConfig: {
                              ...syncConfig.apiConfig,
                              incrementalStrategy: {...syncConfig.apiConfig.incrementalStrategy, type: e.target.value}
                            }
                          })}
                          label="Strategy"
                        >
                          <MenuItem value="none">
                            <Box>
                              <Typography variant="body2" fontWeight="bold">None (Full Sync)</Typography>
                              <Typography variant="caption" color="textSecondary">
                                Fetch all records every time
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="date_url">
                            <Box>
                              <Typography variant="body2" fontWeight="bold">Date in URL Path</Typography>
                              <Typography variant="caption" color="textSecondary">
                                Date is part of the URL path (e.g., /items/2024-01-15)
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="date_param">
                            <Box>
                              <Typography variant="body2" fontWeight="bold">Date as Query Parameter</Typography>
                              <Typography variant="caption" color="textSecondary">
                                Date is passed as a query parameter (e.g., ?since=2024-01-15)
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="pagination_offset">
                            <Box>
                              <Typography variant="body2" fontWeight="bold">Offset/Limit Pagination</Typography>
                              <Typography variant="caption" color="textSecondary">
                                Use offset and limit parameters
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="pagination_page">
                            <Box>
                              <Typography variant="body2" fontWeight="bold">Page Number Pagination</Typography>
                              <Typography variant="caption" color="textSecondary">
                                Use page number parameter
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="pagination_cursor">
                            <Box>
                              <Typography variant="body2" fontWeight="bold">Cursor-based Pagination</Typography>
                              <Typography variant="caption" color="textSecondary">
                                Use cursor from previous response
                              </Typography>
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>

                      {/* Date-based config */}
                      {(syncConfig.apiConfig?.incrementalStrategy?.type === 'date_url' || 
                        syncConfig.apiConfig?.incrementalStrategy?.type === 'date_param') && (
                        <Box display="flex" flexDirection="column" gap={2}>
                          {syncConfig.apiConfig?.incrementalStrategy?.type === 'date_param' && (
                            <TextField
                              fullWidth
                              label="Date Parameter Name"
                              value={syncConfig.apiConfig?.incrementalStrategy?.dateConfig?.paramName || ''}
                              onChange={(e) => setSyncConfig({
                                ...syncConfig,
                                apiConfig: {
                                  ...syncConfig.apiConfig,
                                  incrementalStrategy: {
                                    ...syncConfig.apiConfig.incrementalStrategy,
                                    dateConfig: {...syncConfig.apiConfig.incrementalStrategy.dateConfig, paramName: e.target.value}
                                  }
                                }
                              })}
                              placeholder="since, from_date, updated_after"
                            />
                          )}
                          <TextField
                            fullWidth
                            label="Date Format"
                            value={syncConfig.apiConfig?.incrementalStrategy?.dateConfig?.dateFormat || 'YYYY-MM-DD'}
                            onChange={(e) => setSyncConfig({
                              ...syncConfig,
                              apiConfig: {
                                ...syncConfig.apiConfig,
                                incrementalStrategy: {
                                  ...syncConfig.apiConfig.incrementalStrategy,
                                  dateConfig: {...syncConfig.apiConfig.incrementalStrategy.dateConfig, dateFormat: e.target.value}
                                }
                              }
                            })}
                            helperText="Format: YYYY-MM-DD, YYYY/MM/DD, DD-MM-YYYY, etc."
                          />
                          <FormControl fullWidth>
                            <InputLabel>Date Source</InputLabel>
                            <Select
                              value={syncConfig.apiConfig?.incrementalStrategy?.dateConfig?.dateSource || 'last_sync'}
                              onChange={(e) => setSyncConfig({
                                ...syncConfig,
                                apiConfig: {
                                  ...syncConfig.apiConfig,
                                  incrementalStrategy: {
                                    ...syncConfig.apiConfig.incrementalStrategy,
                                    dateConfig: {...syncConfig.apiConfig.incrementalStrategy.dateConfig, dateSource: e.target.value}
                                  }
                                }
                              })}
                              label="Date Source"
                            >
                              <MenuItem value="last_sync">Last Sync Date</MenuItem>
                              <MenuItem value="fixed_offset">Fixed Day Offset</MenuItem>
                            </Select>
                          </FormControl>
                          {syncConfig.apiConfig?.incrementalStrategy?.dateConfig?.dateSource === 'fixed_offset' && (
                            <TextField
                              fullWidth
                              type="number"
                              label="Days Back"
                              value={syncConfig.apiConfig?.incrementalStrategy?.dateConfig?.offsetDays || 1}
                              onChange={(e) => setSyncConfig({
                                ...syncConfig,
                                apiConfig: {
                                  ...syncConfig.apiConfig,
                                  incrementalStrategy: {
                                    ...syncConfig.apiConfig.incrementalStrategy,
                                    dateConfig: {...syncConfig.apiConfig.incrementalStrategy.dateConfig, offsetDays: parseInt(e.target.value)}
                                  }
                                }
                              })}
                              helperText="Number of days to look back from today"
                            />
                          )}
                        </Box>
                      )}

                      {/* Pagination config */}
                      {syncConfig.apiConfig?.incrementalStrategy?.type?.startsWith('pagination_') && (
                        <Box display="flex" flexDirection="column" gap={2}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Page Size"
                            value={syncConfig.apiConfig?.incrementalStrategy?.paginationConfig?.pageSize || 100}
                            onChange={(e) => setSyncConfig({
                              ...syncConfig,
                              apiConfig: {
                                ...syncConfig.apiConfig,
                                incrementalStrategy: {
                                  ...syncConfig.apiConfig.incrementalStrategy,
                                  paginationConfig: {...syncConfig.apiConfig.incrementalStrategy.paginationConfig, pageSize: parseInt(e.target.value)}
                                }
                              }
                            })}
                          />

                          {syncConfig.apiConfig?.incrementalStrategy?.type === 'pagination_offset' && (
                            <Box display="flex" gap={2}>
                              <TextField
                                label="Offset Parameter"
                                value={syncConfig.apiConfig?.incrementalStrategy?.paginationConfig?.offsetParam || 'offset'}
                                onChange={(e) => setSyncConfig({
                                  ...syncConfig,
                                  apiConfig: {
                                    ...syncConfig.apiConfig,
                                    incrementalStrategy: {
                                      ...syncConfig.apiConfig.incrementalStrategy,
                                      paginationConfig: {...syncConfig.apiConfig.incrementalStrategy.paginationConfig, offsetParam: e.target.value}
                                    }
                                  }
                                })}
                                sx={{ flex: 1 }}
                              />
                              <TextField
                                label="Limit Parameter"
                                value={syncConfig.apiConfig?.incrementalStrategy?.paginationConfig?.limitParam || 'limit'}
                                onChange={(e) => setSyncConfig({
                                  ...syncConfig,
                                  apiConfig: {
                                    ...syncConfig.apiConfig,
                                    incrementalStrategy: {
                                      ...syncConfig.apiConfig.incrementalStrategy,
                                      paginationConfig: {...syncConfig.apiConfig.incrementalStrategy.paginationConfig, limitParam: e.target.value}
                                    }
                                  }
                                })}
                                sx={{ flex: 1 }}
                              />
                            </Box>
                          )}

                          {syncConfig.apiConfig?.incrementalStrategy?.type === 'pagination_page' && (
                            <TextField
                              fullWidth
                              label="Page Parameter"
                              value={syncConfig.apiConfig?.incrementalStrategy?.paginationConfig?.pageParam || 'page'}
                              onChange={(e) => setSyncConfig({
                                ...syncConfig,
                                apiConfig: {
                                  ...syncConfig.apiConfig,
                                  incrementalStrategy: {
                                    ...syncConfig.apiConfig.incrementalStrategy,
                                    paginationConfig: {...syncConfig.apiConfig.incrementalStrategy.paginationConfig, pageParam: e.target.value}
                                  }
                                }
                              })}
                            />
                          )}

                          {syncConfig.apiConfig?.incrementalStrategy?.type === 'pagination_cursor' && (
                            <Box display="flex" flexDirection="column" gap={2}>
                              <TextField
                                fullWidth
                                label="Cursor Parameter"
                                value={syncConfig.apiConfig?.incrementalStrategy?.paginationConfig?.cursorParam || 'cursor'}
                                onChange={(e) => setSyncConfig({
                                  ...syncConfig,
                                  apiConfig: {
                                    ...syncConfig.apiConfig,
                                    incrementalStrategy: {
                                      ...syncConfig.apiConfig.incrementalStrategy,
                                      paginationConfig: {...syncConfig.apiConfig.incrementalStrategy.paginationConfig, cursorParam: e.target.value}
                                    }
                                  }
                                })}
                              />
                              <TextField
                                fullWidth
                                label="Next Cursor Path"
                                value={syncConfig.apiConfig?.incrementalStrategy?.paginationConfig?.nextCursorPath || ''}
                                onChange={(e) => setSyncConfig({
                                  ...syncConfig,
                                  apiConfig: {
                                    ...syncConfig.apiConfig,
                                    incrementalStrategy: {
                                      ...syncConfig.apiConfig.incrementalStrategy,
                                      paginationConfig: {...syncConfig.apiConfig.incrementalStrategy.paginationConfig, nextCursorPath: e.target.value}
                                    }
                                  }
                                })}
                                placeholder="meta.next_cursor or pagination.cursor"
                                helperText="Path to the next cursor value in the response"
                              />
                            </Box>
                          )}

                          <FormControl fullWidth>
                            <InputLabel>End Condition</InputLabel>
                            <Select
                              value={syncConfig.apiConfig?.incrementalStrategy?.paginationConfig?.endCondition?.type || 'empty_data'}
                              onChange={(e) => setSyncConfig({
                                ...syncConfig,
                                apiConfig: {
                                  ...syncConfig.apiConfig,
                                  incrementalStrategy: {
                                    ...syncConfig.apiConfig.incrementalStrategy,
                                    paginationConfig: {
                                      ...syncConfig.apiConfig.incrementalStrategy.paginationConfig,
                                      endCondition: {...syncConfig.apiConfig.incrementalStrategy.paginationConfig.endCondition, type: e.target.value}
                                    }
                                  }
                                }
                              })}
                              label="End Condition"
                            >
                              <MenuItem value="empty_data">Empty Data Array</MenuItem>
                              <MenuItem value="null_cursor">Null/Empty Cursor</MenuItem>
                              <MenuItem value="total_count">Total Count Reached</MenuItem>
                              <MenuItem value="has_more_field">Has More Field = false</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      )}
                    </Box>

                    {/* Rate Limiting */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Rate Limiting</Typography>
                      <Box display="flex" gap={2}>
                        <TextField
                          type="number"
                          label="Requests per Minute"
                          value={syncConfig.apiConfig?.rateLimit?.requestsPerMinute || 60}
                          onChange={(e) => setSyncConfig({
                            ...syncConfig,
                            apiConfig: {
                              ...syncConfig.apiConfig,
                              rateLimit: {...syncConfig.apiConfig.rateLimit, requestsPerMinute: parseInt(e.target.value)}
                            }
                          })}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          type="number"
                          label="Timeout (ms)"
                          value={syncConfig.apiConfig?.timeout || 30000}
                          onChange={(e) => setSyncConfig({
                            ...syncConfig,
                            apiConfig: {...syncConfig.apiConfig, timeout: parseInt(e.target.value)}
                          })}
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    </Box>
                  </>
                )}

                {/* Deduplication Configuration */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Deduplication</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={syncConfig.deduplication?.enabled || false}
                        onChange={(e) => setSyncConfig({
                          ...syncConfig,
                          deduplication: {...syncConfig.deduplication, enabled: e.target.checked}
                        })}
                      />
                    }
                    label="Enable Deduplication by Unique Identifier"
                  />
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                    Use a unique identifier (like SKU, product_id, or item_id) to prevent duplicates and optionally update existing entries
                  </Typography>
                  
                  {syncConfig.deduplication?.enabled && (
                    <Box display="flex" flexDirection="column" gap={2}>
                      {/* Identifier Field - different input based on source */}
                      {syncConfig.source === 'mysql' && syncConfig.mysqlConfig?.tableName ? (
                        <FormControl fullWidth>
                          <InputLabel>Unique Identifier Field</InputLabel>
                          <Select
                            value={syncConfig.deduplication?.identifierField || ''}
                            onChange={(e) => setSyncConfig({
                              ...syncConfig,
                              deduplication: {...syncConfig.deduplication, identifierField: e.target.value}
                            })}
                            label="Unique Identifier Field"
                          >
                            {mysqlSchema.schema[syncConfig.mysqlConfig.tableName]?.map(col => (
                              <MenuItem key={col.name} value={col.name}>
                                {col.name} ({col.type}) {col.key === 'PRI' && '(PRIMARY KEY)'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          fullWidth
                          label="Unique Identifier Field"
                          value={syncConfig.deduplication?.identifierField || ''}
                          onChange={(e) => setSyncConfig({
                            ...syncConfig,
                            deduplication: {...syncConfig.deduplication, identifierField: e.target.value}
                          })}
                          placeholder={syncConfig.source === 'api' ? 'id or item.sku' : 'product_id'}
                          helperText={syncConfig.source === 'api' 
                            ? 'Field path in the API response (e.g., id, item.sku, data.product_id)' 
                            : 'Column name from the MySQL table'
                          }
                        />
                      )}
                      
                      <FormControl fullWidth>
                        <InputLabel>On Duplicate</InputLabel>
                        <Select
                          value={syncConfig.deduplication?.onDuplicate || 'skip'}
                          onChange={(e) => setSyncConfig({
                            ...syncConfig,
                            deduplication: {...syncConfig.deduplication, onDuplicate: e.target.value}
                          })}
                          label="On Duplicate"
                        >
                          <MenuItem value="skip">
                            <Box>
                              <Typography variant="body2" fontWeight="bold">Skip</Typography>
                              <Typography variant="caption" color="textSecondary">
                                Don't add entries that already exist (keep original)
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="update">
                            <Box>
                              <Typography variant="body2" fontWeight="bold">Update</Typography>
                              <Typography variant="caption" color="textSecondary">
                                Update existing entries with new data
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="update_if_newer">
                            <Box>
                              <Typography variant="body2" fontWeight="bold">Update if Newer</Typography>
                              <Typography variant="caption" color="textSecondary">
                                Only update if the new record is more recent (requires freshness field)
                              </Typography>
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                      
                      {syncConfig.deduplication?.onDuplicate === 'update_if_newer' && (
                        syncConfig.source === 'mysql' && syncConfig.mysqlConfig?.tableName ? (
                          <FormControl fullWidth>
                            <InputLabel>Freshness Field</InputLabel>
                            <Select
                              value={syncConfig.deduplication?.freshnessField || ''}
                              onChange={(e) => setSyncConfig({
                                ...syncConfig,
                                deduplication: {...syncConfig.deduplication, freshnessField: e.target.value}
                              })}
                              label="Freshness Field"
                            >
                              {mysqlSchema.schema[syncConfig.mysqlConfig.tableName]
                                ?.filter(col => col.type.toLowerCase().includes('date') || col.type.toLowerCase().includes('time'))
                                .map(col => (
                                  <MenuItem key={col.name} value={col.name}>
                                    {col.name} ({col.type})
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <TextField
                            fullWidth
                            label="Freshness Field"
                            value={syncConfig.deduplication?.freshnessField || ''}
                            onChange={(e) => setSyncConfig({
                              ...syncConfig,
                              deduplication: {...syncConfig.deduplication, freshnessField: e.target.value}
                            })}
                            placeholder="updated_at or metadata.modified_date"
                            helperText="Timestamp field to compare for determining which record is newer"
                          />
                        )
                      )}
                    </Box>
                  )}
                </Box>
                
                {/* Schedule Configuration */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Schedule</Typography>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={syncConfig.schedule?.timezone || 'UTC'}
                      onChange={(e) => setSyncConfig({
                        ...syncConfig,
                        schedule: {...syncConfig.schedule, timezone: e.target.value}
                      })}
                      label="Timezone"
                    >
                      <MenuItem value="UTC">UTC</MenuItem>
                      <MenuItem value="America/New_York">America/New_York (EST/EDT)</MenuItem>
                      <MenuItem value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</MenuItem>
                      <MenuItem value="America/Chicago">America/Chicago (CST/CDT)</MenuItem>
                      <MenuItem value="Europe/London">Europe/London (GMT/BST)</MenuItem>
                      <MenuItem value="Europe/Paris">Europe/Paris (CET/CEST)</MenuItem>
                      <MenuItem value="Asia/Tokyo">Asia/Tokyo (JST)</MenuItem>
                      <MenuItem value="Asia/Shanghai">Asia/Shanghai (CST)</MenuItem>
                      <MenuItem value="Asia/Singapore">Asia/Singapore (SGT)</MenuItem>
                      <MenuItem value="Asia/Jakarta">Asia/Jakarta (WIB)</MenuItem>
                      <MenuItem value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {syncConfig.schedule?.times?.map((time, index) => (
                    <Box key={index} display="flex" gap={2} mb={2} alignItems="center">
                      <TextField
                        label="Hour"
                        type="number"
                        inputProps={{ min: 0, max: 23 }}
                        value={time.hour}
                        onChange={(e) => {
                          const newTimes = [...(syncConfig.schedule.times || [])];
                          newTimes[index].hour = parseInt(e.target.value);
                          setSyncConfig({
                            ...syncConfig,
                            schedule: {...syncConfig.schedule, times: newTimes}
                          });
                        }}
                        sx={{ width: 100 }}
                      />
                      <TextField
                        label="Minute"
                        type="number"
                        inputProps={{ min: 0, max: 59 }}
                        value={time.minute}
                        onChange={(e) => {
                          const newTimes = [...(syncConfig.schedule.times || [])];
                          newTimes[index].minute = parseInt(e.target.value);
                          setSyncConfig({
                            ...syncConfig,
                            schedule: {...syncConfig.schedule, times: newTimes}
                          });
                        }}
                        sx={{ width: 100 }}
                      />
                      <Typography variant="body2" color="textSecondary" sx={{ flex: 1 }}>
                        {String(time.hour).padStart(2, '0')}:{String(time.minute).padStart(2, '0')} {syncConfig.schedule.timezone}
                      </Typography>
                      <IconButton
                        onClick={() => {
                          const newTimes = syncConfig.schedule.times.filter((_, i) => i !== index);
                          setSyncConfig({
                            ...syncConfig,
                            schedule: {...syncConfig.schedule, times: newTimes}
                          });
                        }}
                        color="error"
                        disabled={syncConfig.schedule.times.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newTimes = [...(syncConfig.schedule.times || []), { hour: 2, minute: 0 }];
                      setSyncConfig({
                        ...syncConfig,
                        schedule: {...syncConfig.schedule, times: newTimes}
                      });
                    }}
                  >
                    Add Schedule Time
                  </Button>
                </Box>
                
                {/* Test Result */}
                {syncTestResult && (
                  <Alert severity={syncTestResult.success ? "success" : "error"}>
                    {syncTestResult.success ? (
                      <Box>
                        <Typography variant="body2" fontWeight="bold">Configuration is valid!</Typography>
                        <Typography variant="caption">Sample records fetched: {syncTestResult.recordCount}</Typography>
                        {syncTestResult.sampleRecords && syncTestResult.sampleRecords.length > 0 && (
                          <Box mt={1}>
                            <Typography variant="caption" fontWeight="bold">Sample data:</Typography>
                            <pre style={{ fontSize: '11px', maxHeight: '150px', overflow: 'auto' }}>
                              {JSON.stringify(syncTestResult.sampleRecords[0], null, 2)}
                            </pre>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2">Error: {syncTestResult.error}</Typography>
                    )}
                  </Alert>
                )}
                
                {/* Last Sync Info */}
                {selectedLibrary?.syncConfig?.lastSync && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Last Sync</Typography>
                    <Typography variant="caption" display="block">
                      Status: <Chip size="small" label={selectedLibrary.syncConfig.lastSync.status} 
                        color={selectedLibrary.syncConfig.lastSync.status === 'success' ? 'success' : 'error'} />
                    </Typography>
                    <Typography variant="caption" display="block">
                      Time: {new Date(selectedLibrary.syncConfig.lastSync.timestamp).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Processed: {selectedLibrary.syncConfig.lastSync.itemsProcessed} | 
                      Added: {selectedLibrary.syncConfig.lastSync.itemsAdded} | 
                      Updated: {selectedLibrary.syncConfig.lastSync.itemsUpdated || 0} | 
                      Skipped: {selectedLibrary.syncConfig.lastSync.itemsSkipped}
                    </Typography>
                    {selectedLibrary.syncConfig.lastSync.errorMessage && (
                      <Typography variant="caption" color="error" display="block">
                        Error: {selectedLibrary.syncConfig.lastSync.errorMessage}
                      </Typography>
                    )}
                  </Paper>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)}>Cancel</Button>
          {syncConfig.enabled && (
            <Button 
              onClick={handleTestSyncConfig} 
              disabled={testingSyncConfig || (
                syncConfig.source === 'mysql' 
                  ? (!syncConfig.mysqlConfig?.tableName || !syncConfig.mysqlConfig?.canonicalNameField)
                  : (!syncConfig.apiConfig?.baseUrl || !syncConfig.apiConfig?.responseConfig?.canonicalNameField)
              )}
              startIcon={testingSyncConfig ? <CircularProgress size={16} /> : null}
            >
              Test Configuration
            </Button>
          )}
          <Button 
            variant="contained" 
            onClick={handleSaveSyncConfig}
            disabled={syncConfig.enabled && (
              syncConfig.source === 'mysql' 
                ? (!syncConfig.mysqlConfig?.tableName || !syncConfig.mysqlConfig?.canonicalNameField)
                : (!syncConfig.apiConfig?.baseUrl || !syncConfig.apiConfig?.responseConfig?.canonicalNameField)
            )}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync History Dialog */}
      <Dialog open={syncHistoryDialogOpen} onClose={() => setSyncHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Sync History</DialogTitle>
        <DialogContent>
          {loadingSyncHistory ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box mt={2}>
              {syncStats && (
                <Box display="flex" gap={2} mb={3}>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                    <Typography variant="h4" color="primary">{syncStats.totalSyncs}</Typography>
                    <Typography variant="caption">Total Syncs</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                    <Typography variant="h4" color="success.main">{syncStats.successful}</Typography>
                    <Typography variant="caption">Successful</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                    <Typography variant="h4" color="error.main">{syncStats.failed}</Typography>
                    <Typography variant="caption">Failed</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                    <Typography variant="h4">{syncStats.totalItemsAdded}</Typography>
                    <Typography variant="caption">Items Added</Typography>
                  </Paper>
                </Box>
              )}
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Processed</TableCell>
                      <TableCell align="right">Added</TableCell>
                      <TableCell align="right">Skipped</TableCell>
                      <TableCell align="right">Duration</TableCell>
                      <TableCell>Triggered By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {syncHistory.map((history) => (
                      <TableRow key={history._id}>
                        <TableCell>{new Date(history.syncDate).toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip 
                            size="small" 
                            label={history.status}
                            color={history.status === 'success' ? 'success' : history.status === 'failed' ? 'error' : 'warning'}
                          />
                        </TableCell>
                        <TableCell align="right">{history.itemsProcessed}</TableCell>
                        <TableCell align="right">{history.itemsAdded}</TableCell>
                        <TableCell align="right">{history.itemsSkipped}</TableCell>
                        <TableCell align="right">{(history.duration / 1000).toFixed(2)}s</TableCell>
                        <TableCell>{history.metadata?.triggeredBy || 'scheduled'}</TableCell>
                      </TableRow>
                    ))}
                    {syncHistory.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="textSecondary">No sync history available</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncHistoryDialogOpen(false)}>Close</Button>
          <Button onClick={() => fetchSyncHistory(selectedLibrary.libraryId)} startIcon={<SyncIcon />}>
            Refresh
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const CleanupTab = ({ companyId, projectId, project, isActive }) => {
  const [cleanupSettings, setCleanupSettings] = useState(null);
  const [cleanupHistory, setCleanupHistory] = useState([]);
  const [cleanupStats, setCleanupStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [softDeletedDialogOpen, setSoftDeletedDialogOpen] = useState(false);
  const [softDeletedExecutions, setSoftDeletedExecutions] = useState([]);
  const [softDeletedLoading, setSoftDeletedLoading] = useState(false);
  const [runCleanupDialogOpen, setRunCleanupDialogOpen] = useState(false);
  const [runningCleanup, setRunningCleanup] = useState(false);
  
  // Pagination for history
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  
  // Available options
  const retentionValues = Array.from({ length: 24 }, (_, i) => i + 1);
  const retentionUnits = ['days', 'weeks', 'months', 'years'];
  const gracePeriodValues = Array.from({ length: 90 }, (_, i) => i + 1);
  const gracePeriodUnits = ['days', 'weeks'];
  const frequencies = ['daily', 'weekly', 'monthly'];
  const allStatuses = ['completed', 'failed', 'cancelled', 'started', 'in_progress', 'waiting_input', 'waiting_webhook'];
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const daysOfMonth = Array.from({ length: 31 }, (_, i) => i + 1);
  
  // Fetch cleanup settings
  const fetchCleanupSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/projects/${projectId}/cleanup/settings`);
      setCleanupSettings(response.data);
    } catch (err) {
      setError('Failed to fetch cleanup settings');
      console.error('Cleanup settings fetch error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch cleanup stats
  const fetchCleanupStats = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/cleanup/stats`);
      setCleanupStats(response.data);
    } catch (err) {
      console.error('Failed to fetch cleanup stats:', err);
    }
  };
  
  // Fetch cleanup history
  const fetchCleanupHistory = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/cleanup/history`, {
        params: {
          page: historyPage,
          limit: historyRowsPerPage
        }
      });
      setCleanupHistory(response.data.history || []);
      setHistoryTotal(response.data.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to fetch cleanup history:', err);
    }
  };
  
  // Save cleanup settings
  const saveCleanupSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      await api.put(`/projects/${projectId}/cleanup/settings`, cleanupSettings);
      
      setSuccess('Cleanup settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
      
      // Refresh stats
      await fetchCleanupStats();
    } catch (err) {
      setError('Failed to save cleanup settings: ' + (err.response?.data?.message || err.message));
      console.error('Save cleanup settings error:', err);
    } finally {
      setSaving(false);
    }
  };
  
  // Handle run cleanup now
  const handleRunCleanupNow = async () => {
    try {
      setRunningCleanup(true);
      setError('');
      
      await api.post(`/projects/${projectId}/cleanup/run`);
      
      setSuccess('Cleanup started successfully! Check history for results.');
      setRunCleanupDialogOpen(false);
      
      // Refresh history and stats after a delay
      setTimeout(() => {
        fetchCleanupHistory();
        fetchCleanupStats();
      }, 3000);
    } catch (err) {
      setError('Failed to run cleanup: ' + (err.response?.data?.message || err.message));
    } finally {
      setRunningCleanup(false);
    }
  };
  
  // Handle preview cleanup
  const handlePreviewCleanup = async () => {
    try {
      setPreviewLoading(true);
      setPreviewDialogOpen(true);
      
      const response = await api.get(`/projects/${projectId}/cleanup/preview`);
      setPreviewData(response.data);
    } catch (err) {
      setError('Failed to generate preview: ' + (err.response?.data?.message || err.message));
      setPreviewDialogOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Fetch soft deleted executions
  const fetchSoftDeletedExecutions = async () => {
    try {
      setSoftDeletedLoading(true);
      setSoftDeletedDialogOpen(true);
      
      const response = await api.get(`/projects/${projectId}/cleanup/soft-deleted`);
      setSoftDeletedExecutions(response.data.executions || []);
    } catch (err) {
      setError('Failed to fetch soft-deleted executions: ' + (err.response?.data?.message || err.message));
      setSoftDeletedDialogOpen(false);
    } finally {
      setSoftDeletedLoading(false);
    }
  };
  
  // Handle restore execution
  const handleRestoreExecution = async (executionId) => {
    try {
      await api.post(`/projects/${projectId}/cleanup/restore/${executionId}`);
      
      setSuccess(`Execution ${executionId} restored successfully!`);
      
      // Refresh soft deleted list
      await fetchSoftDeletedExecutions();
    } catch (err) {
      setError('Failed to restore execution: ' + (err.response?.data?.message || err.message));
    }
  };
  
  // Format bytes to human-readable
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };
  
  // Calculate cutoff date for display
  const calculateCutoffDate = () => {
    if (!cleanupSettings?.retentionPeriod) return null;
    
    const now = new Date();
    const { value, unit } = cleanupSettings.retentionPeriod;
    
    let milliseconds = 0;
    switch (unit) {
      case 'days':
        milliseconds = value * 24 * 60 * 60 * 1000;
        break;
      case 'weeks':
        milliseconds = value * 7 * 24 * 60 * 60 * 1000;
        break;
      case 'months':
        milliseconds = value * 30 * 24 * 60 * 60 * 1000;
        break;
      case 'years':
        milliseconds = value * 365 * 24 * 60 * 60 * 1000;
        break;
      default:
        milliseconds = value * 24 * 60 * 60 * 1000;
    }
    
    return new Date(now.getTime() - milliseconds);
  };
  
  useEffect(() => {
    if (isActive) {
      fetchCleanupSettings();
      fetchCleanupStats();
      fetchCleanupHistory();
    }
  }, [isActive, projectId]);
  
  useEffect(() => {
    if (isActive) {
      fetchCleanupHistory();
    }
  }, [historyPage, historyRowsPerPage]);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!cleanupSettings) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load cleanup settings</Alert>
      </Box>
    );
  }
  
  const cutoffDate = calculateCutoffDate();
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Automatic Cleanup
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure automatic cleanup of old flow executions and associated S3 files. Deleted executions can be restored during the grace period.
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      {/* Enable/Disable Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Enable Automatic Cleanup
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {cleanupSettings.enabled 
                ? 'Cleanup is currently enabled and will run on schedule'
                : 'Enable cleanup to automatically remove old executions'}
            </Typography>
          </Box>
          <Switch
            checked={cleanupSettings.enabled || false}
            onChange={(e) => setCleanupSettings({ ...cleanupSettings, enabled: e.target.checked })}
            color="primary"
          />
        </Box>
        
        {cleanupSettings.enabled && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Warning: Enabling cleanup will automatically delete old executions based on the settings below. 
            Deleted executions can be restored during the grace period.
          </Alert>
        )}
      </Paper>
      
      {/* Retention Period Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Retention Period
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Executions older than this period will be marked for deletion
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Period Value</InputLabel>
              <Select
                value={cleanupSettings.retentionPeriod?.value || 30}
                label="Period Value"
                onChange={(e) => setCleanupSettings({
                  ...cleanupSettings,
                  retentionPeriod: { ...cleanupSettings.retentionPeriod, value: e.target.value }
                })}
              >
                {retentionValues.map(val => (
                  <MenuItem key={val} value={val}>{val}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Unit</InputLabel>
              <Select
                value={cleanupSettings.retentionPeriod?.unit || 'days'}
                label="Unit"
                onChange={(e) => setCleanupSettings({
                  ...cleanupSettings,
                  retentionPeriod: { ...cleanupSettings.retentionPeriod, unit: e.target.value }
                })}
              >
                {retentionUnits.map(unit => (
                  <MenuItem key={unit} value={unit}>{unit.charAt(0).toUpperCase() + unit.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        {cutoffDate && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Executions created before {cutoffDate.toLocaleDateString()} {cutoffDate.toLocaleTimeString()} will be eligible for cleanup
          </Alert>
        )}
      </Paper>
      
      {/* Status Filter Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Status Filter
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Select which execution statuses should be cleaned up
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setCleanupSettings({ ...cleanupSettings, cleanupStatuses: allStatuses })}
          >
            Select All
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setCleanupSettings({ ...cleanupSettings, cleanupStatuses: [] })}
          >
            Deselect All
          </Button>
        </Box>
        
        <FormGroup>
          <Grid container spacing={1}>
            {allStatuses.map(status => (
              <Grid item xs={12} sm={6} md={4} key={status}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(cleanupSettings.cleanupStatuses || []).includes(status)}
                      onChange={(e) => {
                        const current = cleanupSettings.cleanupStatuses || [];
                        const newStatuses = e.target.checked
                          ? [...current, status]
                          : current.filter(s => s !== status);
                        setCleanupSettings({ ...cleanupSettings, cleanupStatuses: newStatuses });
                      }}
                    />
                  }
                  label={status.replace(/_/g, ' ').charAt(0).toUpperCase() + status.replace(/_/g, ' ').slice(1)}
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>
      </Paper>
      
      {/* Soft Delete Grace Period */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Grace Period (Soft Delete)
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Deleted executions can be restored during this period before permanent deletion
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Grace Period Value</InputLabel>
              <Select
                value={cleanupSettings.softDeleteGracePeriod?.value || 7}
                label="Grace Period Value"
                onChange={(e) => setCleanupSettings({
                  ...cleanupSettings,
                  softDeleteGracePeriod: { ...cleanupSettings.softDeleteGracePeriod, value: e.target.value }
                })}
              >
                {gracePeriodValues.map(val => (
                  <MenuItem key={val} value={val}>{val}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Unit</InputLabel>
              <Select
                value={cleanupSettings.softDeleteGracePeriod?.unit || 'days'}
                label="Unit"
                onChange={(e) => setCleanupSettings({
                  ...cleanupSettings,
                  softDeleteGracePeriod: { ...cleanupSettings.softDeleteGracePeriod, unit: e.target.value }
                })}
              >
                {gracePeriodUnits.map(unit => (
                  <MenuItem key={unit} value={unit}>{unit.charAt(0).toUpperCase() + unit.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        <Alert severity="info" sx={{ mt: 2 }}>
          During the grace period, you can restore deleted executions from the "View Soft Deleted" list
        </Alert>
      </Paper>
      
      {/* Schedule Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Cleanup Schedule
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure when automatic cleanup runs
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Frequency</InputLabel>
          <Select
            value={cleanupSettings.schedule?.frequency || 'daily'}
            label="Frequency"
            onChange={(e) => setCleanupSettings({
              ...cleanupSettings,
              schedule: { ...cleanupSettings.schedule, frequency: e.target.value }
            })}
          >
            {frequencies.map(freq => (
              <MenuItem key={freq} value={freq}>{freq.charAt(0).toUpperCase() + freq.slice(1)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <TextField
          fullWidth
          label="Time (24-hour format)"
          type="time"
          value={cleanupSettings.schedule?.time || '02:00'}
          onChange={(e) => setCleanupSettings({
            ...cleanupSettings,
            schedule: { ...cleanupSettings.schedule, time: e.target.value }
          })}
          sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
        />
        
        {cleanupSettings.schedule?.frequency === 'weekly' && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Day of Week</InputLabel>
            <Select
              value={cleanupSettings.schedule?.dayOfWeek ?? 0}
              label="Day of Week"
              onChange={(e) => setCleanupSettings({
                ...cleanupSettings,
                schedule: { ...cleanupSettings.schedule, dayOfWeek: e.target.value }
              })}
            >
              {daysOfWeek.map((day, index) => (
                <MenuItem key={index} value={index}>{day}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        
        {cleanupSettings.schedule?.frequency === 'monthly' && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Day of Month</InputLabel>
            <Select
              value={cleanupSettings.schedule?.dayOfMonth || 1}
              label="Day of Month"
              onChange={(e) => setCleanupSettings({
                ...cleanupSettings,
                schedule: { ...cleanupSettings.schedule, dayOfMonth: e.target.value }
              })}
            >
              {daysOfMonth.map(day => (
                <MenuItem key={day} value={day}>{day}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        
        <TextField
          fullWidth
          label="Timezone"
          value={cleanupSettings.schedule?.timezone || 'UTC'}
          onChange={(e) => setCleanupSettings({
            ...cleanupSettings,
            schedule: { ...cleanupSettings.schedule, timezone: e.target.value }
          })}
          helperText="e.g., America/New_York, Europe/London, Asia/Tokyo"
        />
      </Paper>
      
      {/* Save Button */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={saveCleanupSettings}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
      
      {/* Manual Actions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Manual Actions
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={handlePreviewCleanup}
              disabled={!cleanupSettings.enabled}
            >
              Preview Cleanup
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              onClick={() => setRunCleanupDialogOpen(true)}
              disabled={!cleanupSettings.enabled}
            >
              Run Cleanup Now
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              color="info"
              onClick={fetchSoftDeletedExecutions}
            >
              View Soft Deleted
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Statistics Panel */}
      {cleanupStats && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Cleanup Statistics
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Cleanups Run
                </Typography>
                <Typography variant="h5">
                  {cleanupStats.totalCleanupsRun || 0}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Executions Deleted
                </Typography>
                <Typography variant="h5">
                  {cleanupStats.totalExecutionsDeleted || 0}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Files Deleted
                </Typography>
                <Typography variant="h5">
                  {cleanupStats.totalFilesDeleted || 0}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Storage Freed
                </Typography>
                <Typography variant="h5">
                  {formatBytes(cleanupStats.totalSizeFreed || 0)}
                </Typography>
              </Box>
            </Grid>
            
            {cleanupStats.lastCleanupAt && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Last Cleanup: {new Date(cleanupStats.lastCleanupAt).toLocaleString()}
                </Typography>
                {cleanupStats.lastCleanupResult && (
                  <Typography variant="body2" color="text.secondary">
                    Soft Deleted: {cleanupStats.lastCleanupResult.softDeleted || 0} | 
                    Permanently Deleted: {cleanupStats.lastCleanupResult.permanentlyDeleted || 0} | 
                    Files: {cleanupStats.lastCleanupResult.filesDeleted || 0} | 
                    Errors: {cleanupStats.lastCleanupResult.errors || 0}
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>
        </Paper>
      )}
      
      {/* Cleanup History Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Cleanup History
        </Typography>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell align="right">Soft Deleted</TableCell>
                <TableCell align="right">Permanent Deleted</TableCell>
                <TableCell align="right">Files Deleted</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cleanupHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No cleanup history yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                cleanupHistory.map((history) => (
                  <TableRow key={history._id}>
                    <TableCell>
                      {new Date(history.startedAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={history.cleanupType.replace('_', ' ')} 
                        size="small"
                        color={history.cleanupType === 'permanent_delete' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={history.triggerType} 
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {history.results?.executionsSoftDeleted || 0}
                    </TableCell>
                    <TableCell align="right">
                      {history.results?.executionsPermanentlyDeleted || 0}
                    </TableCell>
                    <TableCell align="right">
                      {history.results?.filesDeleted || 0}
                    </TableCell>
                    <TableCell align="right">
                      {history.duration ? `${(history.duration / 1000).toFixed(1)}s` : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={history.status} 
                        size="small"
                        color={
                          history.status === 'completed' ? 'success' :
                          history.status === 'failed' ? 'error' : 'default'
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={historyTotal}
          page={historyPage - 1}
          onPageChange={(e, newPage) => setHistoryPage(newPage + 1)}
          rowsPerPage={historyRowsPerPage}
          onRowsPerPageChange={(e) => {
            setHistoryRowsPerPage(parseInt(e.target.value, 10));
            setHistoryPage(1);
          }}
        />
      </Paper>
      
      {/* Preview Dialog */}
      <Dialog 
        open={previewDialogOpen} 
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Cleanup Preview</DialogTitle>
        <DialogContent>
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : previewData ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This is what will be deleted if you run cleanup now
              </Alert>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'warning.light' }}>
                    <Typography variant="h6" color="warning.contrastText">
                      Soft Delete
                    </Typography>
                    <Typography variant="h4" color="warning.contrastText">
                      {previewData.summary?.totalExecutionsToSoftDelete || 0}
                    </Typography>
                    <Typography variant="body2" color="warning.contrastText">
                      executions
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                    <Typography variant="h6" color="error.contrastText">
                      Permanent Delete
                    </Typography>
                    <Typography variant="h4" color="error.contrastText">
                      {previewData.summary?.totalExecutionsToPermanentlyDelete || 0}
                    </Typography>
                    <Typography variant="body2" color="error.contrastText">
                      executions
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Files to Delete
                  </Typography>
                  <Typography variant="h6">
                    {previewData.summary?.totalFilesToDelete || 0}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Storage to Free
                  </Typography>
                  <Typography variant="h6">
                    {formatBytes(previewData.summary?.totalSizeToFree || 0)}
                  </Typography>
                </Grid>
              </Grid>
              
              {previewData.softDelete?.cutoffDate && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Cutoff Date: {new Date(previewData.softDelete.cutoffDate).toLocaleString()}
                </Alert>
              )}
            </Box>
          ) : (
            <Alert severity="error">Failed to load preview data</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Soft Deleted Executions Dialog */}
      <Dialog 
        open={softDeletedDialogOpen} 
        onClose={() => setSoftDeletedDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Soft Deleted Executions</DialogTitle>
        <DialogContent>
          {softDeletedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Execution ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Deleted</TableCell>
                    <TableCell>Permanent Deletion</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {softDeletedExecutions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No soft-deleted executions
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    softDeletedExecutions.map((execution) => (
                      <TableRow key={execution.executionId}>
                        <TableCell>{execution.executionId}</TableCell>
                        <TableCell>
                          <Chip label={execution.status} size="small" />
                        </TableCell>
                        <TableCell>
                          {new Date(execution.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {new Date(execution.deletedAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {new Date(execution.deletionScheduledFor).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => handleRestoreExecution(execution.executionId)}
                          >
                            Restore
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSoftDeletedDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Run Cleanup Confirmation Dialog */}
      <Dialog 
        open={runCleanupDialogOpen} 
        onClose={() => setRunCleanupDialogOpen(false)}
      >
        <DialogTitle>Confirm Manual Cleanup</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will immediately start the cleanup process based on your current settings.
            Executions will be soft-deleted first and can be restored during the grace period.
          </Alert>
          <Typography>
            Are you sure you want to run cleanup now?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunCleanupDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleRunCleanupNow} 
            color="warning"
            variant="contained"
            disabled={runningCleanup}
          >
            {runningCleanup ? 'Starting...' : 'Yes, Run Cleanup'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Excel Templates Tab Component
const ExcelTemplatesTab = ({ companyId, projectId, isActive }) => {
  const [libraries, setLibraries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedLibrary, setSelectedLibrary] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [libraryToDelete, setLibraryToDelete] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [uploading, setUploading] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const fetchLibraries = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/excel-template-libraries`);
      setLibraries(response.data.libraries || []);
    } catch (err) {
      setError('Failed to fetch template libraries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async (libraryId) => {
    try {
      const response = await api.get(
        `/companies/${companyId}/projects/${projectId}/excel-template-libraries/${libraryId}/templates`
      );
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  useEffect(() => {
    if (isActive) {
      fetchLibraries();
    }
  }, [isActive, companyId, projectId]);

  useEffect(() => {
    if (selectedLibrary) {
      fetchTemplates(selectedLibrary.libraryId);
    }
  }, [selectedLibrary]);

  const handleCreateLibrary = async () => {
    try {
      const response = await api.post(
        `/companies/${companyId}/projects/${projectId}/excel-template-libraries`,
        formData
      );
      setLibraries(prev => [response.data.library, ...prev]);
      setCreateDialogOpen(false);
      setFormData({ name: '', description: '' });
    } catch (err) {
      setError('Failed to create library');
      console.error(err);
    }
  };

  const handleUpdateLibrary = async () => {
    if (!selectedLibrary) return;
    try {
      const response = await api.put(
        `/companies/${companyId}/projects/${projectId}/excel-template-libraries/${selectedLibrary.libraryId}`,
        formData
      );
      setLibraries(prev => prev.map(l => l.libraryId === selectedLibrary.libraryId ? response.data.library : l));
      setSelectedLibrary(response.data.library);
      setEditDialogOpen(false);
    } catch (err) {
      setError('Failed to update library');
      console.error(err);
    }
  };

  const handleDeleteLibrary = async () => {
    if (!libraryToDelete) return;
    try {
      await api.delete(
        `/companies/${companyId}/projects/${projectId}/excel-template-libraries/${libraryToDelete.libraryId}`
      );
      setLibraries(prev => prev.filter(l => l.libraryId !== libraryToDelete.libraryId));
      if (selectedLibrary?.libraryId === libraryToDelete.libraryId) {
        setSelectedLibrary(null);
        setTemplates([]);
      }
      setDeleteDialogOpen(false);
      setLibraryToDelete(null);
    } catch (err) {
      setError('Failed to delete library');
      console.error(err);
    }
  };

  const handleUploadTemplate = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedLibrary) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', templateName || file.name);
      const response = await api.post(
        `/companies/${companyId}/projects/${projectId}/excel-template-libraries/${selectedLibrary.libraryId}/templates/upload`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setTemplates(prev => [response.data.template, ...prev]);
      setSelectedLibrary(prev => prev ? { ...prev, templateCount: (prev.templateCount || 0) + 1 } : prev);
      setLibraries(prev => prev.map(l =>
        l.libraryId === selectedLibrary.libraryId
          ? { ...l, templateCount: (l.templateCount || 0) + 1 }
          : l
      ));
      setTemplateName('');
    } catch (err) {
      setError('Failed to upload template');
      console.error(err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!selectedLibrary) return;
    try {
      await api.delete(
        `/companies/${companyId}/projects/${projectId}/excel-template-libraries/${selectedLibrary.libraryId}/templates/${templateId}`
      );
      setTemplates(prev => prev.filter(t => t.templateId !== templateId));
      setSelectedLibrary(prev => prev ? { ...prev, templateCount: Math.max(0, (prev.templateCount || 1) - 1) } : prev);
      setLibraries(prev => prev.map(l =>
        l.libraryId === selectedLibrary?.libraryId
          ? { ...l, templateCount: Math.max(0, (l.templateCount || 1) - 1) }
          : l
      ));
    } catch (err) {
      setError('Failed to delete template');
      console.error(err);
    }
  };

  const handleActivateTemplate = async (templateId) => {
    if (!selectedLibrary) return;
    try {
      const response = await api.post(
        `/companies/${companyId}/projects/${projectId}/excel-template-libraries/${selectedLibrary.libraryId}/templates/${templateId}/activate`
      );
      setTemplates(prev => prev.map(t => t.templateId === templateId ? response.data.template : t));
    } catch (err) {
      setError('Failed to activate template');
      console.error(err);
    }
  };

  const openEditorForTemplate = async (template) => {
    try {
      const response = await api.get(
        `/companies/${companyId}/projects/${projectId}/excel-template-libraries/${selectedLibrary.libraryId}/templates/${template.templateId}`
      );
      setEditingTemplate(response.data.template);
      setEditorOpen(true);
    } catch (err) {
      setError('Failed to load template data');
      console.error(err);
    }
  };

  const handleSaveTemplate = async (updatedTemplate) => {
    if (!selectedLibrary || !editingTemplate) return;
    try {
      const response = await api.put(
        `/companies/${companyId}/projects/${projectId}/excel-template-libraries/${selectedLibrary.libraryId}/templates/${editingTemplate.templateId}`,
        { sheets: updatedTemplate.sheets, name: updatedTemplate.name, description: updatedTemplate.description }
      );
      setTemplates(prev => prev.map(t => t.templateId === editingTemplate.templateId ? { ...t, ...response.data.template } : t));
      setEditorOpen(false);
      setEditingTemplate(null);
    } catch (err) {
      setError('Failed to save template');
      console.error(err);
    }
  };

  const handleReparseTemplate = async (file) => {
    if (!selectedLibrary || !editingTemplate) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post(
        `/companies/${companyId}/projects/${projectId}/excel-template-libraries/${selectedLibrary.libraryId}/templates/${editingTemplate.templateId}/reparse`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const updated = response.data.template;
      setEditingTemplate(updated);
      setTemplates(prev => prev.map(t => t.templateId === updated.templateId ? updated : t));
      return updated;
    } catch (err) {
      setError('Failed to re-parse template file');
      console.error(err);
      return null;
    }
  };

  if (editorOpen && editingTemplate) {
    return (
      <React.Suspense fallback={<Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>}>
        <ExcelTemplateEditor
          template={editingTemplate}
          onSave={handleSaveTemplate}
          onClose={() => { setEditorOpen(false); setEditingTemplate(null); }}
          onActivate={() => handleActivateTemplate(editingTemplate.templateId)}
          onReparse={handleReparseTemplate}
        />
      </React.Suspense>
    );
  }

  if (loading && libraries.length === 0) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {!selectedLibrary ? (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h6" fontWeight={600}>Excel Template Libraries</Typography>
              <Typography variant="body2" color="textSecondary">
                Create template libraries to guide Smart Document extraction for different Excel/CSV formats
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
              New Library
            </Button>
          </Box>

          {libraries.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 2 }}>
              <TableChartIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>No Template Libraries</Typography>
              <Typography variant="body2" color="textSecondary" mb={2}>
                Create a library to start uploading sample Excel files and defining extraction templates.
              </Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
                Create First Library
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {libraries.map(lib => (
                <Grid item xs={12} sm={6} md={4} key={lib.libraryId}>
                  <Card variant="outlined" sx={{ cursor: 'pointer', '&:hover': { boxShadow: 2 } }}>
                    <CardContent onClick={() => setSelectedLibrary(lib)}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <TableChartIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight={600}>{lib.name}</Typography>
                      </Box>
                      {lib.description && (
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                          {lib.description}
                        </Typography>
                      )}
                      <Box display="flex" gap={1} mt={1}>
                        <Chip label={`${lib.templateCount || 0} templates`} size="small" />
                        <Chip
                          label={lib.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={lib.isActive ? 'success' : 'default'}
                        />
                      </Box>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end' }}>
                      <IconButton size="small" onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLibrary(lib);
                        setFormData({ name: lib.name, description: lib.description || '' });
                        setEditDialogOpen(true);
                      }}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={(e) => {
                        e.stopPropagation();
                        setLibraryToDelete(lib);
                        setDeleteDialogOpen(true);
                      }}><DeleteIcon fontSize="small" /></IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      ) : (
        <>
          <Box display="flex" alignItems="center" gap={1} mb={3}>
            <Button size="small" onClick={() => { setSelectedLibrary(null); setTemplates([]); }}>
              &larr; Back to Libraries
            </Button>
            <Typography variant="h6" fontWeight={600}>{selectedLibrary.name}</Typography>
            <Chip label={`${templates.length} templates`} size="small" />
          </Box>

          {selectedLibrary.description && (
            <Typography variant="body2" color="textSecondary" mb={2}>{selectedLibrary.description}</Typography>
          )}

          <Box display="flex" gap={2} alignItems="center" mb={3} flexWrap="wrap">
            <TextField
              size="small"
              label="Template Name (optional)"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Sample Excel'}
              <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={handleUploadTemplate} />
            </Button>
          </Box>

          {templates.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 2 }}>
              <UploadFileIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>No Templates Yet</Typography>
              <Typography variant="body2" color="textSecondary">
                Upload a sample Excel or CSV file to create your first extraction template.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Source File</TableCell>
                    <TableCell>Sheets</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Matches</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map(tpl => (
                    <TableRow key={tpl.templateId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{tpl.name}</Typography>
                        {tpl.description && (
                          <Typography variant="caption" color="textSecondary">{tpl.description}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{tpl.sourceFile?.originalName || '-'}</Typography>
                      </TableCell>
                      <TableCell>{tpl.sheets?.length || 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={tpl.status || 'draft'}
                          size="small"
                          color={tpl.status === 'active' ? 'success' : tpl.status === 'archived' ? 'default' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>{tpl.matchCount || 0}</TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {tpl.updatedAt ? new Date(tpl.updatedAt).toLocaleDateString() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={0.5} justifyContent="flex-end" alignItems="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                            onClick={() => openEditorForTemplate(tpl)}
                            sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25, minHeight: 0 }}
                          >
                            Edit
                          </Button>
                          {tpl.status !== 'active' ? (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                              onClick={() => handleActivateTemplate(tpl.templateId)}
                              sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25, minHeight: 0 }}
                            >
                              Activate
                            </Button>
                          ) : (
                            <Chip label="Active" size="small" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 22 }} />
                          )}
                          <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(tpl.templateId)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Create Library Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Template Library</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Library Name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
              placeholder="e.g., Invoice Templates"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              fullWidth
              placeholder="Describe what types of Excel files this library handles"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateLibrary} variant="contained" disabled={!formData.name.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Library Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Template Library</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Library Name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateLibrary} variant="contained" disabled={!formData.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Library Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Library</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{libraryToDelete?.name}"? This will also delete all templates in this library. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteLibrary} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ProjectDetailPage = () => {
  const { companyId, projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [company, setCompany] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Initialize activeTab based on URL parameter
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    return tabParam ? parseInt(tabParam, 10) : 0;
  };
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  
  // Mobile responsive hook
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  
  // Handle hash navigation for direct tab access
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1); // Remove the #
      const tabMap = {
        'flow-designer': 0,
        'executions': 1,
        'errors': 2,
        'billing': 3,
        'ai-settings': 4,
        'external-data-integration': 5,
        'collaboration': 6,
        'history-display': 7,
        'search-configuration': 8,
        'matching-libraries': 9,
        'files': 10,
        'email': 11,
        'push-notifications': 12,
        'webhooks': 13,
        'cleanup': 14,
        'excel-templates': 15
      };
      
      if (tabMap.hasOwnProperty(hash)) {
        setActiveTab(tabMap[hash]);
      }
    };
    
    // Handle initial hash
    handleHashChange();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Sync state
  const [showProjectSyncDialog, setShowProjectSyncDialog] = useState(false);
  const [showFlowSyncDialog, setShowFlowSyncDialog] = useState(false);
  
  // Add billing settings state for token display
  const [billingSettings, setBillingSettings] = useState({
    useQuotaTokens: false,
    currentTokens: 0,
    chargingOptions: {
      perExecution: { enabled: false, rate: 10 },
      perObject: { enabled: false, rate: 1 },
      perStep: { enabled: false, rate: 5 },
      perAIToken: { enabled: false, rate: 0.001 }
    },
    tokenExpiry: 'monthly',
    defaultRefillAmount: 1000,
    autoReset: true
  });

  const fetchBillingSettings = async () => {
    try {
      console.log('Fetching billing settings for project:', projectId);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/billing`);
      setBillingSettings(response.data);
      console.log('✅ Billing settings loaded:', response.data);
    } catch (error) {
      console.error('Failed to fetch billing settings:', error);
      // Fallback to default settings if API fails
      setBillingSettings(prev => ({
        ...prev,
        useQuotaTokens: false,
        currentTokens: 0,
      }));
    }
  };

  // Helper function to get token display color based on remaining tokens
  const getTokenDisplayColor = (currentTokens, defaultRefillAmount) => {
    const percentage = (currentTokens / defaultRefillAmount) * 100;
    if (percentage > 50) return 'success';
    if (percentage > 20) return 'warning';
    return 'error';
  };

  // Helper function to format token display text
  const formatTokenDisplay = (currentTokens) => {
    if (currentTokens >= 1000000) {
      return `${(currentTokens / 1000000).toFixed(1)}M`;
    } else if (currentTokens >= 1000) {
      return `${(currentTokens / 1000).toFixed(1)}K`;
    }
    return currentTokens.toLocaleString();
  };

  const fetchProject = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch company info first for context
      try {
        const companyRes = await api.get(`/companies/${companyId}`);
        setCompany(companyRes.data);
      } catch (companyErr) {
        console.log('Company endpoint not implemented, using generic data');
        setCompany({
          _id: companyId,
          name: `Company ${companyId.slice(-6)}`,
          primaryColor: '#667eea'
        });
      }

      // Fetch project details
      try {
        const projectRes = await api.get(`/companies/${companyId}/projects/${projectId}`);
        const projectData = projectRes.data;
        
        // Load flow step count from FlowStep API
        try {
          const flowData = await flowStepsApi.getFlowData(projectId);
          projectData.flow = {
            steps: flowData.steps || [],
            connections: flowData.connections || []
          };
        } catch (flowError) {
          // No flow data found, use empty flow
          projectData.flow = { steps: [], connections: [] };
        }

        // Initialize project stats
        projectData.stats = {
          totalExecutions: 0,
          successfulExecutions: 0,
          averageProcessingTime: 0,
          lastExecutionAt: null
        };
        
        setProject(projectData);
        setEditName(projectData.name);
        setEditDescription(projectData.description || '');
        
        // Fetch billing settings after project is loaded
        fetchBillingSettings();
        
      } catch (projectErr) {
        console.log('Project endpoint not implemented, using generic data');
        // Generic project data
        const genericProject = {
          _id: projectId,
          name: `Project ${projectId.slice(-6)}`,
          description: 'Project description not available',
          status: 'draft',
          isPublished: false,
          companyId,
          createdAt: new Date().toISOString(),
          stats: {
            totalExecutions: 0,
            successfulExecutions: 0,
            averageProcessingTime: 0,
            lastExecutionAt: null
          }
        };
        setProject(genericProject);
        setEditName(genericProject.name);
        setEditDescription(genericProject.description);
        
        // Fetch billing settings even for generic project
        fetchBillingSettings();
      }

      // Fetch recent executions
      try {
        const executionsRes = await api.get(`/companies/${companyId}/projects/${projectId}/executions`, {
          params: { limit: 10, page: 1 }
        });
        setExecutions(executionsRes.data.executions || []);
      } catch (executionsErr) {
        console.log('Executions endpoint not implemented, using empty array');
        setExecutions([]);
      }
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to load project data');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (companyId && projectId) {
      fetchProject();
    }
  }, [companyId, projectId]);

  // Handle URL parameter changes to update active tab
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (tabIndex >= 0 && tabIndex <= 12) { // Valid tab range
        setActiveTab(tabIndex);
      }
    }
  }, [location.search]);

  // Add page visibility listener to refresh billing data when user returns
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && companyId && projectId) {
        console.log('🔄 Page became visible, refreshing billing data...');
        fetchBillingSettings();
      }
    };

    const handleFocus = () => {
      if (companyId && projectId) {
        console.log('🔄 Window focused, refreshing billing data...');
        fetchBillingSettings();
      }
    };

    // Listen for page visibility changes and window focus
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [companyId, projectId, fetchBillingSettings]);

  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/companies/${companyId}/projects/${projectId}`, {
        name: editName,
        description: editDescription
      });
      setProject(prev => ({
        ...prev,
        name: editName,
        description: editDescription
      }));
      setSettingsDialogOpen(false);
    } catch (err) {
      setError('Failed to save project settings');
    }
    setSaving(false);
  };

  const handlePublishToggle = async () => {
    try {
      await api.put(`/companies/${companyId}/projects/${projectId}`, {
        isPublished: !project.isPublished,
        status: !project.isPublished ? 'active' : 'draft'
      });
      setProject(prev => ({
        ...prev,
        isPublished: !prev.isPublished,
        status: !prev.isPublished ? 'active' : 'draft'
      }));
    } catch (err) {
      setError('Failed to update project status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'in_progress': return 'info';
      case 'waiting_input': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon fontSize="small" />;
      case 'failed': return <ErrorIcon fontSize="small" />;
      case 'in_progress': return <FlashOnIcon fontSize="small" />;
      default: return <FlashOnIcon fontSize="small" />;
    }
  };

  const handleSyncProject = () => {
    setShowProjectSyncDialog(true);
  };

  const handleSyncFlow = () => {
    setShowFlowSyncDialog(true);
  };

  const handleSyncComplete = (result) => {
    console.log('Sync completed:', result);
    // Optionally refresh data or show success message
    fetchProject();
  };

  if (loading) {
    return (
      <Box maxWidth={1200} mx="auto" mt={4} px={2}>
        <BreadcrumbsNav />
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box maxWidth={1200} mx="auto" mt={4} px={2}>
        <BreadcrumbsNav />
        <Alert severity="error">Project not found</Alert>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        bgcolor: '#f5f5f5',
        width: '100%',
        '& .MuiPaper-root': {
          bgcolor: '#ffffff !important',
        },
        '& .MuiCard-root': {
          bgcolor: '#ffffff !important',
        },
        '& .MuiAccordion-root': {
          bgcolor: '#ffffff !important',
        },
        '& .MuiTableContainer-root': {
          bgcolor: '#ffffff !important',
        }
      }}
    >
      <Box 
        maxWidth={1200} 
        mx="auto" 
        mt={4} 
        px={3}
        py={3}
      >
      <BreadcrumbsNav />
      
      {/* Project Header */}
      <Paper elevation={1} sx={{ p: isMobile ? 2 : 3, mb: isMobile ? 2 : 4, bgcolor: '#ffffff' }}>
        <Box display="flex" flexDirection={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "stretch" : "flex-start"} gap={isMobile ? 1.5 : 0}>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" mb={isMobile ? 0.5 : 1}>
              {project.name}
            </Typography>
            {!isMobile && (
              <Typography variant="body1" color="textSecondary" mb={2}>
                {project.description || 'No description provided'}
              </Typography>
            )}
            <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
              <Chip 
                label={project.status || 'draft'} 
                color={project.status === 'active' ? 'success' : 'warning'}
                icon={project.status === 'active' ? <PlayArrowIcon /> : <EditIcon />}
                size={isMobile ? "small" : "medium"}
              />
              <Chip 
                label={project.isPublished ? 'Published' : 'Draft'} 
                variant="outlined"
                icon={project.isPublished ? <PublishIcon /> : <VisibilityIcon />}
                size={isMobile ? "small" : "medium"}
              />
              {billingSettings.useQuotaTokens && (
                <MuiTooltip title={`Current Tokens: ${billingSettings.currentTokens.toLocaleString()}\nDefault Refill: ${billingSettings.defaultRefillAmount.toLocaleString()}\nExpiry: ${billingSettings.tokenExpiry.charAt(0).toUpperCase() + billingSettings.tokenExpiry.slice(1)}`}>
                  <Chip 
                    label={`${formatTokenDisplay(billingSettings.currentTokens)} Tokens`} 
                    color={getTokenDisplayColor(billingSettings.currentTokens, billingSettings.defaultRefillAmount)} 
                    icon={<TokenIcon />}
                    size={isMobile ? "small" : "medium"}
                  />
                </MuiTooltip>
              )}
              {!isMobile && (
                <Typography variant="caption" color="textSecondary" sx={{ ml: 2 }}>
                  Created: {new Date(project.createdAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" flexDirection={isMobile ? "column" : "row"} gap={isMobile ? 1 : 2} width={isMobile ? "100%" : "auto"}>
            {!isMobile && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CloudSyncIcon />}
                  onClick={handleSyncProject}
                  size="small"
                >
                  Sync Project
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AccountTreeIcon />}
                  onClick={handleSyncFlow}
                  size="small"
                >
                  Sync Flow
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => setSettingsDialogOpen(true)}
              fullWidth={isMobile}
            >
              Settings
            </Button>
            <Button
              variant={project.isPublished ? "outlined" : "contained"}
              color={project.isPublished ? "warning" : "success"}
              startIcon={project.isPublished ? <VisibilityIcon /> : <PublishIcon />}
              onClick={handlePublishToggle}
              fullWidth={isMobile}
            >
              {project.isPublished ? 'Unpublish' : 'Publish'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Main Content Tabs */}
      <Paper elevation={1} sx={{ mb: isMobile ? 2 : 4, bgcolor: '#ffffff' }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: isMobile ? 42 : 48,
            '& .MuiTab-root': {
              minHeight: isMobile ? 42 : 48,
              fontSize: isMobile ? '0.8rem' : '0.875rem',
              py: isMobile ? 1 : 1.5,
            }
          }}
        >
          <Tab icon={<AccountTreeIcon />} label="Flow Designer" />
          <Tab icon={<HistoryIcon />} label="Executions" />
          <Tab icon={<ErrorOutlineIcon />} label="Errors" />
          <Tab icon={<AccountBalanceWalletIcon />} label="Billing" />
          <Tab icon={<SmartToyIcon />} label="AI Settings" />
          <Tab icon={<CloudSyncIcon />} label="External Data Integration" />
          <Tab icon={<GroupIcon />} label="Collaboration" />
          <Tab icon={<ViewColumnIcon />} label="History Display" />
          <Tab icon={<SearchIcon />} label="Search Configuration" />
          <Tab icon={<LibraryBooksIcon />} label="Matching Libraries" />
          <Tab icon={<AssignmentIcon />} label="Files" />
          <Tab icon={<EmailIcon />} label="Email" />
          <Tab icon={<NotificationsIcon />} label="Push Notifications" />
          <Tab icon={<WebhookIcon />} label="Webhooks" />
          <Tab icon={<DeleteSweepIcon />} label="Cleanup" />
          <Tab icon={<TableChartIcon />} label="Excel Templates" />
        </Tabs>
        
        <Box sx={{ p: 3, bgcolor: '#ffffff' }}>
          {/* Flow Designer Tab */}
          {activeTab === 0 && (
            <Box>
              
              
              {/* Flow Preview */}
              <Paper elevation={2} sx={{ p: 4, textAlign: 'center', bgcolor: '#ffffff' }}>
                <AccountTreeIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" mb={1}>
                  {project.flow?.steps?.length > 0 ? 'Flow Configured' : 'No Flow Configured'}
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={3}>
                  {project.flow?.steps?.length > 0 
                    ? `Your workflow has ${project.flow.steps.length} steps configured`
                    : 'Design your data extraction workflow using our visual editor'
                  }
                </Typography>
                <Box display="flex" justifyContent="center" gap={2}>
                  <Button 
                    variant="contained" 
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/company/${companyId}/project/${projectId}/flow`)}
                  >
                    {project.flow?.steps?.length > 0 ? 'Edit Flow' : 'Create Flow'}
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Executions Tab */}
          {activeTab === 1 && (
            <ExecutionsTab 
              companyId={companyId} 
              projectId={projectId} 
              project={project}
              isActive={activeTab === 1}
            />
          )}

          {/* Errors Tab */}
          {activeTab === 2 && (
            <ErrorsTab 
              companyId={companyId} 
              projectId={projectId} 
              project={project}
              isActive={activeTab === 2}
            />
          )}

          {/* Billing Tab */}
          {activeTab === 3 && (
            <BillingTab 
              companyId={companyId} 
              projectId={projectId} 
              project={project}
              isActive={activeTab === 3}
            />
          )}

          {/* AI Settings Tab */}
          {activeTab === 4 && (
            <AISettingsTab 
              companyId={companyId} 
              projectId={projectId} 
              project={project}
              isActive={activeTab === 4}
            />
          )}

          {/* External Data Integration Tab */}
          {activeTab === 5 && (
            <ExternalDataIntegrationTab 
              companyId={companyId} 
              projectId={projectId} 
              project={project}
              isActive={activeTab === 5}
            />
          )}

          {/* Collaboration Tab */}
          {activeTab === 6 && (
            <CollaborationTab 
              companyId={companyId} 
              projectId={projectId} 
              project={project}
              isActive={activeTab === 6}
            />
          )}

          {/* History Display Tab */}
          {activeTab === 7 && (
            <HistoryDisplayTab 
              companyId={companyId} 
              projectId={projectId} 
              project={project}
              isActive={activeTab === 7}
            />
          )}

          {/* Search Configuration Tab */}
          {activeTab === 8 && (
            <SearchConfigurationTab 
              companyId={companyId} 
              projectId={projectId} 
              project={project}
              isActive={activeTab === 8}
            />
          )}

          {/* Matching Libraries Tab */}
          {activeTab === 9 && (
            <MatchingLibrariesTab 
              companyId={companyId} 
              projectId={projectId} 
              isActive={activeTab === 9}
            />
          )}

          {/* Files Tab */}
          {activeTab === 10 && (
            <FilesTab 
              companyId={companyId} 
              projectId={projectId} 
              project={project}
              isActive={activeTab === 10}
              navigate={navigate}
            />
          )}

          {/* Email Tab */}
          {activeTab === 11 && (
            <EmailTab 
              companyId={companyId} 
              projectId={projectId} 
              project={project}
              isActive={activeTab === 11}
            />
          )}

          {/* Push Notifications Tab */}
          {activeTab === 12 && (
            <PushNotificationTab 
              companyId={companyId} 
              projectId={projectId} 
              project={project}
              isActive={activeTab === 12}
            />
          )}

          {/* Webhooks Tab */}
          {activeTab === 13 && (
            <WebhooksTab 
              companyId={companyId} 
              projectId={projectId} 
              project={project}
              isActive={activeTab === 13}
            />
          )}

          {/* Cleanup Tab */}
          {activeTab === 14 && (
            <CleanupTab 
              companyId={companyId} 
              projectId={projectId} 
              project={project}
              isActive={activeTab === 14}
            />
          )}

          {/* Excel Templates Tab */}
          {activeTab === 15 && (
            <ExcelTemplatesTab
              companyId={companyId}
              projectId={projectId}
              isActive={activeTab === 15}
            />
          )}
        </Box>
      </Paper>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Project Settings</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Project Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSettings} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Project Sync Dialog */}
      <SyncDialog
        open={showProjectSyncDialog}
        onClose={() => setShowProjectSyncDialog(false)}
        syncType="project"
        targetId={projectId}
        targetName={project?.name}
        onSyncComplete={handleSyncComplete}
      />

      {/* Flow Sync Dialog */}
      <SyncDialog
        open={showFlowSyncDialog}
        onClose={() => setShowFlowSyncDialog(false)}
        syncType="flowsteps"
        targetId={projectId}
        targetName={`${project?.name} Flow`}
        onSyncComplete={handleSyncComplete}
      />
      </Box>
    </Box>
  );
};

export default ProjectDetailPage; 